import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MatematronModal from "../../components/ui/MatematronModal";
import HorizontalScroll from "../../components/ui/HorizontalScroll";
import EdgeDrawer from "../../components/ui/EdgeDrawer";
import ExamCalculator from "../../components/ExamCalculator";
import FormulaSheet from "../../components/FormulaSheet";
import VoiceChannel from "../../components/voice/VoiceChannel";
import { detectEquationType } from "../../components/GraphPlotter/detectEquationType";
import { generatePolynomialData } from "../../components/GraphPlotter/generators/generatePolynomialData";
import { generateRationalData } from "../../components/GraphPlotter/generators/generateRationalData";
import { generateGeneralLineData } from "../../components/GraphPlotter/generators/generateGeneralLineData";
import { generateCircleData, convertGeneralToCircle } from "../../components/GraphPlotter/generators/generateCircleData";
import { generateLogarithmicData } from "../../components/GraphPlotter/generators/generateLogarithmicData";
import { detectExponentialFunction, computeHorizontalAsymptote } from "../../components/GraphPlotter/generators/exponentialAsymptote";
import { toLatexFraction, extractLogArguments } from "../../components/GraphPlotter/sanitizeExpression";
import { AuthContext } from "../../components/AuthContext";
import { ThemeContext } from "../../context/ThemeContext";
import sections from "../../sections";
import { lessonLoaders } from "../../lessonRegistry";
import WhiteboardCanvas from "../components/WhiteboardCanvas";
import {
  fetchWhiteboardPrefs,
  normalizeWhiteboardPrefs,
  patchWhiteboardPrefs,
} from "../api/preferences";
import {
  QuickIconButton,
  PenIcon,
  EraserIcon,
  HighlighterIcon,
  LineIcon,
  PolygonIcon,
  CircleIcon,
  SelectIcon,
  ImageIcon,
  TaskIcon,
  LinkChainIcon,
  CopyCodeIcon,
  TrashCanIcon,
  LeaveDoorIcon,
  GridIcon,
  UndoIcon,
  RedoIcon,
  FullscreenEnterIcon,
  FullscreenExitIcon,
  StylusModeIcon,
  ZoomControl,
} from "../components/WhiteboardControlPrimitives";
import {
  buildTaskTypeChoices,
  normalizeTaskGenerationModes,
} from "../../utils/taskTypeCatalog";
import {
  whiteboardGetRoomState,
  whiteboardGetPageWindow,
  getWhiteboardSocket,
  whiteboardJoinRoom,
  whiteboardLeaveRoom,
  whiteboardPing,
  whiteboardSendCursor,
  whiteboardUpdateState,
} from "../ws/whiteboardClient";
import { createTextWidgetSvgDataUrl } from "../utils/textWidget";

const CURSOR_COLORS = ["#f59e0b", "#8b5cf6", "#ef4444", "#3b82f6", "#f8fafc", "#06b6d4"];
const PARTICIPANT_COLORS = ["#f59e0b", "#8b5cf6", "#ef4444", "#3b82f6"];
const PEN_COLORS = ["#f59e0b", "#8b5cf6", "#ef4444", "#3b82f6", "#f8fafc", "#000000"];
const HIGHLIGHTER_COLORS = ["#f59e0b", "#8b5cf6", "#ef4444", "#3b82f6"];
const HISTORY_LIMIT = 80;
const ZOOM_LEVELS = [100, 110, 125, 150, 175, 200, 250, 300, 350, 400];
const DEFAULT_PAGE_COUNT = 4;
const MAX_PAGE_COUNT = 100;
const BOARD_WIDTH = 1414; // A4 landscape ratio (sqrt(2) : 1)
const PAGE_HEIGHT = 1000;
const IMAGE_ACCEPT = "image/png,image/jpeg,image/webp,image/gif";
const BOARD_SURFACE_OPTIONS = new Set(["default", "white", "black"]);
const MAX_IMAGE_FILE_BYTES = 3 * 1024 * 1024; // 3 MB
const MAX_IMAGE_DATA_URL_CHARS = 4_200_000; // ~4.2 MB text payload
const MAX_IMAGE_LONG_EDGE = 1920;
const IMAGE_COMPRESSION_QUALITY = 0.82;
const IMAGE_OPTIMIZE_THRESHOLD_BYTES = 200_000;
const MAX_TASK_BLOCKS = 50;
const TASK_ADAPTER_LOAD_TIMEOUT_MS = 12000;
const ROOM_WHITEBOARD_PREFS_STORAGE_KEY = "whiteboard.userPrefs.room.v1";
const STYLUS_ONLY_STORAGE_KEY = "whiteboard.stylusOnly.v1";
const HOLD_TOOL_SWITCH_STORAGE_KEY = "whiteboard.holdToolSwitch.v1";
const STYLUS_CURSOR_STORAGE_KEY = "whiteboard.stylusCursor.v1";
const TASK_COLLAPSE_PREFS_STORAGE_PREFIX = "whiteboard.taskCollapse.v1";
const TASK_DOCK_HIDDEN_STORAGE_PREFIX = "whiteboard.taskDockHidden.v1";
const WZORY_URL = process.env.PUBLIC_URL + "/assets/wybrane_wzory_matematyczne_EM2023.pdf";
const BRUSH_TOOLS = ["pen", "eraser", "highlighter"];
const SHAPE_TOOLS = ["line", "polygon", "circle"];
const BASIC_SIDE_TOOLS = ["select", "pen", "eraser"];
const PAGE_WINDOW_TRIGGER_PAGE_COUNT = 12;
const PAGE_WINDOW_TRIGGER_ELEMENT_COUNT = 1200;
const PAGE_WINDOW_WEAK_DEVICE_PAGE_COUNT = 8;
const PAGE_WINDOW_WEAK_DEVICE_ELEMENT_COUNT = 800;
const PAGE_WINDOW_PADDING_BEFORE = 2;
const PAGE_WINDOW_PADDING_AFTER = 1;
const PAGE_WINDOW_MODE_ENABLED = false; // Hotfix: tymczasowo wyłączone (regresja widoczności treści)
const PERSIST_QUEUE_MAX = 120;
const PERSIST_BATCH_DELAY_MIN_MS = 70;
const PERSIST_BATCH_DELAY_MAX_MS = 220;
const DEFAULT_UPDATE_ACK_TIMEOUT_MS = 8000;
const DRAW_UPDATE_ACK_TIMEOUT_MS = 6000;
const DRAW_SYNC_SOFT_WARNING_MS = 6000;
const DRAW_SYNC_HARD_BLOCK_MS = 18000;
const DRAW_SYNC_HARD_ATTEMPT_COUNT = 3;
const DRAW_SYNC_QUEUE_WARNING_SIZE = 4;
const BOARD_STORAGE_WARNING_RATIO = 0.85;
const DEMO_ROOM_CODE = "DEMO";
const DEMO_BOARD_STORAGE_KEY = "whiteboard.demo.roomState.v1";
const DEMO_TASK_SUBTOPIC_IDS = ["2_2_1", "3_3_5", "7_3_3"];
const DEMO_SOCKET = {
  connected: true,
  on: () => {},
  off: () => {},
};

const SECTION_OPTIONS = (sections || []).map((section, sectionIndex) => ({
  id: String(sectionIndex),
  label: section?.title || `Dział ${sectionIndex + 1}`,
  topics: Array.isArray(section?.topics) ? section.topics : [],
}));

function findSectionTopicForSubtopic(subtopicId) {
  const wanted = String(subtopicId || "");
  if (!wanted) return null;
  for (let sectionIndex = 0; sectionIndex < SECTION_OPTIONS.length; sectionIndex += 1) {
    const section = SECTION_OPTIONS[sectionIndex];
    const topics = Array.isArray(section?.topics) ? section.topics : [];
    for (let topicIndex = 0; topicIndex < topics.length; topicIndex += 1) {
      const subtopics = Array.isArray(topics[topicIndex]?.subtopics) ? topics[topicIndex].subtopics : [];
      if (subtopics.some((subtopic) => String(subtopic?.id || "") === wanted)) {
        return { sectionIndex, topicIndex };
      }
    }
  }
  return null;
}

function hashString(value) {
  let hash = 0;
  const text = String(value || "");
  for (let i = 0; i < text.length; i++) {
    hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function colorForUserId(userId) {
  return CURSOR_COLORS[hashString(userId) % CURSOR_COLORS.length];
}

function buildParticipantColorMap(room) {
  const participants = Array.isArray(room?.participants) ? room.participants : [];
  const hostUserId = String(room?.hostUserId || "");
  const map = {};
  if (!participants.length) return map;

  const host = participants.find((p) => String(p?.userId || "") === hostUserId);
  if (host?.userId) {
    map[String(host.userId)] = PARTICIPANT_COLORS[0];
  }

  const others = participants.filter((p) => String(p?.userId || "") !== hostUserId);
  let offset = 0;
  for (const participant of others) {
    const userId = String(participant?.userId || "");
    if (!userId) continue;
    const idx = host?.userId ? (offset % (PARTICIPANT_COLORS.length - 1)) + 1 : offset % PARTICIPANT_COLORS.length;
    map[userId] = PARTICIPANT_COLORS[idx];
    offset += 1;
  }

  return map;
}

function cloneElements(elements) {
  return Array.isArray(elements) ? JSON.parse(JSON.stringify(elements)) : [];
}

function readDemoBoardState() {
  if (typeof window === "undefined") {
    return normalizeBoardState({ version: 1, elements: [], pageCount: DEFAULT_PAGE_COUNT });
  }
  try {
    const parsed = JSON.parse(window.localStorage.getItem(DEMO_BOARD_STORAGE_KEY) || "null");
    if (parsed && typeof parsed === "object") return normalizeBoardState(parsed);
  } catch {
    // ignore invalid local demo state
  }
  return normalizeBoardState({
    version: 1,
    elements: [],
    taskBlocks: [],
    pageCount: DEFAULT_PAGE_COUNT,
  });
}

function writeDemoBoardState(boardState) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(DEMO_BOARD_STORAGE_KEY, JSON.stringify(normalizeBoardState(boardState)));
}

function createDemoRoom(roomCode, boardState) {
  return {
    code: roomCode,
    hostUserId: "demo-user",
    hostRole: "student",
    settings: { title: "Tablica demo" },
    participants: [
      {
        userId: "demo-user",
        name: "Gosc demo",
        role: "student",
        connected: true,
      },
    ],
    boardState: normalizeBoardState(boardState),
  };
}

const DEMO_TUTORIAL_STEPS = [
  {
    id: "panels",
    title: "Panele pomocnicze",
    body: "Tu sa ustawienia tablicy, zmiana strony przyciskow, kalkulator i karta wzorow.",
    target: "panels",
  },
  {
    id: "navigation",
    title: "Nawigacja po tablicy",
    body: "Tutaj przechodzisz miedzy stronami, wlaczasz kratke, dodajesz kolejna strone i wychodzisz z pelnego ekranu.",
    target: "bottom-nav",
  },
  {
    id: "zoom",
    title: "Zoom i widok",
    body: "Gorna grupa odpowiada za przyblizanie, oddalanie i szybkie opcje widoku tablicy.",
    target: "zoom",
  },
  {
    id: "quick-tools",
    title: "Szybki wybor",
    body: "Na start widzisz najczesciej uzywane narzedzia. Przycisk z suwakami u gory rozwija pelny zestaw.",
    target: "quick-tools",
    expandedTools: false,
  },
  {
    id: "full-tools",
    title: "Pelne narzedzia",
    body: "Po rozwinieciu dochodzi dlugopis, zakreslacz, gumka, ksztalty, cofanie, ponawianie oraz dodawanie materialow.",
    target: "side-tools",
    expandedTools: true,
  },
  {
    id: "writing-tools",
    title: "Pisanie i poprawki",
    body: "Dlugopis, zakreslacz i gumka maja szybkie ustawienia koloru oraz grubosci. To podstawowy zestaw do pracy na tablicy.",
    target: "brush-tools",
    expandedTools: true,
  },
  {
    id: "shape-tools",
    title: "Ksztalty",
    body: "W tej grupie sa linia, okrag i wielokat. Przydaja sie do geometrii, schematow i porzadnego zapisu rozwiazania.",
    target: "shape-tools",
    expandedTools: true,
  },
  {
    id: "insert-task",
    title: "Zadanie z platformy",
    body: "Zadanie mozna dodac bez przepisywania tresci. W demo podstawiamy przykladowy temat i pokazujemy, jak zadanie trafia na tablice.",
    target: "task-insert",
    expandedTools: true,
    action: "task",
  },
  {
    id: "insert-graph",
    title: "Wykres na tablicy",
    body: "Wykres mozna wstawic z panelu funkcji i potem wykorzystac go w rozwiazaniu albo wyjasnieniu.",
    target: "graph-insert",
    expandedTools: true,
    action: "graph",
  },
];

function getDemoTutorialTargetStyle(target, controlsSide) {
  const controlsOnLeft = controlsSide === "left";
  switch (target) {
    case "panels":
      return controlsOnLeft
        ? { top: 132, right: 24, width: 270, height: 74 }
        : { top: 132, left: 24, width: 74, height: 236 };
    case "zoom":
      return controlsOnLeft
        ? { top: 132, left: 24, width: 270, height: 74 }
        : { top: 132, right: 24, width: 270, height: 74 };
    case "bottom-nav":
      return controlsOnLeft
        ? { bottom: 28, left: 24, width: 74, height: 260 }
        : { bottom: 28, right: 24, width: 74, height: 260 };
    case "side-tools":
    case "insert-tools":
      return controlsOnLeft
        ? { top: 208, left: 24, width: 74, height: 350 }
        : { top: 208, right: 24, width: 74, height: 350 };
    default:
      return { top: 132, right: 24, width: 270, height: 120 };
  }
}

function readDemoTutorialTargetRect(target, controlsSide) {
  if (typeof document === "undefined") {
    return getDemoTutorialTargetStyle(target, controlsSide);
  }
  const nodes = Array.from(document.querySelectorAll(`[data-demo-tutorial-target="${target}"], [data-demo-tutorial-part="${target}"]`));
  const rects = nodes
    .map((node) => node.getBoundingClientRect())
    .filter((rect) => rect && rect.width > 0 && rect.height > 0);

  if (!rects.length) return getDemoTutorialTargetStyle(target, controlsSide);

  const rawLeft = Math.min(...rects.map((rect) => rect.left));
  const rawTop = Math.min(...rects.map((rect) => rect.top));
  const rawRight = Math.max(...rects.map((rect) => rect.right));
  const rawBottom = Math.max(...rects.map((rect) => rect.bottom));
  const rawWidth = rawRight - rawLeft;
  const rawHeight = rawBottom - rawTop;
  const isSingleColumn = rawWidth <= 68 && rawHeight > rawWidth * 1.8;
  const padX = isSingleColumn ? 2 : 8;
  const padY = isSingleColumn ? 8 : 8;
  const left = Math.max(8, rawLeft - padX);
  const top = Math.max(8, rawTop - padY);
  const right = Math.min(window.innerWidth - 8, rawRight + padX);
  const bottom = Math.min(window.innerHeight - 8, rawBottom + padY);

  return {
    left,
    top,
    width: Math.max(28, right - left),
    height: Math.max(28, bottom - top),
  };
}

function getDemoTutorialCardStyle(targetRect, themeMode) {
  const rect = targetRect || { left: 24, top: 132, width: 260, height: 100 };
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1200;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 800;
  const cardWidth = Math.min(350, Math.max(280, viewportWidth - 36));
  const gap = 18;
  const canPlaceRight = rect.left + rect.width + gap + cardWidth <= viewportWidth - 18;
  const canPlaceLeft = rect.left - gap - cardWidth >= 18;
  const top = Math.max(18, Math.min(viewportHeight - 260, rect.top + rect.height / 2 - 120));
  const horizontal = canPlaceRight
    ? { left: rect.left + rect.width + gap }
    : canPlaceLeft
      ? { left: rect.left - gap - cardWidth }
      : { left: Math.max(18, Math.min(viewportWidth - cardWidth - 18, rect.left)) };
  const base = {
    position: "fixed",
    zIndex: 1710,
    width: cardWidth,
    top,
    padding: 16,
    borderRadius: 14,
    border: "1px solid rgba(245,158,11,0.64)",
    background: themeMode === "light" ? "rgba(255,255,255,0.96)" : "rgba(5,10,32,0.94)",
    boxShadow: "0 18px 48px rgba(0,0,0,0.38)",
  };
  return { ...base, ...horizontal };
}

function DemoTutorialOverlay({ open, stepIndex, controlsSide, themeMode, onStepChange, onClose }) {
  const [targetRect, setTargetRect] = useState(null);
  const [targetReady, setTargetReady] = useState(false);
  const safeIndex = Math.max(0, Math.min(DEMO_TUTORIAL_STEPS.length - 1, Number(stepIndex || 0)));
  const step = DEMO_TUTORIAL_STEPS[safeIndex];

  useEffect(() => {
    if (!open) return undefined;
    let rafId = 0;
    let rafId2 = 0;
    setTargetReady(false);
    setTargetRect(null);
    const measure = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      if (rafId2) window.cancelAnimationFrame(rafId2);
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        rafId2 = window.requestAnimationFrame(() => {
          rafId2 = 0;
          setTargetRect(readDemoTutorialTargetRect(step.target, controlsSide));
          setTargetReady(true);
        });
      });
    };

    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    const timerId = window.setInterval(measure, 500);
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      if (rafId2) window.cancelAnimationFrame(rafId2);
      window.clearInterval(timerId);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, step.target, controlsSide]);

  if (!open) return null;
  const targetStyle = targetRect || getDemoTutorialTargetStyle(step.target, controlsSide);
  const cardStyle = getDemoTutorialCardStyle(targetStyle, themeMode);
  const canGoBack = safeIndex > 0;
  const canGoNext = safeIndex < DEMO_TUTORIAL_STEPS.length - 1;
  const targetWidth = Number(targetStyle.width || 0);
  const targetHeight = Number(targetStyle.height || 0);
  const useVerticalColumnBrackets = targetWidth > 0 && targetWidth <= 72 && targetHeight > targetWidth * 1.8;
  const bracketWidth = useVerticalColumnBrackets
    ? Math.max(36, Math.min(targetWidth - 4, 54))
    : 28;
  const bracketBase = {
    position: "absolute",
    width: bracketWidth,
    height: 28,
    borderColor: "#f59e0b",
    filter: "drop-shadow(0 0 10px rgba(245,158,11,0.8))",
  };

  return (
    <div aria-live="polite" style={{ position: "fixed", inset: 0, zIndex: 1700, pointerEvents: "none" }}>
      <div
        style={{
          position: "fixed",
          ...targetStyle,
          pointerEvents: "none",
          opacity: targetReady ? 1 : 0,
          transition: "opacity 120ms ease",
        }}
      >
        {useVerticalColumnBrackets ? (
          <>
            <span style={{ ...bracketBase, width: "auto", top: 0, left: -4, right: 4, borderTop: "3px solid #f59e0b", borderLeft: "3px solid #f59e0b", borderRight: "3px solid #f59e0b" }} />
            <span style={{ ...bracketBase, width: "auto", bottom: 0, left: -4, right: 4, borderBottom: "3px solid #f59e0b", borderLeft: "3px solid #f59e0b", borderRight: "3px solid #f59e0b" }} />
          </>
        ) : (
          <>
            <span style={{ ...bracketBase, top: 0, left: 0, borderTop: "3px solid #f59e0b", borderLeft: "3px solid #f59e0b" }} />
            <span style={{ ...bracketBase, top: 0, right: 0, borderTop: "3px solid #f59e0b", borderRight: "3px solid #f59e0b" }} />
            <span style={{ ...bracketBase, bottom: 0, left: 0, borderBottom: "3px solid #f59e0b", borderLeft: "3px solid #f59e0b" }} />
            <span style={{ ...bracketBase, bottom: 0, right: 0, borderBottom: "3px solid #f59e0b", borderRight: "3px solid #f59e0b" }} />
          </>
        )}
      </div>

      <div style={{ ...cardStyle, pointerEvents: "auto", opacity: targetReady ? 1 : 0, transition: "opacity 120ms ease" }}>
        <div style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "start" }}>
            <div>
              <div style={{ color: "#f59e0b", fontWeight: 900, fontSize: 12, textTransform: "uppercase" }}>
                Samouczek {safeIndex + 1}/{DEMO_TUTORIAL_STEPS.length}
              </div>
              <strong style={{ display: "block", marginTop: 4, fontSize: 18 }}>{step.title}</strong>
            </div>
            <button className="btn btn-secondary" type="button" onClick={onClose}>
              Zamknij
            </button>
          </div>
          <p style={{ margin: 0, lineHeight: 1.48, opacity: 0.92 }}>{step.body}</p>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
            <button className="btn btn-secondary" type="button" disabled={!canGoBack} onClick={() => onStepChange(safeIndex - 1)}>
              Wstecz
            </button>
            <button className="btn btn-primary" type="button" onClick={() => (canGoNext ? onStepChange(safeIndex + 1) : onClose())}>
              {canGoNext ? "Dalej" : "Gotowe"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function GraphIcon() {
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 20V4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M4 20h16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M6 16c2.2-5.8 4.3-7.8 6.4-6 1.4 1.2 2.3 3.8 5.6-2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 12h10M12 6v12" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.45" />
    </svg>
  );
}

function TextIcon() {
  return (
    <span
      aria-hidden="true"
      style={{ fontFamily: "Rubik, sans-serif", fontSize: 21, fontWeight: 800, lineHeight: 1 }}
    >
      T
    </span>
  );
}

function CustomColorPickerButton({ value, onChange, size = 30, active = false }) {
  const safeValue = /^#[0-9a-f]{6}$/i.test(String(value || "")) ? String(value) : "#f59e0b";
  return (
    <label
      className="btn btn-secondary"
      title="Własny kolor"
      aria-label="Własny kolor"
      style={{
        position: "relative",
        width: size,
        height: size,
        minWidth: size,
        padding: 0,
        borderRadius: 999,
        display: "grid",
        placeItems: "center",
        fontWeight: 900,
        lineHeight: 1,
        cursor: "pointer",
        overflow: "hidden",
        borderColor: active ? "#f59e0b" : undefined,
        boxShadow: active ? "0 0 0 2px rgba(245,158,11,0.25)" : undefined,
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      ⋯
      <input
        type="color"
        value={safeValue}
        onChange={(event) => onChange?.(event.target.value)}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }}
      />
    </label>
  );
}

function RecentCustomColorButton({ color, active, onSelect, size = 30, palette = [] }) {
  if (!/^#[0-9a-f]{6}$/i.test(String(color || ""))) return null;
  if (palette.some((paletteColor) => String(paletteColor).toLowerCase() === String(color).toLowerCase())) return null;
  return (
    <button
      type="button"
      className="btn btn-secondary"
      title={`Ostatni własny kolor ${color}`}
      aria-label={`Ostatni własny kolor ${color}`}
      onClick={() => onSelect?.(color)}
      onContextMenu={(event) => event.preventDefault()}
      style={{
        width: size,
        height: size,
        minWidth: size,
        padding: 0,
        borderRadius: 999,
        background: color,
        borderColor: active ? "#f59e0b" : "rgba(255,255,255,0.24)",
        boxShadow: active ? "0 0 0 2px rgba(245,158,11,0.25)" : undefined,
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        WebkitTapHighlightColor: "transparent",
      }}
    />
  );
}

function GraphThemeIcon({ mode }) {
  const light = mode === "light";
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      {light ? (
        <>
          <circle cx="12" cy="12" r="4.4" fill="none" stroke="currentColor" strokeWidth="2" />
          <path
            d="M12 2.8v2.4M12 18.8v2.4M4.2 4.2l1.7 1.7M18.1 18.1l1.7 1.7M2.8 12h2.4M18.8 12h2.4M4.2 19.8l1.7-1.7M18.1 5.9l1.7-1.7"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </>
      ) : (
        <path
          d="M20.3 14.8A7.4 7.4 0 0 1 9.2 3.7 8.6 8.6 0 1 0 20.3 14.8Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

function getGraphSvgLayout(inputCount = 1) {
  const labelRows = Math.max(0, Number(inputCount || 0));
  const leftPad = 58;
  const topPad = 34;
  const rightPad = 34;
  const plotSide = 520;
  const bottomPad = Math.min(220, 66 + labelRows * 34);
  return {
    labelRows,
    leftPad,
    topPad,
    rightPad,
    bottomPad,
    plotSide,
    plotWidth: plotSide,
    plotHeight: plotSide,
    svgWidth: leftPad + plotSide + rightPad,
    svgHeight: topPad + plotSide + bottomPad,
  };
}

function parseGraphPointInput(value) {
  const match = String(value || "").trim().match(/^([A-Za-z])\s*=\s*\(([^,]+),([^)]+)\)$/);
  if (!match) return null;
  const x = Number(match[2]);
  const y = Number(match[3]);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { label: match[1].toUpperCase(), x, y };
}

function parseGraphSegmentInput(value) {
  const match = String(value || "").trim().match(/^([A-Za-z])\s*(?:-|,|;|:)\s*([A-Za-z])$/);
  if (!match) return null;
  const a = match[1].toUpperCase();
  const b = match[2].toUpperCase();
  if (!a || !b || a === b) return null;
  return { a, b };
}

// Luźny parser dla interfejsu: akceptuje też chwilowe a===b oraz niepełne
// odcinki, żeby zmiana końcówek w selektach była płynna (bez "odskakiwania").
function parseGraphSegmentEndpointsLoose(value) {
  const parts = String(value || "").trim().split(/[-,;:]/);
  const a = String(parts[0] || "").trim().toUpperCase().slice(0, 1);
  const b = String(parts[1] || "").trim().toUpperCase().slice(0, 1);
  return { a, b };
}

function createGraphSvgDataUrl(graphConfig = {}) {
  const inputs = (Array.isArray(graphConfig.inputs) ? graphConfig.inputs : [])
    .map((input) => String(input || "").trim())
    .filter(Boolean);
  const pointInputs = (Array.isArray(graphConfig.points) ? graphConfig.points : [])
    .map((input) => String(input || "").trim())
    .filter(Boolean);
  const segmentInputs = (Array.isArray(graphConfig.segments) ? graphConfig.segments : [])
    .map((input) => String(input || "").trim())
    .filter(Boolean);
  const range = graphConfig.range || {};
  const graphThemeMode = String(graphConfig.themeMode || "dark") === "light" ? "light" : "dark";
  const xRange = Array.isArray(range.x) && range.x.length === 2 ? range.x.map(Number) : [-10, 10];
  const yRange = Array.isArray(range.y) && range.y.length === 2 ? range.y.map(Number) : [-10, 10];
  const theme = graphThemeMode === "light"
    ? {
        background: "#ffffff",
        plotBackground: "#f8fafc",
        grid: "#cbd5e1",
        strongGrid: "#003366",
        axis: "#003366",
        tick: "#003366",
        holeFill: "#ffffff",
        colors: ["#b000a8", "#b22222", "#d61f69", "#147a31", "#d35400", "#2563eb"],
      }
    : {
        background: "#0f172a",
        plotBackground: "#0b1120",
        grid: "#334155",
        strongGrid: "#00ccff",
        axis: "#00ccff",
        tick: "#00ccff",
        holeFill: "#0f172a",
        colors: ["#ff00ff", "#b22222", "#ff2e63", "#00ff00", "#ff6600", "#6699ff"],
      };
  const colors = theme.colors;
  const {
    leftPad,
    topPad,
    plotWidth,
    plotHeight,
    svgWidth,
    svgHeight,
  } = getGraphSvgLayout(inputs.length);
  const xMin = Number.isFinite(xRange[0]) ? xRange[0] : -10;
  const xMax = Number.isFinite(xRange[1]) ? xRange[1] : 10;
  const yMin = Number.isFinite(yRange[0]) ? yRange[0] : -10;
  const yMax = Number.isFinite(yRange[1]) ? yRange[1] : 10;
  const dx = Math.max(1e-6, xMax - xMin);
  const dy = Math.max(1e-6, yMax - yMin);
  const toPx = (x, y) => ({
    x: leftPad + ((Number(x) - xMin) / dx) * plotWidth,
    y: topPad + ((yMax - Number(y)) / dy) * plotHeight,
  });
  const escapeText = (value) => String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  const stripOuterParens = (value) => {
    const text = String(value || "").trim();
    return text.startsWith("(") && text.endsWith(")") ? text.slice(1, -1).trim() : text;
  };
  const replaceLatexFractionsForSvgText = (value) => {
    let result = String(value || "");
    let guard = 0;
    const fractionPattern = /\\frac\{([^{}]+)\}\{([^{}]+)\}/;
    while (fractionPattern.test(result) && guard < 20) {
      result = result.replace(fractionPattern, (_, numerator, denominator) => {
        return `(${stripOuterParens(numerator)})/(${stripOuterParens(denominator)})`;
      });
      guard += 1;
    }
    return result;
  };
  const formatGraphLabelText = (input) => {
    return replaceLatexFractionsForSvgText(toLatexFraction(input))
      .replace(/\\cdot/g, "·")
      .replace(/\s*=\s*/g, " = ")
      .replace(/\s*([+-])\s*/g, " $1 ")
      .replace(/\s+/g, " ")
      .trim();
  };
  const tokenizeMathLabel = (value) => {
    const text = formatGraphLabelText(value);
    const tokens = [];
    const pushNormal = (content) => {
      if (!content) return;
      const previous = tokens[tokens.length - 1];
      if (previous?.kind === "normal") previous.text += content;
      else tokens.push({ kind: "normal", text: content });
    };

    for (let i = 0; i < text.length; i += 1) {
      if (text.startsWith("\\sqrt{", i)) {
        let depth = 0;
        let end = i + 5;
        for (; end < text.length; end += 1) {
          if (text[end] === "{") depth += 1;
          if (text[end] === "}") depth -= 1;
          if (depth === 0) break;
        }
        tokens.push({ kind: "sqrt", text: text.slice(i + 6, end) });
        i = end;
        continue;
      }
      if (text.startsWith("\\pi", i)) {
        pushNormal("π");
        i += 2;
        continue;
      }
      if (text.startsWith("\\log", i)) {
        pushNormal("log");
        i += 3;
        continue;
      }
      const char = text[i];
      if (char === "^" || char === "_") {
        const kind = char === "^" ? "super" : "sub";
        let next = "";
        if (text[i + 1] === "{") {
          let depth = 0;
          let end = i + 1;
          for (; end < text.length; end += 1) {
            if (text[end] === "{") depth += 1;
            if (text[end] === "}") depth -= 1;
            if (depth === 0) break;
          }
          next = text.slice(i + 2, end);
          i = end;
        } else {
          let end = i + 1;
          while (end < text.length && /[0-9a-zA-Z+-]/.test(text[end])) end += 1;
          next = text.slice(i + 1, end);
          i = end - 1;
        }
        if (next) tokens.push({ kind, text: next });
      } else {
        pushNormal(char);
      }
    }
    return tokens;
  };
  const estimateLabelRunWidth = (text, fontSize) => {
    return Array.from(String(text || "")).reduce((width, char) => {
      if (char === " ") return width + fontSize * 0.24;
      if ("ijlI.,:;|".includes(char)) return width + fontSize * 0.24;
      if ("()[]{}1".includes(char)) return width + fontSize * 0.34;
      if ("+-=*/".includes(char)) return width + fontSize * 0.46;
      return width + fontSize * 0.5;
    }, 0);
  };
  const renderSvgMathText = (input, color, y) => {
    const normalSize = 24;
    const scriptSize = 16;
    let cursor = leftPad;
    const pieces = tokenizeMathLabel(input).map((token) => {
      if (token.kind === "super") {
        const piece = `<text x="${cursor.toFixed(1)}" y="${(y - 12).toFixed(1)}" fill="${color}" font-family="KaTeX_Main, 'Times New Roman', serif" font-size="${scriptSize}" font-style="italic">${escapeText(token.text)}</text>`;
        cursor += estimateLabelRunWidth(token.text, scriptSize) - 1;
        return piece;
      }
      if (token.kind === "sub") {
        const piece = `<text x="${cursor.toFixed(1)}" y="${(y + 8).toFixed(1)}" fill="${color}" font-family="KaTeX_Main, 'Times New Roman', serif" font-size="${scriptSize}" font-style="italic">${escapeText(token.text)}</text>`;
        cursor += estimateLabelRunWidth(token.text, scriptSize) - 1;
        return piece;
      }
      if (token.kind === "sqrt") {
        const rootX = cursor;
        const contentX = rootX + 19;
        const contentWidth = Math.max(12, estimateLabelRunWidth(token.text, normalSize));
        const piece = `
          <g>
            <text x="${rootX.toFixed(1)}" y="${y.toFixed(1)}" fill="${color}" font-family="KaTeX_Main, 'Times New Roman', serif" font-size="${normalSize + 3}" font-style="italic">√</text>
            <line x1="${(contentX - 1).toFixed(1)}" y1="${(y - 20).toFixed(1)}" x2="${(contentX + contentWidth + 2).toFixed(1)}" y2="${(y - 20).toFixed(1)}" stroke="${color}" stroke-width="1.7" stroke-linecap="round"/>
            <text x="${contentX.toFixed(1)}" y="${y.toFixed(1)}" fill="${color}" font-family="KaTeX_Main, 'Times New Roman', serif" font-size="${normalSize}" font-style="italic">${escapeText(token.text)}</text>
          </g>
        `;
        cursor += 22 + contentWidth;
        return piece;
      }
      const piece = `<text x="${cursor.toFixed(1)}" y="${y.toFixed(1)}" fill="${color}" font-family="KaTeX_Main, 'Times New Roman', serif" font-size="${normalSize}" font-style="italic">${escapeText(token.text)}</text>`;
      cursor += estimateLabelRunWidth(token.text, normalSize);
      return piece;
    });
    return `<g>${pieces.join("")}</g>`;
  };
  const renderMathLabel = (input, index) => {
    const y = topPad + plotHeight + 62 + index * 34;
    const color = colors[index % colors.length];
    return renderSvgMathText(input, color, y);
  };
  const grid = [];
  const tickLabels = [];
  // "Ładny" krok podziałki: całe liczby, a połówki dopiero przy mocniejszym
  // zoomie. Nigdy pośrednie wartości typu 1.6 / 4.8 (efekt dzielenia na 10).
  const niceGridStep = (span) => {
    const raw = Math.max(1e-9, span) / 10;
    const steps = [0.5, 1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 5000, 10000];
    return steps.find((step) => step >= raw) || 100000;
  };
  const xStep = niceGridStep(dx);
  const yStep = niceGridStep(dy);

  const xStart = Math.ceil(xMin / xStep - 1e-9) * xStep;
  const xCount = Math.floor((xMax - xStart) / xStep + 1e-9);
  for (let k = 0; k <= xCount; k += 1) {
    const xValue = xStart + k * xStep;
    const px = leftPad + ((xValue - xMin) / dx) * plotWidth;
    grid.push(`<line x1="${px.toFixed(1)}" y1="${topPad}" x2="${px.toFixed(1)}" y2="${topPad + plotHeight}" stroke="${theme.grid}" stroke-width="1" opacity="0.58"/>`);
    if (Math.abs(xValue) > 1e-9) {
      tickLabels.push(`<text x="${px.toFixed(1)}" y="${topPad + plotHeight + 24}" fill="${theme.tick}" font-family="Arial, sans-serif" font-size="18" text-anchor="middle">${Number(xValue.toFixed(2))}</text>`);
    }
  }

  const yStart = Math.ceil(yMin / yStep - 1e-9) * yStep;
  const yCount = Math.floor((yMax - yStart) / yStep + 1e-9);
  for (let k = 0; k <= yCount; k += 1) {
    const yValue = yStart + k * yStep;
    const py = topPad + ((yMax - yValue) / dy) * plotHeight;
    grid.push(`<line x1="${leftPad}" y1="${py.toFixed(1)}" x2="${leftPad + plotWidth}" y2="${py.toFixed(1)}" stroke="${theme.grid}" stroke-width="1" opacity="0.58"/>`);
    if (Math.abs(yValue) > 1e-9) {
      tickLabels.push(`<text x="${leftPad - 12}" y="${(py + 6).toFixed(1)}" fill="${theme.tick}" font-family="Arial, sans-serif" font-size="18" text-anchor="end">${Number(yValue.toFixed(2))}</text>`);
    }
  }
  const axisX = yMin <= 0 && yMax >= 0 ? toPx(0, 0).y : null;
  const axisY = xMin <= 0 && xMax >= 0 ? toPx(0, 0).x : null;
  const traces = [];
  const pointMap = {};

  pointInputs.forEach((pointInput) => {
    const point = parseGraphPointInput(pointInput);
    if (!point) return;
    pointMap[point.label] = point;
  });

  const extendLogSegmentsToVisibleEdge = (segments, asymptotes) => {
    const list = Array.isArray(segments) ? segments : [];
    const sortedAsymptotes = Array.isArray(asymptotes)
      ? asymptotes.filter((value) => Number.isFinite(Number(value))).map(Number).sort((a, b) => a - b)
      : [];
    if (!list.length || !sortedAsymptotes.length) return list;

    const yMiddle = (yMin + yMax) / 2;
    const xTolerance = Math.max(dx / 15, Math.abs(xMax - xMin) * 1e-5);
    return list.map((segment) => {
      const xs = Array.isArray(segment?.x) ? segment.x.map(Number) : [];
      const ys = Array.isArray(segment?.y) ? segment.y.map(Number) : [];
      if (xs.length < 2 || ys.length < 2) return segment;

      const firstX = xs[0];
      const secondX = xs[1];
      const firstY = ys[0];
      const direction = Math.sign(secondX - firstX) || 1;
      const matchingAsymptote = sortedAsymptotes.find((xVal) => {
        if (direction > 0) return xVal <= firstX && Math.abs(firstX - xVal) <= Math.max(dx * 1.25, xTolerance);
        return xVal >= firstX && Math.abs(firstX - xVal) <= Math.max(dx * 1.25, xTolerance);
      });
      if (!Number.isFinite(matchingAsymptote)) return segment;

      const edgeY = firstY < yMiddle ? yMin : yMax;
      return {
        ...segment,
        x: [firstX, ...xs],
        y: [edgeY, ...ys],
      };
    });
  };

  inputs.forEach((input, index) => {
    const color = colors[index % colors.length];
    const equationType = detectEquationType(input);
    const pushSegments = (segments) => {
      (segments || []).forEach((segment) => {
        const coords = (segment.x || []).map((x, i) => {
          const y = segment.y?.[i];
          if (!Number.isFinite(Number(x)) || !Number.isFinite(Number(y))) return null;
          if (Number(x) < xMin - dx * 0.05 || Number(x) > xMax + dx * 0.05 || Number(y) < yMin - dy * 0.2 || Number(y) > yMax + dy * 0.2) return null;
          const point = toPx(x, y);
          return `${point.x.toFixed(1)},${point.y.toFixed(1)}`;
        }).filter(Boolean);
        if (coords.length >= 2) {
          traces.push(`<polyline points="${coords.join(" ")}" fill="none" stroke="${color}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>`);
        }
      });
    };

    if (equationType === "vertical-line") {
      const xVal = Number(input.split("=")[1]);
      if (Number.isFinite(xVal)) {
        const top = toPx(xVal, yMax);
        const bottom = toPx(xVal, yMin);
        traces.push(`<line x1="${top.x.toFixed(1)}" y1="${top.y.toFixed(1)}" x2="${bottom.x.toFixed(1)}" y2="${bottom.y.toFixed(1)}" stroke="${color}" stroke-width="4"/>`);
      }
      return;
    }

    if (equationType === "function") {
      const rhs = input.split("=")[1];
      if (!rhs) return;

      if (extractLogArguments(rhs).length > 0) {
        const logResult = generateLogarithmicData(rhs, xMin, xMax, yMin, yMax, dx / 900);
        pushSegments(extendLogSegmentsToVisibleEdge(logResult.segments, logResult.asymptotes));
        logResult.asymptotes.forEach((xVal) => {
          if (Math.abs(xVal) < 1e-6 || xVal < xMin || xVal > xMax) return;
          const top = toPx(xVal, yMax);
          const bottom = toPx(xVal, yMin);
          traces.push(`<line x1="${top.x.toFixed(1)}" y1="${top.y.toFixed(1)}" x2="${bottom.x.toFixed(1)}" y2="${bottom.y.toFixed(1)}" stroke="${color}" stroke-width="2.5" stroke-dasharray="10 8" opacity="0.75"/>`);
        });
        return;
      }

      if (detectExponentialFunction(rhs)) {
        pushSegments(generatePolynomialData(rhs, xMin, xMax, dx / 900));
        const hAsymptote = computeHorizontalAsymptote(rhs, xMin, xMax);
        if (hAsymptote !== null && Math.abs(hAsymptote) > 1e-6 && hAsymptote >= yMin && hAsymptote <= yMax) {
          const left = toPx(xMin, hAsymptote);
          const right = toPx(xMax, hAsymptote);
          traces.push(`<line x1="${left.x.toFixed(1)}" y1="${left.y.toFixed(1)}" x2="${right.x.toFixed(1)}" y2="${right.y.toFixed(1)}" stroke="${color}" stroke-width="2.5" stroke-dasharray="10 8" opacity="0.75"/>`);
        }
        return;
      }

      if (rhs.includes("/")) {
        const result = generateRationalData(rhs, xMin, xMax, yMin, yMax, dx / 900);
        pushSegments(result.segments);
        result.asymptotes.forEach((xVal) => {
          const top = toPx(xVal, yMax);
          const bottom = toPx(xVal, yMin);
          traces.push(`<line x1="${top.x.toFixed(1)}" y1="${top.y.toFixed(1)}" x2="${bottom.x.toFixed(1)}" y2="${bottom.y.toFixed(1)}" stroke="${color}" stroke-width="2.5" stroke-dasharray="10 8" opacity="0.75"/>`);
        });
        result.holes.forEach((hole) => {
          const point = toPx(hole.x, hole.y);
          traces.push(`<circle cx="${point.x.toFixed(1)}" cy="${point.y.toFixed(1)}" r="7" fill="${theme.holeFill}" stroke="${color}" stroke-width="3"/>`);
        });
      } else {
        pushSegments(generatePolynomialData(rhs, xMin, xMax, dx / 900));
      }
      return;
    }

    if (equationType === "general-line") {
      pushSegments(generateGeneralLineData(input, xMin, xMax, dx / 900));
      return;
    }

    if (equationType === "circle") {
      pushSegments(generateCircleData(input, 0.01));
      return;
    }

    if (equationType === "circle-general") {
      const params = convertGeneralToCircle(input);
      if (!params) return;
      const points = [];
      for (let t = 0; t <= Math.PI * 2; t += 0.02) {
        const point = toPx(params.a + params.r * Math.cos(t), params.b + params.r * Math.sin(t));
        points.push(`${point.x.toFixed(1)},${point.y.toFixed(1)}`);
      }
      traces.push(`<polyline points="${points.join(" ")}" fill="none" stroke="${color}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>`);
    }
  });

  segmentInputs.forEach((segmentInput, segmentIndex) => {
    const segment = parseGraphSegmentInput(segmentInput);
    if (!segment) return;
    const start = pointMap[segment.a];
    const end = pointMap[segment.b];
    if (!start || !end) return;
    const color = colors[(inputs.length + segmentIndex) % colors.length];
    const a = toPx(start.x, start.y);
    const b = toPx(end.x, end.y);
    traces.push(`<line x1="${a.x.toFixed(1)}" y1="${a.y.toFixed(1)}" x2="${b.x.toFixed(1)}" y2="${b.y.toFixed(1)}" stroke="${color}" stroke-width="4" stroke-linecap="round"/>`);
  });

  Object.values(pointMap).forEach((point, pointIndex) => {
    const color = colors[(inputs.length + segmentInputs.length + pointIndex) % colors.length];
    const px = toPx(point.x, point.y);
    traces.push(`<circle cx="${px.x.toFixed(1)}" cy="${px.y.toFixed(1)}" r="6" fill="${color}" stroke="${theme.background}" stroke-width="2"/>`);
    traces.push(`<text x="${(px.x + 10).toFixed(1)}" y="${(px.y - 10).toFixed(1)}" fill="${color}" font-family="Arial, sans-serif" font-size="18" font-weight="700">${escapeText(point.label)}</text>`);
  });

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}">
      <defs>
        <clipPath id="graphPlotClip">
          <rect x="${leftPad}" y="${topPad}" width="${plotWidth}" height="${plotHeight}"/>
        </clipPath>
      </defs>
      <rect width="${svgWidth}" height="${svgHeight}" rx="18" ry="18" fill="${theme.background}"/>
      <rect x="${leftPad}" y="${topPad}" width="${plotWidth}" height="${plotHeight}" fill="${theme.plotBackground}"/>
      ${grid.join("")}
      ${axisX !== null ? `<line x1="${leftPad}" y1="${axisX.toFixed(1)}" x2="${leftPad + plotWidth}" y2="${axisX.toFixed(1)}" stroke="${theme.axis}" stroke-width="3"/>` : ""}
      ${axisY !== null ? `<line x1="${axisY.toFixed(1)}" y1="${topPad}" x2="${axisY.toFixed(1)}" y2="${topPad + plotHeight}" stroke="${theme.axis}" stroke-width="3"/>` : ""}
      ${axisX !== null ? `<path d="M${leftPad + plotWidth - 16} ${axisX - 8}L${leftPad + plotWidth} ${axisX}L${leftPad + plotWidth - 16} ${axisX + 8}" fill="none" stroke="${theme.axis}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>` : ""}
      ${axisY !== null ? `<path d="M${axisY - 8} ${topPad + 16}L${axisY} ${topPad}L${axisY + 8} ${topPad + 16}" fill="none" stroke="${theme.axis}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>` : ""}
      <g clip-path="url(#graphPlotClip)">${traces.join("")}</g>
      ${tickLabels.join("")}
      <text x="${leftPad + plotWidth - 18}" y="${axisX !== null ? axisX + 30 : topPad + plotHeight - 10}" fill="${theme.axis}" font-family="Arial, sans-serif" font-size="26" font-weight="700">x</text>
      <text x="${axisY !== null ? axisY + 20 : leftPad + 14}" y="${topPad + 22}" fill="${theme.axis}" font-family="Arial, sans-serif" font-size="26" font-weight="700">y</text>
      ${inputs.map(renderMathLabel).join("")}
    </svg>
  `;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg.replace(/\s+/g, " ").trim())}`;
}

function resolveDefaultGraphTheme(themeMode, boardSurface) {
  const surface = String(boardSurface || "default").toLowerCase();
  if (surface === "white") return "light";
  if (surface === "black") return "dark";
  return String(themeMode || "dark") === "light" ? "light" : "dark";
}

function cloneTaskBlocks(blocks) {
  return Array.isArray(blocks) ? JSON.parse(JSON.stringify(blocks)) : [];
}

function getTaskCollapsePrefsStorageKey(roomCode) {
  return `${TASK_COLLAPSE_PREFS_STORAGE_PREFIX}.${String(roomCode || "room").toUpperCase()}`;
}

function readTaskCollapsePrefs(roomCode) {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(getTaskCollapsePrefsStorageKey(roomCode));
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeTaskCollapsePrefs(roomCode, prefs) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(getTaskCollapsePrefsStorageKey(roomCode), JSON.stringify(prefs || {}));
  } catch {
    // Local UI preference only; ignore storage failures.
  }
}

function getTaskDockHiddenStorageKey(roomCode) {
  return `${TASK_DOCK_HIDDEN_STORAGE_PREFIX}.${String(roomCode || "room").toUpperCase()}`;
}

function readTaskDockHidden(roomCode) {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(getTaskDockHiddenStorageKey(roomCode)) === "1";
  } catch {
    return false;
  }
}

function writeTaskDockHidden(roomCode, hidden) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(getTaskDockHiddenStorageKey(roomCode), hidden ? "1" : "0");
  } catch {
    // Local UI preference only; ignore storage failures.
  }
}

function roundWireCoord(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.round(numeric * 100) / 100;
}

function compactPointForWire(point) {
  if (Array.isArray(point)) {
    return [roundWireCoord(point[0]), roundWireCoord(point[1])];
  }
  return [roundWireCoord(point?.x), roundWireCoord(point?.y)];
}

function normalizeWirePointForState(point) {
  if (Array.isArray(point)) {
    return {
      x: Number(point[0] || 0),
      y: Number(point[1] || 0),
    };
  }
  return {
    x: Number(point?.x || 0),
    y: Number(point?.y || 0),
  };
}

function compactElementForWire(element) {
  if (!element || typeof element !== "object") return element;
  if (String(element.type || "") !== "stroke") return element;
  return {
    ...element,
    points: (Array.isArray(element.points) ? element.points : []).map(compactPointForWire),
  };
}

function normalizeWireElementForState(element) {
  if (!element || typeof element !== "object") return element;
  if (String(element.type || "") !== "stroke") return element;
  return {
    ...element,
    points: (Array.isArray(element.points) ? element.points : []).map(normalizeWirePointForState),
  };
}

function normalizeTaskBlockId(id) {
  const normalized = String(id || "").trim();
  return normalized || null;
}

function resolveActiveTaskBlockId(taskBlocks, preferredId) {
  const blocks = Array.isArray(taskBlocks) ? taskBlocks : [];
  if (!blocks.length) return null;
  const preferred = normalizeTaskBlockId(preferredId);
  if (preferred && blocks.some((block) => String(block?.id || "") === preferred)) {
    return preferred;
  }
  return normalizeTaskBlockId(blocks[0]?.id) || null;
}

function ensureElementIds(elements) {
  const list = Array.isArray(elements) ? elements : [];
  const nowBase = Date.now();
  return list.map((element, index) => {
    if (!element || typeof element !== "object") return element;
    const existingId = String(element.id || "").trim();
    if (existingId) return element;
    const type = String(element.type || "el").trim() || "el";
    return {
      ...element,
      id: `${type}_${nowBase}_${index}_${Math.random().toString(16).slice(2, 8)}`,
    };
  });
}

function normalizeBoardState(state) {
  const source = state || {};
  const normalizeActionMap = (raw) => (
    raw && typeof raw === "object"
      ? Object.entries(raw).reduce((acc, [userId, entries]) => {
          if (!userId) return acc;
          acc[String(userId)] = Array.isArray(entries) ? JSON.parse(JSON.stringify(entries)).slice(-HISTORY_LIMIT) : [];
          return acc;
        }, {})
      : {}
  );
  return {
    version: Number(source.version || 1),
    elements: ensureElementIds(cloneElements(source.elements)),
    pageCount: Math.max(1, Math.min(MAX_PAGE_COUNT, Number(source.pageCount || DEFAULT_PAGE_COUNT))),
    taskBlocks: cloneTaskBlocks(source.taskBlocks),
    activeTaskBlockId: resolveActiveTaskBlockId(source.taskBlocks, source.activeTaskBlockId),
    redoByUser:
      source.redoByUser && typeof source.redoByUser === "object"
        ? Object.entries(source.redoByUser).reduce((acc, [userId, entries]) => {
            if (!userId) return acc;
            acc[String(userId)] = (Array.isArray(entries) ? entries : [])
              .map((entry) => {
                if (entry && typeof entry === "object" && entry.element && typeof entry.element === "object") {
                  return {
                    index: Number.isFinite(Number(entry.index)) ? Number(entry.index) : null,
                    element: cloneElements([entry.element])[0],
                  };
                }
                if (entry && typeof entry === "object") {
                  return { index: null, element: cloneElements([entry])[0] };
                }
                return null;
              })
              .filter(Boolean);
            return acc;
          }, {})
        : {},
    actionLogByUser: normalizeActionMap(source.actionLogByUser),
    redoActionLogByUser: normalizeActionMap(source.redoActionLogByUser),
    undoStack: Array.isArray(source.undoStack) ? source.undoStack.map(cloneElements) : [],
    redoStack: Array.isArray(source.redoStack) ? source.redoStack.map(cloneElements) : [],
    updatedAt: source.updatedAt || Date.now(),
    updatedBy: source.updatedBy || null,
  };
}

function splitMoveKey(key) {
  const raw = String(key || "");
  const idx = raw.indexOf(":");
  if (idx <= 0) return { type: "", id: "" };
  return {
    type: raw.slice(0, idx),
    id: raw.slice(idx + 1),
  };
}

function matchesMoveTarget(element, index, key) {
  const { type, id } = splitMoveKey(key);
  if (!type || !id) return false;
  if (type === "image" && String(element?.type || "") === "image") {
    if (element?.id) return String(element.id) === id;
    return `image_${index}` === id;
  }
  if (type === "stroke" && String(element?.type || "") === "stroke") {
    if (element?.id) return String(element.id) === id;
    return `stroke_${index}` === id;
  }
  return false;
}

function translateElementByDelta(element, dx, dy) {
  if (!dx && !dy) return element;
  if (String(element?.type || "") === "image") {
    return {
      ...element,
      x: Number(element.x || 0) + dx,
      y: Number(element.y || 0) + dy,
    };
  }
  if (String(element?.type || "") === "stroke") {
    const points = Array.isArray(element.points) ? element.points : [];
    return {
      ...element,
      points: points.map((point) => ({
        ...point,
        x: Number(point.x || 0) + dx,
        y: Number(point.y || 0) + dy,
      })),
    };
  }
  return element;
}

function normalizeCrop(crop) {
  const raw = crop || {};
  const left = Math.max(0, Math.min(0.95, Number(raw.left ?? 0)));
  const top = Math.max(0, Math.min(0.95, Number(raw.top ?? 0)));
  const right = Math.max(left + 0.05, Math.min(1, Number(raw.right ?? 1)));
  const bottom = Math.max(top + 0.05, Math.min(1, Number(raw.bottom ?? 1)));
  return { left, top, right, bottom };
}

function cloneGraphConfigForPatch(graph) {
  if (!graph || typeof graph !== "object") return null;
  try {
    return JSON.parse(JSON.stringify(graph));
  } catch {
    return null;
  }
}

function cloneTextWidgetForPatch(textWidget) {
  if (!textWidget || typeof textWidget !== "object") return null;
  return {
    version: 1,
    text: String(textWidget.text || "").slice(0, 20000),
    color: String(textWidget.color || "#f8fafc").slice(0, 64),
    fontFamily: "Rubik",
    fontSize: Math.max(12, Math.min(160, Number(textWidget.fontSize || 36))),
    fontWeight: Math.max(300, Math.min(900, Math.round(Number(textWidget.fontWeight || 400) / 100) * 100)),
  };
}

function applyMoveToElements(elements, movedIds, dx, dy, actor) {
  const ids = Array.isArray(movedIds) ? movedIds.map((id) => String(id || "")).filter(Boolean) : [];
  if (!ids.length || (!dx && !dy)) return cloneElements(elements);
  return (Array.isArray(elements) ? elements : []).map((element, index) => {
    const isTarget = ids.some((key) => matchesMoveTarget(element, index, key));
    if (!isTarget) return { ...element };
    return translateElementByDelta(element, dx, dy);
  });
}

function scaleElementByFactor(element, scale, originX, originY) {
  const safeScale = Number.isFinite(scale) ? Math.max(0.05, Number(scale)) : 1;
  const ox = Number(originX || 0);
  const oy = Number(originY || 0);
  if (Math.abs(safeScale - 1) < 1e-6) return { ...element };

  if (String(element?.type || "") === "image") {
    const x = Number(element?.x || 0);
    const y = Number(element?.y || 0);
    const width = Math.max(1, Number(element?.width || 1));
    const height = Math.max(1, Number(element?.height || 1));
    return {
      ...element,
      x: ox + (x - ox) * safeScale,
      y: oy + (y - oy) * safeScale,
      width: Math.max(20, width * safeScale),
      height: Math.max(20, height * safeScale),
    };
  }

  if (String(element?.type || "") === "stroke") {
    const points = Array.isArray(element?.points) ? element.points : [];
    return {
      ...element,
      // Celowo nie skalujemy grubości kreski przy zmianie rozmiaru zaznaczenia.
      width: Math.max(1, Number(element?.width || 1)),
      points: points.map((point) => ({
        ...point,
        x: ox + (Number(point?.x || 0) - ox) * safeScale,
        y: oy + (Number(point?.y || 0) - oy) * safeScale,
      })),
    };
  }

  return { ...element };
}

function applyResizeToElements(elements, resizedIds, scale, originX, originY, actor) {
  const ids = Array.isArray(resizedIds) ? resizedIds.map((id) => String(id || "")).filter(Boolean) : [];
  const safeScale = Number.isFinite(scale) ? Number(scale) : 1;
  if (!ids.length || Math.abs(safeScale - 1) < 1e-6) return cloneElements(elements);
  return (Array.isArray(elements) ? elements : []).map((element, index) => {
    const isTarget = ids.some((key) => matchesMoveTarget(element, index, key));
    if (!isTarget) return { ...element };
    return scaleElementByFactor(element, safeScale, originX, originY);
  });
}

function pushHistory(stack, elements) {
  const next = [...stack, cloneElements(elements)];
  return next.slice(-HISTORY_LIMIT);
}

function insertElementAt(elements, index, element) {
  const list = cloneElements(elements);
  const safeIndex = Number.isFinite(Number(index))
    ? Math.max(0, Math.min(list.length, Number(index)))
    : list.length;
  return [...list.slice(0, safeIndex), cloneElements([element])[0], ...list.slice(safeIndex)];
}

function replaceElementById(elements, elementId, replacement) {
  const id = String(elementId || "");
  if (!id) return cloneElements(elements);
  const list = cloneElements(elements);
  let replaced = false;
  const next = list.map((element) => {
    if (String(element?.id || "") !== id) return element;
    replaced = true;
    return cloneElements([replacement])[0];
  });
  if (!replaced) {
    next.push(cloneElements([replacement])[0]);
  }
  return next;
}

function stampNewElementsWithAuthor(baseElements, nextElements, userId) {
  const actor = String(userId || "");
  if (!actor) return cloneElements(nextElements);
  const baseIdSet = new Set(
    (Array.isArray(baseElements) ? baseElements : []).map((el, index) => elementStableId(el, index))
  );
  return (Array.isArray(nextElements) ? nextElements : []).map((el, index) => {
    const stableId = elementStableId(el, index);
    if (baseIdSet.has(stableId)) return { ...el };
    if (el?.createdBy) return { ...el };
    return { ...el, createdBy: actor };
  });
}

function computeNextBoardState(baseState, normalizedIncoming, options = {}, userId = null) {
  const base = normalizeBoardState(baseState);
  const actor = String(userId || "");
  const nextElements = stampNewElementsWithAuthor(base.elements, normalizedIncoming?.elements, actor);
  const nextTaskBlocks = cloneTaskBlocks(normalizedIncoming?.taskBlocks);
  const nextActiveTaskBlockId = resolveActiveTaskBlockId(
    nextTaskBlocks,
    normalizedIncoming?.activeTaskBlockId ?? base.activeTaskBlockId
  );
  const actionLog = Array.isArray(base.actionLogByUser?.[actor]) ? base.actionLogByUser[actor] : [];
  const redoActionLog = Array.isArray(base.redoActionLogByUser?.[actor]) ? base.redoActionLogByUser[actor] : [];
  let finalState = null;

  if (options.history === "undo") {
    if (actionLog.length) {
      const latestAction = actionLog[actionLog.length - 1];
      const nextActionLogByUser = { ...(base.actionLogByUser || {}), [actor]: actionLog.slice(0, -1) };
      const nextRedoActionLogByUser = {
        ...(base.redoActionLogByUser || {}),
        [actor]: [...redoActionLog, cloneElements([latestAction])[0]].slice(-HISTORY_LIMIT),
      };

      let undoneElements = cloneElements(base.elements);
      if (latestAction?.kind === "draw" && latestAction?.elementId) {
        const targetId = String(latestAction.elementId);
        undoneElements = undoneElements.filter((element) => String(element?.id || "") !== targetId);
      } else if (latestAction?.kind === "erase" && Array.isArray(latestAction?.removedEntries)) {
        for (const entry of latestAction.removedEntries) {
          if (!entry?.element) continue;
          undoneElements = insertElementAt(undoneElements, entry?.index, entry.element);
        }
      } else if (latestAction?.kind === "image_delete" && latestAction?.element) {
        undoneElements = insertElementAt(undoneElements, latestAction?.index, latestAction.element);
      } else if (latestAction?.kind === "image_patch" && latestAction?.elementId && latestAction?.before) {
        undoneElements = replaceElementById(undoneElements, latestAction.elementId, latestAction.before);
      } else if (latestAction?.kind === "selection_delete" && Array.isArray(latestAction?.removedEntries)) {
        for (const entry of latestAction.removedEntries) {
          if (!entry?.element) continue;
          undoneElements = insertElementAt(undoneElements, entry?.index, entry.element);
        }
      } else if (latestAction?.kind === "selection_move") {
        const dx = Number(latestAction?.dx || 0);
        const dy = Number(latestAction?.dy || 0);
        undoneElements = applyMoveToElements(undoneElements, latestAction?.movedIds, -dx, -dy, actor);
      } else if (latestAction?.kind === "selection_resize") {
        const scale = Number(latestAction?.scale || 1);
        const originX = Number(latestAction?.originX || 0);
        const originY = Number(latestAction?.originY || 0);
        const inverseScale = scale > 0 ? 1 / scale : 1;
        undoneElements = applyResizeToElements(
          undoneElements,
          latestAction?.resizedIds,
          inverseScale,
          originX,
          originY,
          actor
        );
      } else if (latestAction?.kind === "line_style" && latestAction?.elementId) {
        const targetId = String(latestAction.elementId || "");
        const beforeLineStyle = String(latestAction?.beforeLineStyle || "solid");
        undoneElements = undoneElements.map((element) => {
          if (String(element?.type || "") !== "stroke") return element;
          if (String(element?.id || "") !== targetId) return element;
          return {
            ...element,
            lineStyle: beforeLineStyle,
          };
        });
      } else if (latestAction?.kind === "stroke_patch" && latestAction?.elementId && latestAction?.before) {
        undoneElements = replaceElementById(undoneElements, latestAction.elementId, latestAction.before);
      } else {
        return null;
      }

      const nextRedoEntries = { ...(base.redoByUser || {}) };
      if (actor) nextRedoEntries[actor] = [];
      finalState = {
        ...base,
        version: base.version + 1,
        elements: undoneElements,
        redoByUser: nextRedoEntries,
        actionLogByUser: nextActionLogByUser,
        redoActionLogByUser: nextRedoActionLogByUser,
      };
    } else {
      const ownedIndex = base.elements
        .map((element, index) => ({ element, index }))
        .filter(({ element }) => String(element?.createdBy || "") === actor)
        .map(({ index }) => index)
        .pop();
      if (!Number.isInteger(ownedIndex)) return null;
      const removedElement = cloneElements([base.elements[ownedIndex]])[0];
      const nextRedoEntries = [...(base.redoByUser?.[actor] || []), { index: ownedIndex, element: removedElement }].slice(-HISTORY_LIMIT);
      const nextRedoByUser = { ...(base.redoByUser || {}), [actor]: nextRedoEntries };
      finalState = {
        ...base,
        version: base.version + 1,
        elements: [...base.elements.slice(0, ownedIndex), ...base.elements.slice(ownedIndex + 1)],
        redoByUser: nextRedoByUser,
      };
    }
  } else if (options.history === "redo") {
    if (redoActionLog.length) {
      const actionToReplay = redoActionLog[redoActionLog.length - 1];
      const nextRedoActionLogByUser = {
        ...(base.redoActionLogByUser || {}),
        [actor]: redoActionLog.slice(0, -1),
      };

      let redoneElements = cloneElements(base.elements);
      if (actionToReplay?.kind === "draw") {
        const element = actionToReplay?.element;
        const targetId = String(actionToReplay?.elementId || element?.id || "");
        if (!targetId || !element) return null;
        const exists = redoneElements.some((entry) => String(entry?.id || "") === targetId);
        if (!exists) redoneElements.push(cloneElements([element])[0]);
      } else if (actionToReplay?.kind === "erase" && Array.isArray(actionToReplay?.removedEntries)) {
        const removedIdSet = new Set(
          actionToReplay.removedEntries
            .map((entry) => String(entry?.element?.id || ""))
            .filter(Boolean)
        );
        if (removedIdSet.size) {
          redoneElements = redoneElements.filter((entry) => !removedIdSet.has(String(entry?.id || "")));
        }
      } else if (actionToReplay?.kind === "image_delete" && actionToReplay?.elementId) {
        const targetId = String(actionToReplay.elementId || "");
        redoneElements = redoneElements.filter((entry) => String(entry?.id || "") !== targetId);
      } else if (actionToReplay?.kind === "image_patch" && actionToReplay?.elementId && actionToReplay?.after) {
        redoneElements = replaceElementById(redoneElements, actionToReplay.elementId, actionToReplay.after);
      } else if (actionToReplay?.kind === "selection_delete" && Array.isArray(actionToReplay?.removedEntries)) {
        const removedIdSet = new Set(
          actionToReplay.removedEntries
            .map((entry) => String(entry?.element?.id || ""))
            .filter(Boolean)
        );
        if (removedIdSet.size) {
          redoneElements = redoneElements.filter((entry) => !removedIdSet.has(String(entry?.id || "")));
        }
      } else if (actionToReplay?.kind === "selection_move") {
        const dx = Number(actionToReplay?.dx || 0);
        const dy = Number(actionToReplay?.dy || 0);
        redoneElements = applyMoveToElements(redoneElements, actionToReplay?.movedIds, dx, dy, actor);
      } else if (actionToReplay?.kind === "selection_resize") {
        const scale = Number(actionToReplay?.scale || 1);
        const originX = Number(actionToReplay?.originX || 0);
        const originY = Number(actionToReplay?.originY || 0);
        redoneElements = applyResizeToElements(
          redoneElements,
          actionToReplay?.resizedIds,
          scale,
          originX,
          originY,
          actor
        );
      } else if (actionToReplay?.kind === "line_style" && actionToReplay?.elementId) {
        const targetId = String(actionToReplay.elementId || "");
        const afterLineStyle = String(actionToReplay?.afterLineStyle || "solid");
        redoneElements = redoneElements.map((element) => {
          if (String(element?.type || "") !== "stroke") return element;
          if (String(element?.id || "") !== targetId) return element;
          return {
            ...element,
            lineStyle: afterLineStyle,
          };
        });
      } else if (actionToReplay?.kind === "stroke_patch" && actionToReplay?.elementId && actionToReplay?.after) {
        redoneElements = replaceElementById(redoneElements, actionToReplay.elementId, actionToReplay.after);
      } else {
        return null;
      }

      const nextActionLogByUser = {
        ...(base.actionLogByUser || {}),
        [actor]: [...actionLog, cloneElements([actionToReplay])[0]].slice(-HISTORY_LIMIT),
      };
      const nextRedoEntries = { ...(base.redoByUser || {}) };
      if (actor) nextRedoEntries[actor] = [];
      finalState = {
        ...base,
        version: base.version + 1,
        elements: redoneElements,
        redoByUser: nextRedoEntries,
        actionLogByUser: nextActionLogByUser,
        redoActionLogByUser: nextRedoActionLogByUser,
      };
    } else {
      const userRedoEntries = Array.isArray(base.redoByUser?.[actor]) ? base.redoByUser[actor] : [];
      if (!userRedoEntries.length) return null;
      const latestEntry = userRedoEntries[userRedoEntries.length - 1];
      const restoredElement = cloneElements([latestEntry?.element || latestEntry])[0];
      const requestedIndex = Number(latestEntry?.index);
      const insertIndex = Number.isFinite(requestedIndex)
        ? Math.max(0, Math.min(base.elements.length, requestedIndex))
        : base.elements.length;
      const nextRedoEntries = userRedoEntries.slice(0, -1);
      const nextRedoByUser = { ...(base.redoByUser || {}), [actor]: nextRedoEntries };
      finalState = {
        ...base,
        version: base.version + 1,
        elements: [...base.elements.slice(0, insertIndex), restoredElement, ...base.elements.slice(insertIndex)],
        redoByUser: nextRedoByUser,
      };
    }
  } else if (options.history === "skip") {
    finalState = {
      ...base,
      ...normalizedIncoming,
      version: base.version + 1,
      elements: nextElements,
      taskBlocks: nextTaskBlocks,
      activeTaskBlockId: nextActiveTaskBlockId,
    };
  } else {
    const sameElements = JSON.stringify(base.elements) === JSON.stringify(nextElements);
    const sameTaskBlocks = JSON.stringify(base.taskBlocks || []) === JSON.stringify(nextTaskBlocks);
    const sameActiveTask = normalizeTaskBlockId(base.activeTaskBlockId) === normalizeTaskBlockId(nextActiveTaskBlockId);
    if (sameElements && sameTaskBlocks && sameActiveTask) return null;
    const nextRedoByUser = { ...(base.redoByUser || {}) };
    if (actor) nextRedoByUser[actor] = [];
    const nextActionLogByUser = { ...(base.actionLogByUser || {}) };
    const nextRedoActionLogByUser = { ...(base.redoActionLogByUser || {}) };
    if (actor) {
      nextRedoActionLogByUser[actor] = [];
      if (options?.kind === "draw") {
        const appendedElement = nextElements[nextElements.length - 1];
        if (appendedElement?.id) {
          const entry = {
            kind: "draw",
            elementId: String(appendedElement.id),
            element: cloneElements([appendedElement])[0],
          };
          nextActionLogByUser[actor] = [...(actionLog || []), entry].slice(-HISTORY_LIMIT);
        }
      } else if (options?.kind === "erase") {
        const removedIds = Array.isArray(options?.removedIds)
          ? options.removedIds.map((id) => String(id || "").trim()).filter(Boolean)
          : [];
        if (removedIds.length) {
          const removedEntries = (base.elements || [])
            .map((element, index) => ({ element, index }))
            .filter(({ element }) => removedIds.includes(String(element?.id || "")))
            .map(({ element, index }) => ({
              index,
              element: cloneElements([element])[0],
            }));
          if (removedEntries.length) {
            const entry = {
              kind: "erase",
              removedEntries,
            };
            nextActionLogByUser[actor] = [...(actionLog || []), entry].slice(-HISTORY_LIMIT);
          }
        }
      } else if (options?.kind === "image_delete") {
        const removedImages = (base.elements || [])
          .map((element, index) => ({ element, index }))
          .filter(({ element }) => String(element?.type || "") === "image")
          .filter(({ element }) => !nextElements.some((nextElement) => String(nextElement?.id || "") === String(element?.id || "")));
        const removed = removedImages[removedImages.length - 1];
        if (removed?.element?.id) {
          const entry = {
            kind: "image_delete",
            elementId: String(removed.element.id),
            element: cloneElements([removed.element])[0],
            index: Number(removed.index || 0),
          };
          nextActionLogByUser[actor] = [...(actionLog || []), entry].slice(-HISTORY_LIMIT);
        }
      } else if (options?.kind === "image_transform" || options?.kind === "image_crop" || options?.kind === "image_rotate") {
        const baseById = new Map((base.elements || []).map((element) => [String(element?.id || ""), element]));
        const nextById = new Map((nextElements || []).map((element) => [String(element?.id || ""), element]));
        const changed = Array.from(nextById.keys()).find((id) => {
          if (!id || !baseById.has(id)) return false;
          return JSON.stringify(baseById.get(id)) !== JSON.stringify(nextById.get(id));
        });
        if (changed) {
          const entry = {
            kind: "image_patch",
            elementId: changed,
            before: cloneElements([baseById.get(changed)])[0],
            after: cloneElements([nextById.get(changed)])[0],
          };
          nextActionLogByUser[actor] = [...(actionLog || []), entry].slice(-HISTORY_LIMIT);
        }
      } else if (options?.kind === "selection_delete") {
        const removedIds = Array.isArray(options?.removedIds)
          ? options.removedIds.map((id) => String(id || "").trim()).filter(Boolean)
          : [];
        if (removedIds.length) {
          const removedEntries = (base.elements || [])
            .map((element, index) => ({ element, index }))
            .filter(({ element }) => removedIds.includes(String(element?.id || "")))
            .map(({ element, index }) => ({
              index,
              element: cloneElements([element])[0],
            }));
          if (removedEntries.length) {
            const entry = {
              kind: "selection_delete",
              removedEntries,
            };
            nextActionLogByUser[actor] = [...(actionLog || []), entry].slice(-HISTORY_LIMIT);
          }
        }
      } else if (options?.kind === "selection_move") {
        const movedIds = Array.isArray(options?.movedIds)
          ? options.movedIds.map((id) => String(id || "")).filter(Boolean)
          : [];
        const dx = Number(options?.dx || 0);
        const dy = Number(options?.dy || 0);
        if (movedIds.length && (dx || dy)) {
          const entry = {
            kind: "selection_move",
            movedIds,
            dx,
            dy,
          };
          nextActionLogByUser[actor] = [...(actionLog || []), entry].slice(-HISTORY_LIMIT);
        }
      } else if (options?.kind === "selection_resize") {
        const resizedIds = Array.isArray(options?.resizedIds)
          ? options.resizedIds.map((id) => String(id || "")).filter(Boolean)
          : [];
        const scale = Number(options?.scale || 1);
        const originX = Number(options?.originX || 0);
        const originY = Number(options?.originY || 0);
        if (resizedIds.length && Math.abs(scale - 1) > 1e-6) {
          const entry = {
            kind: "selection_resize",
            resizedIds,
            scale,
            originX,
            originY,
          };
          nextActionLogByUser[actor] = [...(actionLog || []), entry].slice(-HISTORY_LIMIT);
        }
      } else if (options?.kind === "line_style") {
        const targetId = String(options?.elementId || "").trim();
        if (targetId) {
          const beforeElement = (base.elements || []).find((element) => String(element?.id || "") === targetId);
          const afterElement = (nextElements || []).find((element) => String(element?.id || "") === targetId);
          if (beforeElement && afterElement) {
            const beforeLineStyle = String(beforeElement?.lineStyle || "solid");
            const afterLineStyle = String(afterElement?.lineStyle || "solid");
            if (beforeLineStyle !== afterLineStyle) {
              const entry = {
                kind: "line_style",
                elementId: targetId,
                beforeLineStyle,
                afterLineStyle,
              };
              nextActionLogByUser[actor] = [...(actionLog || []), entry].slice(-HISTORY_LIMIT);
            }
          }
        }
      } else if (options?.kind === "stroke_patch") {
        const targetId = String(options?.elementId || "").trim();
        if (targetId) {
          const beforeElement = (base.elements || []).find(
            (element) => String(element?.id || "") === targetId && String(element?.type || "") === "stroke"
          );
          const afterElement = (nextElements || []).find(
            (element) => String(element?.id || "") === targetId && String(element?.type || "") === "stroke"
          );
          if (beforeElement && afterElement && JSON.stringify(beforeElement) !== JSON.stringify(afterElement)) {
            const entry = {
              kind: "stroke_patch",
              elementId: targetId,
              before: cloneElements([beforeElement])[0],
              after: cloneElements([afterElement])[0],
            };
            nextActionLogByUser[actor] = [...(actionLog || []), entry].slice(-HISTORY_LIMIT);
          }
        }
      }
    }
    finalState = {
      ...base,
      version: base.version + 1,
      elements: nextElements,
      taskBlocks: nextTaskBlocks,
      activeTaskBlockId: nextActiveTaskBlockId,
      redoByUser: nextRedoByUser,
      actionLogByUser: nextActionLogByUser,
      redoActionLogByUser: nextRedoActionLogByUser,
    };
  }

  finalState.updatedAt = Date.now();
  finalState.updatedBy = String(userId || base.updatedBy || "");
  return finalState;
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("file_read_failed"));
    reader.readAsDataURL(file);
  });
}

function getImageSize(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth || img.width, height: img.naturalHeight || img.height });
    img.onerror = () => reject(new Error("image_load_failed"));
    img.src = src;
  });
}

function loadImageFromSrc(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image_load_failed"));
    img.src = src;
  });
}

function estimateDataUrlBytes(src) {
  const value = String(src || "");
  const comma = value.indexOf(",");
  if (comma < 0) return value.length;
  const base64 = value.slice(comma + 1);
  return Math.floor((base64.length * 3) / 4);
}

async function optimizeImageDataUrl(file) {
  const originalSrc = await readFileAsDataUrl(file);
  const originalSize = await getImageSize(originalSrc);
  const originalBytes = estimateDataUrlBytes(originalSrc);
  const longestEdge = Math.max(1, Number(originalSize.width || 1), Number(originalSize.height || 1));
  const downscaleRatio = Math.min(1, MAX_IMAGE_LONG_EDGE / longestEdge);
  const shouldDownscale = downscaleRatio < 0.999;

  if (!shouldDownscale && originalBytes <= IMAGE_OPTIMIZE_THRESHOLD_BYTES) {
    return {
      src: originalSrc,
      size: originalSize,
      optimized: false,
    };
  }

  const image = await loadImageFromSrc(originalSrc);
  const targetWidth = Math.max(1, Math.round(Number(originalSize.width || image.naturalWidth || 1) * downscaleRatio));
  const targetHeight = Math.max(1, Math.round(Number(originalSize.height || image.naturalHeight || 1) * downscaleRatio));
  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext("2d", { alpha: true });
  if (!ctx) {
    return {
      src: originalSrc,
      size: originalSize,
      optimized: false,
    };
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.clearRect(0, 0, targetWidth, targetHeight);
  ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

  const webp = canvas.toDataURL("image/webp", IMAGE_COMPRESSION_QUALITY);
  const jpeg = canvas.toDataURL("image/jpeg", IMAGE_COMPRESSION_QUALITY);
  const bestCandidate = [webp, jpeg].sort((a, b) => estimateDataUrlBytes(a) - estimateDataUrlBytes(b))[0];
  const bestBytes = estimateDataUrlBytes(bestCandidate);

  if (!shouldDownscale && bestBytes >= originalBytes) {
    return {
      src: originalSrc,
      size: originalSize,
      optimized: false,
    };
  }

  return {
    src: bestCandidate,
    size: { width: targetWidth, height: targetHeight },
    optimized: true,
  };
}

function getBoardSurfaceStorageKey(themeMode) {
  return `whiteboard.boardSurface.${themeMode === "light" ? "light" : "dark"}`;
}

function getGridEnabledStorageKey() {
  return "whiteboard.gridEnabled";
}

function getVoiceWidgetEnabledStorageKey() {
  return "whiteboard.voiceWidgetEnabled";
}

function isEditableTarget(target) {
  const tagName = String(target?.tagName || "").toLowerCase();
  return tagName === "input" || tagName === "textarea" || tagName === "select" || !!target?.isContentEditable;
}

function elementStableId(element, index) {
  if (element && typeof element === "object" && element.id) return String(element.id);
  return `${String(element?.type || "el")}_${index}`;
}

function sameElementSequence(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (elementStableId(a[i], i) !== elementStableId(b[i], i)) return false;
  }
  return true;
}

function buildDeltaPayload(baseState, finalState, options = {}) {
  const baseElements = Array.isArray(baseState?.elements) ? baseState.elements : [];
  const finalElements = Array.isArray(finalState?.elements) ? finalState.elements : [];
  const baseIds = new Set(baseElements.map((el) => String(el?.id || "")).filter(Boolean));
  const finalIds = new Set(finalElements.map((el) => String(el?.id || "")).filter(Boolean));

  if (options?.kind === "draw") {
    if (finalElements.length === baseElements.length + 1 && sameElementSequence(baseElements, finalElements.slice(0, -1))) {
      return {
        type: "append_elements",
        elements: [compactElementForWire(finalElements[finalElements.length - 1])],
      };
    }
    return null;
  }

  if (options?.kind === "erase") {
    const removedIds = Array.isArray(options?.removedIds)
      ? options.removedIds.map((id) => String(id)).filter(Boolean)
      : [];
    if (removedIds.length > 0) {
      return {
        type: "remove_elements_by_id",
        ids: removedIds,
      };
    }
    return null;
  }

  if (options?.kind === "clear") {
    return { type: "clear_elements" };
  }

  if (options?.kind === "selection_move") {
    const ids = Array.isArray(options?.movedIds)
      ? options.movedIds.map((id) => String(id || "").trim()).filter(Boolean)
      : [];
    const dx = Number(options?.dx || 0);
    const dy = Number(options?.dy || 0);
    if (!ids.length || (!dx && !dy)) return null;
    return {
      type: "move_elements_by_id",
      ids,
      dx,
      dy,
    };
  }

  if (options?.kind === "selection_delete") {
    const removedIds = Array.isArray(options?.removedIds)
      ? options.removedIds.map((id) => String(id || "").trim()).filter(Boolean)
      : [];
    if (!removedIds.length) return null;
    return {
      type: "remove_elements_by_id",
      ids: removedIds,
    };
  }

  if (options?.kind === "image_delete") {
    const removedIds = [...baseIds].filter((id) => id && !finalIds.has(id));
    if (removedIds.length === 1) {
      return { type: "remove_elements_by_id", ids: removedIds };
    }
    return null;
  }

  if (options?.kind === "image_transform" || options?.kind === "image_crop" || options?.kind === "image_rotate") {
    const baseImageMap = new Map(
      baseElements
        .filter((el) => String(el?.type || "") === "image")
        .map((el) => [String(el?.id || ""), el])
        .filter(([id]) => !!id)
    );
    const finalImageMap = new Map(
      finalElements
        .filter((el) => String(el?.type || "") === "image")
        .map((el) => [String(el?.id || ""), el])
        .filter(([id]) => !!id)
    );

    const changedId = Array.from(finalImageMap.keys()).find((id) => {
      if (!baseImageMap.has(id)) return false;
      return JSON.stringify(baseImageMap.get(id)) !== JSON.stringify(finalImageMap.get(id));
    });

    if (changedId) {
      const before = baseImageMap.get(changedId) || {};
      const after = finalImageMap.get(changedId) || {};
      const patch = {};

      for (const key of ["x", "y", "width", "height", "rotation", "naturalWidth", "naturalHeight"]) {
        const beforeVal = Number(before?.[key]);
        const afterVal = Number(after?.[key]);
        if (Number.isFinite(afterVal) && beforeVal !== afterVal) {
          patch[key] = afterVal;
        }
      }

      const beforeCrop = normalizeCrop(before?.crop);
      const afterCrop = normalizeCrop(after?.crop);
      if (JSON.stringify(beforeCrop) !== JSON.stringify(afterCrop)) {
        patch.crop = afterCrop;
      }

      const beforeSrc = String(before?.src || "");
      const afterSrc = String(after?.src || "");
      if (afterSrc && beforeSrc !== afterSrc) {
        patch.src = afterSrc;
      }

      const beforeGraph = before?.graph && typeof before.graph === "object" ? before.graph : null;
      const afterGraph = after?.graph && typeof after.graph === "object" ? after.graph : null;
      if (JSON.stringify(beforeGraph) !== JSON.stringify(afterGraph)) {
        const graphPatch = cloneGraphConfigForPatch(afterGraph);
        if (graphPatch) patch.graph = graphPatch;
      }

      const beforeTextWidget = cloneTextWidgetForPatch(before?.textWidget);
      const afterTextWidget = cloneTextWidgetForPatch(after?.textWidget);
      if (JSON.stringify(beforeTextWidget) !== JSON.stringify(afterTextWidget) && afterTextWidget) {
        patch.textWidget = afterTextWidget;
      }

      if (Object.keys(patch).length) {
        return {
          type: "patch_image_by_id",
          id: changedId,
          patch,
        };
      }
    }
    return null;
  }

  if (options?.kind === "line_style") {
    const targetId = String(options?.elementId || "").trim();
    if (!targetId) return null;
    const beforeLine = baseElements.find(
      (element) => String(element?.id || "") === targetId && String(element?.type || "") === "stroke"
    );
    const afterLine = finalElements.find(
      (element) => String(element?.id || "") === targetId && String(element?.type || "") === "stroke"
    );
    if (!beforeLine || !afterLine) return null;
    const beforeLineStyle = String(beforeLine?.lineStyle || "solid");
    const afterLineStyle = String(afterLine?.lineStyle || "solid");
    if (beforeLineStyle === afterLineStyle) return null;
    return {
      type: "patch_stroke_by_id",
      id: targetId,
      patch: {
        lineStyle: afterLineStyle,
      },
    };
  }

  if (options?.kind === "stroke_patch") {
    const targetId = String(options?.elementId || "").trim();
    if (!targetId) return null;
    const beforeStroke = baseElements.find(
      (element) => String(element?.id || "") === targetId && String(element?.type || "") === "stroke"
    );
    const afterStroke = finalElements.find(
      (element) => String(element?.id || "") === targetId && String(element?.type || "") === "stroke"
    );
    if (!beforeStroke || !afterStroke) return null;
    const beforePoints = Array.isArray(beforeStroke.points) ? beforeStroke.points : [];
    const afterPoints = Array.isArray(afterStroke.points) ? afterStroke.points : [];
    const patch = {};
    if (JSON.stringify(beforePoints) !== JSON.stringify(afterPoints)) {
      patch.points = afterPoints.map(compactPointForWire);
    }
    if (String(beforeStroke.color || "") !== String(afterStroke.color || "")) {
      patch.color = String(afterStroke.color || "").slice(0, 64);
    }
    if (Number(beforeStroke.width || 3) !== Number(afterStroke.width || 3)) {
      patch.width = Math.max(1, Math.min(48, Number(afterStroke.width || 3)));
    }
    if (!Object.keys(patch).length) return null;
    return {
      type: "patch_stroke_by_id",
      id: targetId,
      patch,
    };
  }

  if (options?.history === "undo") {
    const removedIds = [...baseIds].filter((id) => !finalIds.has(id));
    if (removedIds.length === 1) {
      return { type: "remove_elements_by_id", ids: removedIds };
    }
    return null;
  }

  if (options?.history === "redo") {
    if (finalElements.length === baseElements.length + 1 && sameElementSequence(baseElements, finalElements.slice(0, -1))) {
      return {
        type: "append_elements",
        elements: [compactElementForWire(finalElements[finalElements.length - 1])],
      };
    }
    const addedIds = [...finalIds].filter((id) => id && !baseIds.has(id));
    if (addedIds.length === 1) {
      const addedId = addedIds[0];
      const index = finalElements.findIndex((element) => String(element?.id || "") === addedId);
      if (index >= 0) {
        return {
          type: "insert_element",
          index,
          element: compactElementForWire(finalElements[index]),
        };
      }
    }
    return null;
  }

  const basePageCount = Number(baseState?.pageCount || DEFAULT_PAGE_COUNT);
  const nextPageCount = Number(finalState?.pageCount || DEFAULT_PAGE_COUNT);
  if (basePageCount !== nextPageCount && sameElementSequence(baseElements, finalElements)) {
    return { type: "set_page_count", pageCount: nextPageCount };
  }

  const baseTaskBlocks = Array.isArray(baseState?.taskBlocks) ? baseState.taskBlocks : [];
  const finalTaskBlocks = Array.isArray(finalState?.taskBlocks) ? finalState.taskBlocks : [];
  const baseActiveTaskBlockId = normalizeTaskBlockId(baseState?.activeTaskBlockId);
  const finalActiveTaskBlockId = normalizeTaskBlockId(finalState?.activeTaskBlockId);
  if (
    (JSON.stringify(baseTaskBlocks) !== JSON.stringify(finalTaskBlocks) ||
      baseActiveTaskBlockId !== finalActiveTaskBlockId) &&
    sameElementSequence(baseElements, finalElements) &&
    basePageCount === nextPageCount
  ) {
    return {
      type: "set_task_blocks",
      taskBlocks: finalTaskBlocks,
      activeTaskBlockId: finalActiveTaskBlockId,
    };
  }

  return null;
}

function isAppendElementsDelta(delta) {
  return (
    delta &&
    typeof delta === "object" &&
    String(delta.type || "") === "append_elements" &&
    Array.isArray(delta.elements) &&
    delta.elements.length > 0
  );
}

function isInsertElementDelta(delta) {
  return (
    delta &&
    typeof delta === "object" &&
    String(delta.type || "") === "insert_element" &&
    delta.element &&
    typeof delta.element === "object"
  );
}

function isRebasableElementAddDelta(delta) {
  return isAppendElementsDelta(delta) || isInsertElementDelta(delta);
}

function isTransientPersistError(error) {
  const value = String(error || "").trim();
  return (
    value === "ack_timeout" ||
    value === "timeout" ||
    value === "transport_error" ||
    value === "network_error"
  );
}

function isBoardSizePersistError(error) {
  const value = String(error || "").trim();
  return value === "state_too_large" || value === "delta_too_large";
}

function formatStorageMegabytes(bytes, fallbackBytes = 8 * 1024 * 1024) {
  const value = Number(bytes);
  const safeBytes = Number.isFinite(value) && value > 0 ? value : fallbackBytes;
  return `${Math.max(0.1, safeBytes / (1024 * 1024)).toLocaleString("pl-PL", {
    maximumFractionDigits: 1,
  })} MB`;
}

function getDeltaDebugSummary(delta) {
  const type = String(delta?.type || "");
  const elements = Array.isArray(delta?.elements) ? delta.elements : [];
  const firstStroke = elements.find((element) => String(element?.type || "") === "stroke");
  return {
    type,
    elements: elements.length,
    strokePoints: Array.isArray(firstStroke?.points) ? firstStroke.points.length : 0,
    ids: elements.map((element) => String(element?.id || "")).filter(Boolean).slice(0, 3),
  };
}

function logWhiteboardPersistIssue(details) {
  if (typeof console === "undefined" || typeof console.warn !== "function") return;
  try {
    if (typeof localStorage === "undefined" || localStorage.getItem("whiteboardDebug") !== "1") return;
  } catch {
    return;
  }
  const ua = typeof navigator === "undefined" ? "" : String(navigator.userAgent || "");
  console.warn("[whiteboard:persist]", {
    ...details,
    userAgent: ua,
  });
}

function applyDeltaToBoardState(currentState, delta) {
  const base = normalizeBoardState(currentState);
  const safeDelta = delta && typeof delta === "object" ? delta : null;
  if (!safeDelta) return null;
  const type = String(safeDelta.type || "").trim();

  const next = {
    ...base,
    elements: cloneElements(base.elements),
    pageCount: Math.max(1, Math.min(MAX_PAGE_COUNT, Number(base.pageCount || DEFAULT_PAGE_COUNT))),
    taskBlocks: cloneTaskBlocks(base.taskBlocks),
    activeTaskBlockId: resolveActiveTaskBlockId(base.taskBlocks, base.activeTaskBlockId),
    redoByUser: base.redoByUser && typeof base.redoByUser === "object" ? JSON.parse(JSON.stringify(base.redoByUser)) : {},
  };

  if (type === "append_elements") {
    const append = Array.isArray(safeDelta.elements)
      ? cloneElements(safeDelta.elements.map(normalizeWireElementForState))
      : [];
    if (!append.length) return null;
    const existingIds = new Set(
      (Array.isArray(next.elements) ? next.elements : [])
        .map((element) => String(element?.id || "").trim())
        .filter(Boolean)
    );
    const appendFiltered = append.filter((element) => {
      const id = String(element?.id || "").trim();
      if (!id) return true;
      if (existingIds.has(id)) return false;
      existingIds.add(id);
      return true;
    });
    if (!appendFiltered.length) return null;
    next.elements = next.elements.concat(appendFiltered).slice(0, 5000);
    return next;
  }

  if (type === "insert_element") {
    const element = safeDelta.element && typeof safeDelta.element === "object"
      ? cloneElements([normalizeWireElementForState(safeDelta.element)])[0]
      : null;
    if (!element) return null;
    const elementId = String(element?.id || "").trim();
    if (elementId && next.elements.some((entry) => String(entry?.id || "").trim() === elementId)) {
      return null;
    }
    const rawIndex = Number(safeDelta.index);
    const insertIndex = Number.isFinite(rawIndex)
      ? Math.max(0, Math.min(next.elements.length, Math.round(rawIndex)))
      : next.elements.length;
    next.elements = [
      ...next.elements.slice(0, insertIndex),
      element,
      ...next.elements.slice(insertIndex),
    ].slice(0, 5000);
    return next;
  }

  if (type === "remove_elements_by_id") {
    const ids = new Set(
      (Array.isArray(safeDelta.ids) ? safeDelta.ids : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    );
    if (!ids.size) return null;
    next.elements = next.elements.filter((element, index) => {
      const elementId = String(element?.id || "").trim();
      const fallbackImageId = `image_${index}`;
      const fallbackStrokeId = `stroke_${index}`;
      if (elementId && ids.has(elementId)) return false;
      if (String(element?.type || "") === "image") {
        if (ids.has(`image:${elementId}`) || ids.has(`image:${fallbackImageId}`) || ids.has(fallbackImageId)) {
          return false;
        }
      }
      if (String(element?.type || "") === "stroke") {
        if (ids.has(`stroke:${elementId}`) || ids.has(`stroke:${fallbackStrokeId}`) || ids.has(fallbackStrokeId)) {
          return false;
        }
      }
      return true;
    });
    return next;
  }

  if (type === "clear_elements") {
    next.elements = [];
    return next;
  }

  if (type === "set_page_count") {
    next.pageCount = Math.max(1, Math.min(MAX_PAGE_COUNT, Number(safeDelta.pageCount || next.pageCount || DEFAULT_PAGE_COUNT)));
    return next;
  }

  if (type === "set_task_blocks") {
    next.taskBlocks = Array.isArray(safeDelta.taskBlocks) ? cloneTaskBlocks(safeDelta.taskBlocks).slice(0, MAX_TASK_BLOCKS) : [];
    next.activeTaskBlockId = resolveActiveTaskBlockId(next.taskBlocks, safeDelta.activeTaskBlockId);
    return next;
  }

  if (type === "patch_image_by_id") {
    const id = String(safeDelta.id || "").trim();
    const patch = safeDelta.patch && typeof safeDelta.patch === "object" ? safeDelta.patch : null;
    if (!id || !patch) return null;

    let changed = false;
    next.elements = next.elements.map((element) => {
      if (String(element?.type || "") !== "image") return element;
      if (String(element?.id || "") !== id) return element;

      const updated = { ...element };
      for (const key of ["x", "y", "width", "height", "rotation", "naturalWidth", "naturalHeight"]) {
        if (patch[key] === undefined) continue;
        const numeric = Number(patch[key]);
        if (!Number.isFinite(numeric)) continue;
        if (Number(updated[key]) !== numeric) {
          updated[key] = numeric;
          changed = true;
        }
      }

      if (patch.crop && typeof patch.crop === "object") {
        const normalized = normalizeCrop(patch.crop);
        if (JSON.stringify(normalizeCrop(updated.crop)) !== JSON.stringify(normalized)) {
          updated.crop = normalized;
          changed = true;
        }
      }

      if (typeof patch.src === "string" && patch.src && String(updated.src || "") !== patch.src) {
        updated.src = patch.src;
        changed = true;
      }

      if (patch.graph && typeof patch.graph === "object") {
        const graphPatch = cloneGraphConfigForPatch(patch.graph);
        if (graphPatch && JSON.stringify(updated.graph || null) !== JSON.stringify(graphPatch)) {
          updated.graph = graphPatch;
          changed = true;
        }
      }

      if (patch.textWidget && typeof patch.textWidget === "object") {
        const textWidgetPatch = cloneTextWidgetForPatch(patch.textWidget);
        if (textWidgetPatch && JSON.stringify(updated.textWidget || null) !== JSON.stringify(textWidgetPatch)) {
          updated.textWidget = textWidgetPatch;
          changed = true;
        }
      }

      return updated;
    });

    return changed ? next : null;
  }

  if (type === "patch_stroke_by_id") {
    const id = String(safeDelta.id || "").trim();
    const patch = safeDelta.patch && typeof safeDelta.patch === "object" ? safeDelta.patch : null;
    if (!id || !patch) return null;

    let changed = false;
    next.elements = next.elements.map((element) => {
      if (String(element?.type || "") !== "stroke") return element;
      if (String(element?.id || "") !== id) return element;
      const updated = { ...element };
      if (patch.lineStyle !== undefined) {
        const normalizedLineStyle = String(patch.lineStyle || "") === "dashed" ? "dashed" : "solid";
        const currentLineStyle = String(updated?.lineStyle || "solid");
        if (currentLineStyle !== normalizedLineStyle) {
          updated.lineStyle = normalizedLineStyle;
          changed = true;
        }
      }
      if (Array.isArray(patch.points)) {
        const normalizedPoints = patch.points.map(normalizeWirePointForState);
        if (JSON.stringify(Array.isArray(updated.points) ? updated.points : []) !== JSON.stringify(normalizedPoints)) {
          updated.points = normalizedPoints;
          changed = true;
        }
      }
      if (typeof patch.color === "string" && String(updated.color || "") !== patch.color) {
        updated.color = patch.color.slice(0, 64);
        changed = true;
      }
      if (patch.width !== undefined) {
        const width = Math.max(1, Math.min(48, Number(patch.width || 3)));
        if (Number(updated.width || 3) !== width) {
          updated.width = width;
          changed = true;
        }
      }
      return updated;
    });
    return changed ? next : null;
  }

  if (type === "move_elements_by_id") {
    const ids = new Set(
      (Array.isArray(safeDelta.ids) ? safeDelta.ids : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    );
    const dx = Number(safeDelta.dx || 0);
    const dy = Number(safeDelta.dy || 0);
    if (!ids.size || (!dx && !dy)) return null;

    next.elements = next.elements.map((element, index) => {
      const elementId = String(element?.id || "");
      const fallbackImageId = `image_${index}`;
      const fallbackStrokeId = `stroke_${index}`;
      const targetByDirectId = elementId && ids.has(elementId);
      const targetByPrefixedImage = ids.has(`image:${elementId}`) || ids.has(`image:${fallbackImageId}`);
      const targetByPrefixedStroke = ids.has(`stroke:${elementId}`) || ids.has(`stroke:${fallbackStrokeId}`);
      const isTarget = targetByDirectId || targetByPrefixedImage || targetByPrefixedStroke;
      if (!isTarget) return element;

      if (element?.type === "image") {
        return {
          ...element,
          x: Number(element?.x || 0) + dx,
          y: Number(element?.y || 0) + dy,
        };
      }
      if (element?.type === "stroke") {
        const points = Array.isArray(element.points) ? element.points : [];
        return {
          ...element,
          points: points.map((point) => ({
            ...point,
            x: Number(point?.x || 0) + dx,
            y: Number(point?.y || 0) + dy,
          })),
        };
      }
      return element;
    });
    return next;
  }

  return null;
}

function makeTaskBlockId() {
  return `task_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function makeClientId() {
  return `wb_client_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function makeOperationId(counter) {
  return `op_${Date.now().toString(36)}_${Number(counter || 0).toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, Math.max(0, Number(ms) || 0)));
}

function withTimeout(promise, timeoutMs, message) {
  let timer = null;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(message || "timeout")), Math.max(1000, Number(timeoutMs) || 1000));
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}

function getElementPageRange(element, pageCount = DEFAULT_PAGE_COUNT) {
  const maxPage = Math.max(0, Number(pageCount || DEFAULT_PAGE_COUNT) - 1);
  if (!element || typeof element !== "object") return { start: 0, end: 0 };

  if (String(element.type || "") === "image") {
    const y = Number(element.y || 0);
    const h = Math.max(1, Number(element.height || 1));
    const start = Math.max(0, Math.min(maxPage, Math.floor(y / PAGE_HEIGHT)));
    const end = Math.max(start, Math.min(maxPage, Math.floor((y + h) / PAGE_HEIGHT)));
    return { start, end };
  }

  if (String(element.type || "") === "stroke") {
    const points = Array.isArray(element.points) ? element.points : [];
    if (!points.length) return { start: 0, end: 0 };
    let minY = Number(points[0]?.y || 0);
    let maxY = Number(points[0]?.y || 0);
    for (let i = 1; i < points.length; i += 1) {
      const y = Number(points[i]?.y || 0);
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    const start = Math.max(0, Math.min(maxPage, Math.floor(minY / PAGE_HEIGHT)));
    const end = Math.max(start, Math.min(maxPage, Math.floor(maxY / PAGE_HEIGHT)));
    return { start, end };
  }

  return { start: 0, end: maxPage };
}

function filterElementsForPageWindow(elements, pageStart, pageEnd, pageCount, paddingBefore = 0, paddingAfter = 0) {
  const list = Array.isArray(elements) ? elements : [];
  const maxPage = Math.max(0, Number(pageCount || DEFAULT_PAGE_COUNT) - 1);
  const start = Math.max(0, Math.min(maxPage, Number(pageStart || 0) - Math.max(0, Number(paddingBefore || 0))));
  const end = Math.max(start, Math.min(maxPage, Number(pageEnd || start) + Math.max(0, Number(paddingAfter || 0))));
  return list.filter((element) => {
    const range = getElementPageRange(element, pageCount);
    return range.end >= start && range.start <= end;
  });
}

function shouldUsePageWindowMode(boardState) {
  if (!PAGE_WINDOW_MODE_ENABLED) return false;
  const pageCount = Math.max(1, Number(boardState?.pageCount || DEFAULT_PAGE_COUNT));
  const elementsCount = Array.isArray(boardState?.elements) ? boardState.elements.length : 0;
  const deviceMemory = typeof navigator === "undefined" ? 0 : Number(navigator.deviceMemory || 0);
  const weakDevice = Number.isFinite(deviceMemory) && deviceMemory > 0 && deviceMemory <= 4;
  if (pageCount >= PAGE_WINDOW_TRIGGER_PAGE_COUNT || elementsCount >= PAGE_WINDOW_TRIGGER_ELEMENT_COUNT) return true;
  if (weakDevice && (pageCount >= PAGE_WINDOW_WEAK_DEVICE_PAGE_COUNT || elementsCount >= PAGE_WINDOW_WEAK_DEVICE_ELEMENT_COUNT)) {
    return true;
  }
  return false;
}

function pickTaskByChoice(generatedTasks, choice) {
  const tasks = Array.isArray(generatedTasks) ? generatedTasks : [];
  if (!tasks.length) return null;
  const preferredIndex = Number(choice?.index);
  const preferredType = String(choice?.type || "");
  if (Number.isInteger(preferredIndex) && tasks[preferredIndex] && String(tasks[preferredIndex]?.type || "") === preferredType) {
    return tasks[preferredIndex];
  }
  if (preferredType) {
    const byType = tasks.find((task) => String(task?.type || "") === preferredType);
    if (byType) return byType;
  }
  return tasks[0];
}

function resolveTaskGenerationMode(adapter, requestedValue) {
  const modes = normalizeTaskGenerationModes(adapter);
  const requested = String(requestedValue || "");
  return (
    modes.find((mode) => mode.value === requested) ||
    modes.find((mode) => mode.value === "all_types") ||
    modes.find((mode) => mode.value === String(adapter?.defaultGenerationMode || "")) ||
    modes[0] ||
    null
  );
}

function generateTasksForChoice(adapter, choice = {}) {
  if (typeof adapter?.generateTasks !== "function") return [];
  const storedOptions =
    choice?.generationOptions && typeof choice.generationOptions === "object"
      ? choice.generationOptions
      : null;
  const mode = resolveTaskGenerationMode(adapter, choice?.generationMode);
  const options = storedOptions || mode?.generationOptions || null;
  return options ? adapter.generateTasks(options) : adapter.generateTasks();
}

function SettingsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M10.7 3h2.6l.4 2.1a7 7 0 0 1 1.7.7l1.9-1.1 1.8 1.8-1.1 1.9c.3.5.6 1.1.7 1.7l2.1.4v2.6l-2.1.4a7 7 0 0 1-.7 1.7l1.1 1.9-1.8 1.8-1.9-1.1a7 7 0 0 1-1.7.7L13.3 21h-2.6l-.4-2.1a7 7 0 0 1-1.7-.7l-1.9 1.1-1.8-1.8 1.1-1.9a7 7 0 0 1-.7-1.7L3 13.3v-2.6l2.1-.4a7 7 0 0 1 .7-1.7L4.7 6.7l1.8-1.8 1.9 1.1a7 7 0 0 1 1.7-.7z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3.1" fill="none" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 7h16M9 7V5h6v2M8 7l.7 12h6.6L16 7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10.5 11v5M13.5 11v5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function CalculatorIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="3" width="16" height="18" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <rect x="7" y="6" width="10" height="3" rx="1" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="9" cy="12" r="1" fill="currentColor" />
      <circle cx="12" cy="12" r="1" fill="currentColor" />
      <circle cx="15" cy="12" r="1" fill="currentColor" />
      <circle cx="9" cy="15.5" r="1" fill="currentColor" />
      <circle cx="12" cy="15.5" r="1" fill="currentColor" />
      <circle cx="15" cy="15.5" r="1" fill="currentColor" />
    </svg>
  );
}

function FormulaIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="5" y="3.5" width="14" height="17" rx="2.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 8h8M8 11.5h8M8 15h6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function VoiceIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 4a3 3 0 0 0-3 3v5a3 3 0 0 0 6 0V7a3 3 0 0 0-3-3Z" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3M8.5 21h7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="12" cy="8.2" r="1.1" fill="currentColor" />
      <path d="M12 11v6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function ShuffleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 7h4l4 4 4-4h4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18 3l2 4-4 2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 17h4l4-4 4 4h4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18 21l2-4-4-2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronUpIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 7.5L19 16.5H5z" fill="currentColor" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 16.5L5 7.5h14z" fill="currentColor" />
    </svg>
  );
}

function ToolsMenuIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 7h14M5 12h14M5 17h14" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
      <circle cx="9" cy="7" r="1.7" fill="currentColor" />
      <circle cx="15" cy="12" r="1.7" fill="currentColor" />
      <circle cx="11" cy="17" r="1.7" fill="currentColor" />
    </svg>
  );
}

function DuplicateIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="9" y="9" width="10" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <rect x="5" y="5" width="10" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}


export default function WhiteboardRoomPage({ demoMode = false }) {
  const { code } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const socket = useMemo(() => (demoMode ? DEMO_SOCKET : getWhiteboardSocket()), [demoMode]);
  const { authorizedFetch, voiceChannelMode } = useContext(AuthContext);
  const { theme } = useContext(ThemeContext) || {};
  const themeMode = theme === "light" ? "light" : "dark";

  const roomCode = String(demoMode ? DEMO_ROOM_CODE : code || "").trim().toUpperCase();

  const [me, setMe] = useState(() => (
    demoMode
      ? { _id: "demo-user", name: "Gosc demo", displayName: "Gosc demo", role: "student" }
      : null
  ));
  const [localBoardState, setLocalBoardState] = useState(() =>
    demoMode
      ? readDemoBoardState()
      : normalizeBoardState({ version: 1, elements: [], pageCount: DEFAULT_PAGE_COUNT })
  );
  const [room, setRoom] = useState(() => (
    demoMode ? createDemoRoom(roomCode, readDemoBoardState()) : null
  ));
  const [tool, setTool] = useState("select");
  const [zoomPercent, setZoomPercent] = useState(100);
  const [penColor, setPenColor] = useState(CURSOR_COLORS[0]);
  const [highlighterColor, setHighlighterColor] = useState(HIGHLIGHTER_COLORS[0]);
  const [hasCustomColor, setHasCustomColor] = useState(false);
  const [lastCustomColor, setLastCustomColor] = useState("");
  const [brushSize, setBrushSize] = useState(5);
  const [highlighterSize, setHighlighterSize] = useState(14);
  const [lastBrushTool, setLastBrushTool] = useState("pen");
  const [lastShapeTool, setLastShapeTool] = useState("line");
  const [brushGroupOpen, setBrushGroupOpen] = useState(false);
  const [shapeGroupOpen, setShapeGroupOpen] = useState(false);
  const [quickToolConfigTool, setQuickToolConfigTool] = useState("");
  const [err, setErr] = useState("");
  const [archiveReadOnly, setArchiveReadOnly] = useState(false);
  const [archiveReadOnlyAt, setArchiveReadOnlyAt] = useState(null);
  const [boardGroup, setBoardGroup] = useState(null);
  const [isRibbonOpen, setIsRibbonOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);
  const [connectionBannerState, setConnectionBannerState] = useState(() => {
    if (demoMode) return null;
    if (typeof navigator === "undefined") return null;
    if (!navigator.onLine) return "offline";
    return socket.connected ? null : "reconnecting";
  });
  const [hardSyncActive, setHardSyncActive] = useState(false);
  const [storageLimitError, setStorageLimitError] = useState(null);
  const [storageUsageWarning, setStorageUsageWarning] = useState(null);
  const [remoteDraftsByUser, setRemoteDraftsByUser] = useState({});
  const [remoteCursorsByUser, setRemoteCursorsByUser] = useState({});
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [taskDeleteOpen, setTaskDeleteOpen] = useState(false);
  const [taskDeleteTargetId, setTaskDeleteTargetId] = useState("");
  const [taskCollapsePrefs, setTaskCollapsePrefs] = useState(() => readTaskCollapsePrefs(roomCode));
  const [taskRerollOpen, setTaskRerollOpen] = useState(false);
  const [taskRerollTargetId, setTaskRerollTargetId] = useState("");
  const [taskDuplicateOpen, setTaskDuplicateOpen] = useState(false);
  const [taskDuplicateTargetId, setTaskDuplicateTargetId] = useState("");
  const [taskLimitOpen, setTaskLimitOpen] = useState(false);
  const [taskLimitContext, setTaskLimitContext] = useState("add");
  const [currentCanvasPage, setCurrentCanvasPage] = useState(0);
  const [windowHeight, setWindowHeight] = useState(() => (typeof window !== "undefined" ? window.innerHeight : 900));
  const [boardSurface, setBoardSurface] = useState(() => {
    if (typeof window === "undefined") return "default";
    const stored = window.localStorage.getItem(getBoardSurfaceStorageKey(themeMode));
    return BOARD_SURFACE_OPTIONS.has(stored) ? stored : "default";
  });
  const [gridEnabled, setGridEnabled] = useState(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem(getGridEnabledStorageKey());
    return stored === null ? true : stored === "1";
  });
  const [voiceWidgetEnabled, setVoiceWidgetEnabled] = useState(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem(getVoiceWidgetEnabledStorageKey());
    return stored === null ? true : stored === "1";
  });
  const [taskPickerOpen, setTaskPickerOpen] = useState(false);
  const [taskPickerBusy, setTaskPickerBusy] = useState(false);
  const [taskSectionIndex, setTaskSectionIndex] = useState(0);
  const [taskTopicIndex, setTaskTopicIndex] = useState(0);
  const [taskSubtopicId, setTaskSubtopicId] = useState("");
  const [taskChoices, setTaskChoices] = useState([]);
  const [taskChoiceKey, setTaskChoiceKey] = useState("");
  const [taskGenerationMode, setTaskGenerationMode] = useState("");
  const [taskChoicesLoading, setTaskChoicesLoading] = useState(false);
  const [taskAdapters, setTaskAdapters] = useState({});
  const [taskAdapterStatus, setTaskAdapterStatus] = useState({});
  const [activeTaskScrollable, setActiveTaskScrollable] = useState(false);
  const [taskDockHidden, setTaskDockHidden] = useState(() => readTaskDockHidden(roomCode));
  const [graphPickerOpen, setGraphPickerOpen] = useState(false);
  const [graphInputs, setGraphInputs] = useState(["f(x)=x^2-4"]);
  const [graphRangeFields, setGraphRangeFields] = useState({ xmin: -10, xmax: 10, ymin: -10, ymax: 10 });
  const [graphPoints, setGraphPoints] = useState([]);
  const [graphSegments, setGraphSegments] = useState([]);
  const [graphThemeChoice, setGraphThemeChoice] = useState(() => resolveDefaultGraphTheme("dark", "default"));
  const [graphEditTargetId, setGraphEditTargetId] = useState("");
  const [graphDeleteTarget, setGraphDeleteTarget] = useState(null);
  const [textPickerOpen, setTextPickerOpen] = useState(false);
  const [textInput, setTextInput] = useState("");
  const [textEditTargetId, setTextEditTargetId] = useState("");
  const [controlsSide, setControlsSide] = useState("right");
  const [sideToolsExpanded, setSideToolsExpanded] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [stylusOnly, setStylusOnly] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(STYLUS_ONLY_STORAGE_KEY) === "1";
  });
  const [holdToolSwitchEnabled, setHoldToolSwitchEnabled] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem(HOLD_TOOL_SWITCH_STORAGE_KEY) !== "0";
  });
  const [stylusCursorEnabled, setStylusCursorEnabled] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem(STYLUS_CURSOR_STORAGE_KEY) !== "0";
  });
  const [demoTutorialOpen, setDemoTutorialOpen] = useState(() => {
    if (!demoMode) return false;
    const params = new URLSearchParams(location.search || "");
    return params.get("tutorial") === "1";
  });
  const [demoTutorialStep, setDemoTutorialStep] = useState(0);

  useEffect(() => {
    if (demoMode || !authorizedFetch || !roomCode) {
      setBoardGroup(null);
      return;
    }
    let active = true;
    authorizedFetch(`/api/whiteboards/${encodeURIComponent(roomCode)}/group`)
      .then(async (response) => {
        const data = await response.json().catch(() => null);
        if (active) setBoardGroup(response.ok ? data?.group || null : null);
      })
      .catch(() => {
        if (active) setBoardGroup(null);
      });
    return () => {
      active = false;
    };
  }, [authorizedFetch, demoMode, roomCode]);

  useEffect(() => {
    if (!demoMode) return;
    const params = new URLSearchParams(location.search || "");
    const shouldOpen = params.get("tutorial") === "1";
    setDemoTutorialOpen(shouldOpen);
    if (shouldOpen) setDemoTutorialStep(0);
  }, [demoMode, location.search]);

  useEffect(() => {
    if (!demoMode || !demoTutorialOpen) return;
    const step = DEMO_TUTORIAL_STEPS[demoTutorialStep];
    if (typeof step?.expandedTools === "boolean") {
      setSideToolsExpanded(step.expandedTools);
    }
    if (step?.target === "brush-tools") {
      setBrushGroupOpen(true);
      setShapeGroupOpen(false);
      setTool((current) => (BRUSH_TOOLS.includes(current) ? current : "pen"));
    } else if (step?.target === "shape-tools") {
      setBrushGroupOpen(false);
      setShapeGroupOpen(true);
      setTool((current) => (SHAPE_TOOLS.includes(current) ? current : "line"));
    }
    if (step?.action === "task") {
      setGraphPickerOpen(false);
      setTaskDockHidden(false);
      writeTaskDockHidden(roomCode, false);
      const demoTaskSubtopicId = DEMO_TASK_SUBTOPIC_IDS[1];
      const demoTaskLocation = findSectionTopicForSubtopic(demoTaskSubtopicId);
      if (demoTaskLocation) {
        setTaskSectionIndex(demoTaskLocation.sectionIndex);
        setTaskTopicIndex(demoTaskLocation.topicIndex);
        setTaskSubtopicId(demoTaskSubtopicId);
      }
      setTaskPickerOpen(true);
      return;
    }
    if (step?.action === "graph") {
      setTaskPickerOpen(false);
      openGraphCreator();
      return;
    }
    setTaskPickerOpen(false);
    setGraphPickerOpen(false);
  }, [demoMode, demoTutorialOpen, demoTutorialStep]);
  const [prefsReady, setPrefsReady] = useState(false);
  const isApplyingRemoteRef = useRef(false);
  const fullscreenRef = useRef(null);
  const imageInputRef = useRef(null);
  const latestBoardStateRef = useRef(localBoardState);
  const draftThrottleRef = useRef({ lastSentAt: 0, queued: null, timer: null });
  const cursorThrottleRef = useRef({ lastSentAt: 0, queued: null, timer: null, hideTimer: null });
  const remoteDraftClearTimersRef = useRef(new Map());
  const connectionBannerTimerRef = useRef(null);
  const connectionBannerStateRef = useRef(connectionBannerState);
  const activeRoomCodeRef = useRef(roomCode);
  const taskAdapterCacheRef = useRef({});
  const taskAdapterLoadsRef = useRef({});
  const activeTaskBodyRef = useRef(null);
  const graphInputRefs = useRef({});
  const resizeRafRef = useRef(null);
  const prefsSaveTimerRef = useRef(null);
  const persistInFlightRef = useRef(false);
  const persistQueueRef = useRef([]);
  const persistFlushTimerRef = useRef(null);
  const persistLatencyEmaRef = useRef(90);
  const lastPersistErrorRef = useRef("");
  const operationCounterRef = useRef(0);
  const gapSyncInFlightRef = useRef(false);
  const gapSyncLastAtRef = useRef(0);
  const gapSyncTimerRef = useRef(null);
  const recoveryPullInFlightRef = useRef(false);
  const recoveryPullLastAtRef = useRef(0);
  const pageWindowModeRef = useRef(false);
  const pageWindowFetchInFlightRef = useRef(false);
  const pageWindowLastKeyRef = useRef("");
  const pageWindowRequestSeqRef = useRef(0);
  const currentCanvasPageRef = useRef(0);
  const consistencyAuditTimerRef = useRef(null);
  const consistencyAuditInFlightRef = useRef(false);
  const consistencyAuditLastAtRef = useRef(0);
  const lastLocalCursorPointRef = useRef(null);
  const clientIdRef = useRef(
    typeof window === "undefined"
      ? makeClientId()
      : (() => {
          const key = "whiteboard.clientId.v1";
          const existing = window.localStorage.getItem(key);
          if (existing) return existing;
          const next = makeClientId();
          window.localStorage.setItem(key, next);
          return next;
        })()
  );
  const lastLocalEditAtRef = useRef(0);
  const lastRemoteDeltaAtRef = useRef(0);
  const recoveryNeededRef = useRef(false);
  const latestRoomRef = useRef(null);
  activeRoomCodeRef.current = roomCode;
  const isTouchDevice = useMemo(() => {
    if (typeof window === "undefined" || typeof navigator === "undefined") return false;
    return !!(
      navigator.maxTouchPoints > 0 ||
      "ontouchstart" in window ||
      window.matchMedia?.("(pointer: coarse)")?.matches
    );
  }, []);
  const isAdminUser = useMemo(() => {
    const role = String(me?.role || me?.accountType || "").toLowerCase();
    return role === "admin" || role === "administrator" || me?.isAdmin === true;
  }, [me]);
  const roomHostIsAdmin = useMemo(() => {
    const explicitHostRole = String(room?.hostRole || room?.ownerRole || "").toLowerCase();
    if (explicitHostRole === "admin" || explicitHostRole === "administrator" || explicitHostRole === "superadmin") {
      return true;
    }
    const hostUserId = String(room?.hostUserId || "");
    if (!hostUserId) return false;
    const hostParticipant = (Array.isArray(room?.participants) ? room.participants : []).find(
      (participant) => String(participant?.userId || "") === hostUserId
    );
    const inferredHostRole = String(hostParticipant?.role || "").toLowerCase();
    return inferredHostRole === "admin" || inferredHostRole === "administrator" || inferredHostRole === "superadmin";
  }, [room?.hostRole, room?.hostUserId, room?.ownerRole, room?.participants]);
  const normalizedVoiceChannelMode = ["off", "admin", "all"].includes(voiceChannelMode) ? voiceChannelMode : "admin";
  const voiceChannelAvailable =
    !demoMode &&
    !archiveReadOnly &&
    (normalizedVoiceChannelMode === "all" ||
      (normalizedVoiceChannelMode === "admin" && (isAdminUser || roomHostIsAdmin)));
  const showStylusToggle = isTouchDevice || isAdminUser;

  useEffect(() => {
    if (BRUSH_TOOLS.includes(tool)) setLastBrushTool(tool);
    if (SHAPE_TOOLS.includes(tool)) setLastShapeTool(tool);
    setBrushGroupOpen(false);
    setShapeGroupOpen(false);
  }, [tool]);

  useEffect(() => {
    latestBoardStateRef.current = localBoardState;
  }, [localBoardState]);

  useEffect(() => {
    currentCanvasPageRef.current = Number(currentCanvasPage || 0);
  }, [currentCanvasPage]);

  useEffect(() => {
    latestRoomRef.current = room;
  }, [room]);

  useEffect(() => {
    connectionBannerStateRef.current = connectionBannerState;
  }, [connectionBannerState]);

  useEffect(() => {
    // React może zachować ten sam komponent przy przejściu /tablica/A -> /tablica/B.
    // Żadna kolejka ani blokada synchronizacji starego pokoju nie może wtedy przejść do nowego.
    activeRoomCodeRef.current = roomCode;
    clearPersistFlushTimer();
    persistQueueRef.current = [];
    lastPersistErrorRef.current = "";
    recoveryNeededRef.current = false;
    recoveryPullLastAtRef.current = 0;
    setHardSyncActive(false);
    setStorageLimitError(null);
    setStorageUsageWarning(null);
    setErr("");
    clearConnectionBannerTimer();
    if (demoMode) {
      setConnectionBannerState(null);
    } else if (typeof navigator !== "undefined" && !navigator.onLine) {
      setConnectionBannerState("offline");
    } else {
      setConnectionBannerState(socket.connected ? null : "reconnecting");
    }
  }, [demoMode, roomCode, socket]);

  function clearRemoteDraftTimer(userId) {
    const timers = remoteDraftClearTimersRef.current;
    const key = String(userId || "");
    if (!key) return;
    const t = timers.get(key);
    if (t) clearTimeout(t);
    timers.delete(key);
  }

  function clearAllRemoteDraftTimers() {
    const timers = remoteDraftClearTimersRef.current;
    timers.forEach((t) => clearTimeout(t));
    timers.clear();
  }

  function clearConnectionBannerTimer() {
    if (!connectionBannerTimerRef.current) return;
    clearTimeout(connectionBannerTimerRef.current);
    connectionBannerTimerRef.current = null;
  }

  function clearPersistFlushTimer() {
    if (!persistFlushTimerRef.current) return;
    clearTimeout(persistFlushTimerRef.current);
    persistFlushTimerRef.current = null;
  }

  function estimatePersistBatchDelay() {
    const firstJob = Array.isArray(persistQueueRef.current) ? persistQueueRef.current[0] : null;
    if (firstJob?.opMeta?.kind === "draw" && isRebasableElementAddDelta(firstJob?.delta)) {
      return 0;
    }
    const queueLen = Number(persistQueueRef.current?.length || 0);
    const latencyEma = Math.max(20, Number(persistLatencyEmaRef.current || 90));
    const latencyPressure = Math.min(1, latencyEma / 280);
    const queuePressure = Math.min(1, Math.max(0, queueLen - 1) / 18);
    const blend = Math.max(latencyPressure, queuePressure);
    const delay =
      PERSIST_BATCH_DELAY_MIN_MS +
      Math.round((PERSIST_BATCH_DELAY_MAX_MS - PERSIST_BATCH_DELAY_MIN_MS) * blend);
    return Math.max(PERSIST_BATCH_DELAY_MIN_MS, Math.min(PERSIST_BATCH_DELAY_MAX_MS, delay));
  }

  function isDurableDrawPersistJob(job) {
    return (
      job?.opMeta?.kind === "draw" &&
      isRebasableElementAddDelta(job?.delta)
    );
  }

  function getPersistJobAgeMs(job, now = Date.now()) {
    return Math.max(0, now - Number(job?.createdAt || now));
  }

  function isSamePersistJob(currentJob, expectedJob) {
    if (!currentJob || !expectedJob) return false;
    const currentOpId = String(currentJob?.opMeta?.opId || "");
    const expectedOpId = String(expectedJob?.opMeta?.opId || "");
    if (currentOpId || expectedOpId) return currentOpId === expectedOpId;
    return Number(currentJob?.createdAt || 0) === Number(expectedJob?.createdAt || 0);
  }

  function maybeWarnAboutSlowDrawQueue(now = Date.now()) {
    const firstJob = Array.isArray(persistQueueRef.current) ? persistQueueRef.current[0] : null;
    if (!isDurableDrawPersistJob(firstJob)) return;
    const queueLen = Number(persistQueueRef.current?.length || 0);
    const ageMs = getPersistJobAgeMs(firstJob, now);
    if (ageMs >= DRAW_SYNC_SOFT_WARNING_MS || queueLen >= DRAW_SYNC_QUEUE_WARNING_SIZE) {
      setConnectionBannerState((prev) => {
        if (prev === "offline" || prev === "reconnecting") return prev;
        return socket.connected ? "syncing" : "reconnecting";
      });
    }
    if (ageMs >= DRAW_SYNC_HARD_BLOCK_MS || Number(firstJob?.attempts || 0) >= DRAW_SYNC_HARD_ATTEMPT_COUNT) {
      setHardSyncActive(true);
    }
  }

  function schedulePersistFlush() {
    if (persistInFlightRef.current) return;
    if (!persistQueueRef.current.length) return;
    if (persistFlushTimerRef.current) return;
    const waitMs = estimatePersistBatchDelay();
    if (waitMs <= 0) {
      void flushPersistQueue();
      return;
    }
    persistFlushTimerRef.current = setTimeout(() => {
      persistFlushTimerRef.current = null;
      void flushPersistQueue();
    }, waitMs);
  }

  function isLocalEditingHot(now = Date.now(), windowMs = 2000) {
    const recentEdit = now - Number(lastLocalEditAtRef.current || 0) < Math.max(400, Number(windowMs) || 2000);
    const queueBusy = Number(persistQueueRef.current?.length || 0) > 0;
    return recentEdit || queueBusy || persistInFlightRef.current;
  }

  function showConnectedBannerBriefly() {
    clearConnectionBannerTimer();
    setConnectionBannerState("connected");
    connectionBannerTimerRef.current = setTimeout(() => {
      setConnectionBannerState((prev) => (prev === "connected" ? null : prev));
      connectionBannerTimerRef.current = null;
    }, 2000);
  }

  function clearGapSyncTimer() {
    if (!gapSyncTimerRef.current) return;
    clearTimeout(gapSyncTimerRef.current);
    gapSyncTimerRef.current = null;
  }

  function applyServerStateSafely(nextState, options = {}) {
    const normalized = normalizeBoardState(nextState);
    const currentLocal = normalizeBoardState(latestBoardStateRef.current);
    const force = options?.force === true;
    const allowSameVersion = options?.allowSameVersion === true;
    const currentVersion = Number(latestBoardStateRef.current?.version || 1);
    const nextVersion = Number(normalized?.version || 1);
    const currentPageCount = Math.max(1, Math.min(MAX_PAGE_COUNT, Number(currentLocal.pageCount || DEFAULT_PAGE_COUNT)));
    const incomingPageCount = Math.max(1, Math.min(MAX_PAGE_COUNT, Number(normalized.pageCount || DEFAULT_PAGE_COUNT)));
    if (
      incomingPageCount < currentPageCount &&
      options?.allowPageCountDecrease !== true &&
      (currentPageCount > DEFAULT_PAGE_COUNT || currentVersion > 1)
    ) {
      console.warn(
        `[whiteboard] rejected incoming pageCount decrease ${incomingPageCount} < ${currentPageCount}`
      );
      return false;
    }
    const actor = String(me?._id || "");
    if (actor) {
      const localActionLog = Array.isArray(currentLocal.actionLogByUser?.[actor])
        ? currentLocal.actionLogByUser[actor]
        : [];
      const localRedoActionLog = Array.isArray(currentLocal.redoActionLogByUser?.[actor])
        ? currentLocal.redoActionLogByUser[actor]
        : [];
      normalized.actionLogByUser = {
        ...(normalized.actionLogByUser || {}),
        [actor]: localActionLog.slice(-HISTORY_LIMIT),
      };
      normalized.redoActionLogByUser = {
        ...(normalized.redoActionLogByUser || {}),
        [actor]: localRedoActionLog.slice(-HISTORY_LIMIT),
      };
    }
    if (!force && nextVersion < currentVersion) {
      return false;
    }

    if (!force && nextVersion === currentVersion && !allowSameVersion) {
      return false;
    }

    isApplyingRemoteRef.current = true;
    latestBoardStateRef.current = normalized;
    setLocalBoardState(normalized);
    requestAnimationFrame(() => {
      isApplyingRemoteRef.current = false;
    });
    return true;
  }

  async function requestRecoverySyncThrottled(minIntervalMs = 1600, options = {}) {
    const targetRoomCode = String(options?.roomCode || roomCode || "").trim().toUpperCase();
    if (!targetRoomCode || targetRoomCode !== activeRoomCodeRef.current) return false;
    const now = Date.now();
    const minMs = Math.max(600, Number(minIntervalMs) || 1600);
    const force = options?.force === true;
    const recentActivityMs = 12000;
    const hasRecentLocalActivity = now - Number(lastLocalEditAtRef.current || 0) < recentActivityMs;
    const hasRecentRemoteActivity = now - Number(lastRemoteDeltaAtRef.current || 0) < recentActivityMs;

    if (!force && isLocalEditingHot(now, 2200)) {
      return false;
    }
    if (!force && !recoveryNeededRef.current && (hasRecentLocalActivity || hasRecentRemoteActivity)) {
      return false;
    }
    if (recoveryPullInFlightRef.current) return false;
    if (now - Number(recoveryPullLastAtRef.current || 0) < minMs) return false;

    recoveryPullInFlightRef.current = true;
    recoveryPullLastAtRef.current = now;
    try {
      const res = await whiteboardGetRoomState(targetRoomCode).catch(() => null);
      if (
        targetRoomCode === activeRoomCodeRef.current &&
        res?.ok &&
        String(res.room?.code || "") === targetRoomCode
      ) {
        setRoom(res.room);
        clearAllRemoteDraftTimers();
        setRemoteDraftsByUser({});
        applyIncomingBoardState(res.room?.boardState, { force: true, allowSameVersion: true });
        recoveryNeededRef.current = false;
        lastRemoteDeltaAtRef.current = Date.now();
        return true;
      }
      return false;
    } finally {
      recoveryPullInFlightRef.current = false;
    }
  }

  async function runHardRecoverySync(reason = "unknown", targetRoomCode = roomCode) {
    const normalizedTargetRoomCode = String(targetRoomCode || "").trim().toUpperCase();
    if (!normalizedTargetRoomCode || normalizedTargetRoomCode !== activeRoomCodeRef.current) return false;
    setHardSyncActive(true);
    try {
      recoveryNeededRef.current = true;
      const waitStartedAt = Date.now();
      while (isLocalEditingHot(Date.now(), 1800) && Date.now() - waitStartedAt < 2600) {
        await new Promise((resolve) => setTimeout(resolve, 180));
      }
      const ok = await requestRecoverySyncThrottled(400, { roomCode: normalizedTargetRoomCode });
      if (ok) return true;
      // Druga próba po krótkim czasie, żeby dać szansę po reconnect/ack timeout.
      await new Promise((resolve) => setTimeout(resolve, 450));
      const retryOk = await requestRecoverySyncThrottled(400, { roomCode: normalizedTargetRoomCode });
      if (!retryOk) {
        setErr((prev) => prev || `sync_recovery_failed:${reason}`);
      }
      return retryOk;
    } finally {
      setHardSyncActive(false);
    }
  }

  async function requestGapRecoverySync() {
    const now = Date.now();
    if (gapSyncInFlightRef.current) return;
    if (now - Number(gapSyncLastAtRef.current || 0) < 1200) return;
    gapSyncInFlightRef.current = true;
    gapSyncLastAtRef.current = now;
    try {
      const ok = await requestRecoverySyncThrottled(1200);
      if (ok) recoveryNeededRef.current = false;
    } finally {
      gapSyncInFlightRef.current = false;
    }
  }

  function scheduleGapRecoverySync(delayMs = 220) {
    recoveryNeededRef.current = true;
    clearGapSyncTimer();
    const wait = Math.max(120, Number(delayMs) || 220);
    gapSyncTimerRef.current = setTimeout(() => {
      gapSyncTimerRef.current = null;
      void requestGapRecoverySync();
    }, wait);
  }

  async function requestPageWindowSlice(page, options = {}) {
    if (!pageWindowModeRef.current) return false;
    if (pageWindowFetchInFlightRef.current && !options?.force) return false;

    const normalizedCurrent = normalizeBoardState(latestBoardStateRef.current);
    const maxPage = Math.max(0, Number(normalizedCurrent.pageCount || DEFAULT_PAGE_COUNT) - 1);
    const targetPage = Math.max(0, Math.min(maxPage, Number(page || 0)));
    const requestKey = `${roomCode}:${targetPage}:${maxPage}`;
    if (!options?.force && requestKey === pageWindowLastKeyRef.current) return false;

    pageWindowLastKeyRef.current = requestKey;
    pageWindowFetchInFlightRef.current = true;
    pageWindowRequestSeqRef.current += 1;
    const requestSeq = Number(pageWindowRequestSeqRef.current || 0);

    try {
      const res = await whiteboardGetPageWindow(roomCode, targetPage, targetPage, {
        paddingBefore: PAGE_WINDOW_PADDING_BEFORE,
        paddingAfter: PAGE_WINDOW_PADDING_AFTER,
      }).catch(() => null);
      if (!res?.ok || String(res.code || "") !== roomCode) return false;
      if (requestSeq !== Number(pageWindowRequestSeqRef.current || 0)) return false;

      const serverSlice = normalizeBoardState(res.boardState || {});
      const merged = {
        ...normalizedCurrent,
        version: Math.max(Number(normalizedCurrent.version || 1), Number(serverSlice.version || 1)),
        pageCount: Math.max(1, Math.min(MAX_PAGE_COUNT, Number(serverSlice.pageCount || normalizedCurrent.pageCount || DEFAULT_PAGE_COUNT))),
        taskBlocks: cloneTaskBlocks(
          Array.isArray(serverSlice.taskBlocks) ? serverSlice.taskBlocks : normalizedCurrent.taskBlocks
        ),
        activeTaskBlockId: resolveActiveTaskBlockId(
          Array.isArray(serverSlice.taskBlocks) ? serverSlice.taskBlocks : normalizedCurrent.taskBlocks,
          serverSlice.activeTaskBlockId || normalizedCurrent.activeTaskBlockId
        ),
        elements: ensureElementIds(cloneElements(serverSlice.elements)),
        updatedAt: Date.now(),
      };

      const applied = applyServerStateSafely(merged, { force: true, allowSameVersion: true });
      if (applied) {
        recoveryNeededRef.current = false;
        lastRemoteDeltaAtRef.current = Date.now();
      }
      return applied;
    } finally {
      if (requestSeq === Number(pageWindowRequestSeqRef.current || 0)) {
        pageWindowFetchInFlightRef.current = false;
      }
    }
  }

  function applyIncomingBoardState(rawBoardState, options = {}) {
    const normalized = normalizeBoardState(rawBoardState);
    const usePageWindow = shouldUsePageWindowMode(normalized);
    pageWindowModeRef.current = usePageWindow;
    if (!usePageWindow) {
      pageWindowLastKeyRef.current = "";
      pageWindowFetchInFlightRef.current = false;
    }

    const pageCount = Math.max(1, Number(normalized.pageCount || DEFAULT_PAGE_COUNT));
    const targetPage = Math.max(0, Math.min(pageCount - 1, Number(currentCanvasPageRef.current || 0)));
    const boardForLocal = usePageWindow
      ? {
          ...normalized,
          elements: filterElementsForPageWindow(
            normalized.elements,
            targetPage,
            targetPage,
            pageCount,
            PAGE_WINDOW_PADDING_BEFORE,
            PAGE_WINDOW_PADDING_AFTER
          ),
        }
      : normalized;

    // Projekcja lokalnych nierozliczonych operacji na świeży stan serwera:
    // redukuje efekt "napisał i zniknęło" przy słabszym łączu/retry.
    const pendingOps = Array.isArray(persistQueueRef.current) ? persistQueueRef.current : [];
    const projected = pendingOps.reduce((stateAcc, job) => {
      const delta = job?.delta;
      if (!delta || typeof delta !== "object") return stateAcc;
      const baseVersion = Number(job?.baseVersion);
      const currentVersion = Number(stateAcc?.version || 1);
      if (!Number.isFinite(baseVersion)) return stateAcc;
      // Odtwarzamy tylko operacje "przyszłe" względem aktualnego server state.
      // Append-only kreski z unikalnym id można projektować także po przesunięciu wersji,
      // bo applyDeltaToBoardState odfiltruje duplikaty.
      if (baseVersion < currentVersion && !isRebasableElementAddDelta(delta)) return stateAcc;
      const nextProjected = applyDeltaToBoardState(stateAcc, delta);
      if (!nextProjected) return stateAcc;
      return {
        ...nextProjected,
        version: currentVersion + 1,
        updatedAt: Date.now(),
      };
    }, boardForLocal);

    const applied = applyServerStateSafely(projected, options);
    if (applied && usePageWindow) {
      void requestPageWindowSlice(targetPage, { force: true });
    }
    return applied;
  }

  function clearConsistencyAuditTimer() {
    if (!consistencyAuditTimerRef.current) return;
    clearTimeout(consistencyAuditTimerRef.current);
    consistencyAuditTimerRef.current = null;
  }

  async function runConsistencyAudit(minIntervalMs = 7000) {
    if (consistencyAuditInFlightRef.current) return false;
    const now = Date.now();
    const minMs = Math.max(2000, Number(minIntervalMs) || 7000);
    if (now - Number(consistencyAuditLastAtRef.current || 0) < minMs) return false;
    if (isLocalEditingHot(now, 1600)) return false;
    if (!socket?.connected) return false;

    consistencyAuditInFlightRef.current = true;
    consistencyAuditLastAtRef.current = now;
    try {
      const res = await whiteboardGetRoomState(roomCode).catch(() => null);
      if (!res?.ok || String(res.room?.code || "") !== roomCode) return false;
      setRoom(res.room);
      clearAllRemoteDraftTimers();
      setRemoteDraftsByUser({});

      const localVersion = Number(latestBoardStateRef.current?.version || 1);
      const serverVersion = Number(res.room?.boardState?.version || 1);
      const shouldForce = serverVersion !== localVersion;
      const applied = applyIncomingBoardState(res.room?.boardState, {
        force: shouldForce,
        allowSameVersion: true,
      });
      if (applied || shouldForce) {
        recoveryNeededRef.current = false;
        lastRemoteDeltaAtRef.current = Date.now();
      }
      return applied;
    } finally {
      consistencyAuditInFlightRef.current = false;
    }
  }

  function scheduleConsistencyAudit(delayMs = 2200, minIntervalMs = 7000) {
    clearConsistencyAuditTimer();
    const wait = Math.max(400, Number(delayMs) || 2200);
    consistencyAuditTimerRef.current = setTimeout(() => {
      consistencyAuditTimerRef.current = null;
      void runConsistencyAudit(minIntervalMs);
    }, wait);
  }

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    function handleResize() {
      if (resizeRafRef.current) {
        cancelAnimationFrame(resizeRafRef.current);
      }
      resizeRafRef.current = requestAnimationFrame(() => {
        const vvHeight = Number(window.visualViewport?.height || 0);
        const nextHeight = vvHeight > 0 ? vvHeight : window.innerHeight || 900;
        setWindowHeight((prev) => {
          const previous = Number(prev || 0);
          // Stabilizacja pod tablet/mobile: ignorujemy drobne "drgania" viewportu,
          // które powodują przeskakiwanie układu paneli.
          if (previous > 0 && Math.abs(nextHeight - previous) < 18) return previous;
          return nextHeight;
        });
        resizeRafRef.current = null;
      });
    }

    handleResize();
    window.addEventListener("resize", handleResize);
    window.visualViewport?.addEventListener?.("resize", handleResize);
    return () => {
      if (resizeRafRef.current) {
        cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = null;
      }
      window.removeEventListener("resize", handleResize);
      window.visualViewport?.removeEventListener?.("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (prefsReady || typeof window === "undefined") return;
    const stored = window.localStorage.getItem(getBoardSurfaceStorageKey(themeMode));
    setBoardSurface(BOARD_SURFACE_OPTIONS.has(stored) ? stored : "default");
  }, [themeMode, prefsReady]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(getBoardSurfaceStorageKey(themeMode), boardSurface);
  }, [boardSurface, themeMode]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(getGridEnabledStorageKey(), gridEnabled ? "1" : "0");
  }, [gridEnabled]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(getVoiceWidgetEnabledStorageKey(), voiceWidgetEnabled ? "1" : "0");
  }, [voiceWidgetEnabled]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STYLUS_ONLY_STORAGE_KEY, stylusOnly ? "1" : "0");
  }, [stylusOnly]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(HOLD_TOOL_SWITCH_STORAGE_KEY, holdToolSwitchEnabled ? "1" : "0");
  }, [holdToolSwitchEnabled]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STYLUS_CURSOR_STORAGE_KEY, stylusCursorEnabled ? "1" : "0");
  }, [stylusCursorEnabled]);

  useEffect(() => {
    let cancelled = false;

    async function loadPrefs() {
      let hasLocalPrefs = false;
      try {
        if (typeof window !== "undefined") {
          const raw = window.localStorage.getItem(ROOM_WHITEBOARD_PREFS_STORAGE_KEY);
          if (raw) {
            hasLocalPrefs = true;
            const parsed = normalizeWhiteboardPrefs(JSON.parse(raw));
            if (!cancelled) {
              setPenColor(parsed.color);
              if (HIGHLIGHTER_COLORS.includes(parsed.color)) {
                setHighlighterColor(parsed.color);
              }
              setHasCustomColor(true);
              setBrushSize(parsed.penSize);
              setHighlighterSize(parsed.highlighterSize);
              setBoardSurface(parsed.boardSurface);
              setGridEnabled(parsed.gridEnabled);
            }
          }
        }
      } catch {
        // ignore local fallback issues
      }

      if (demoMode) {
        if (!cancelled) setPrefsReady(true);
        return;
      }

      try {
        const serverPrefs = await fetchWhiteboardPrefs();
        if (!cancelled) {
          setPenColor(serverPrefs.color);
          if (HIGHLIGHTER_COLORS.includes(serverPrefs.color)) {
            setHighlighterColor(serverPrefs.color);
          }
          const isDefaultServerColor =
            String(serverPrefs.color || "").toLowerCase() === String(PEN_COLORS[0] || "").toLowerCase();
          // If user has no local prefs and backend returns plain default color,
          // keep color as "not custom" so participant-assigned color (owner/2nd/3rd) can apply.
          setHasCustomColor(hasLocalPrefs || !isDefaultServerColor);
          setBrushSize(serverPrefs.penSize);
          setHighlighterSize(serverPrefs.highlighterSize);
          setBoardSurface(serverPrefs.boardSurface);
          setGridEnabled(serverPrefs.gridEnabled);
          if (typeof window !== "undefined") {
            window.localStorage.setItem(ROOM_WHITEBOARD_PREFS_STORAGE_KEY, JSON.stringify(serverPrefs));
          }
        }
      } catch {
        // silent fallback to local/default
      } finally {
        if (!cancelled) setPrefsReady(true);
      }
    }

    loadPrefs();
    return () => {
      cancelled = true;
    };
  }, [demoMode]);

  useEffect(() => {
    if (!prefsReady) return undefined;
    if (prefsSaveTimerRef.current) clearTimeout(prefsSaveTimerRef.current);

    const payload = {
      color: penColor,
      penSize: brushSize,
      highlighterSize,
      boardSurface,
      gridEnabled,
    };

    prefsSaveTimerRef.current = setTimeout(() => {
      if (!demoMode) patchWhiteboardPrefs(payload).catch(() => {});
      if (typeof window !== "undefined") {
        window.localStorage.setItem(ROOM_WHITEBOARD_PREFS_STORAGE_KEY, JSON.stringify(payload));
      }
      prefsSaveTimerRef.current = null;
    }, 600);

    return () => {
      if (prefsSaveTimerRef.current) {
        clearTimeout(prefsSaveTimerRef.current);
        prefsSaveTimerRef.current = null;
      }
    };
  }, [demoMode, prefsReady, penColor, brushSize, highlighterSize, boardSurface, gridEnabled]);

  useEffect(() => {
    if (demoMode) {
      const demoState = readDemoBoardState();
      setMe({ _id: "demo-user", name: "Gosc demo", displayName: "Gosc demo", role: "student" });
      setRoom(createDemoRoom(roomCode, demoState));
      setLocalBoardState(demoState);
      latestBoardStateRef.current = demoState;
      pageWindowModeRef.current = false;
      setErr("");
      return undefined;
    }

    let alive = true;
    setArchiveReadOnly(false);
    setArchiveReadOnlyAt(null);
    pageWindowModeRef.current = false;
    pageWindowLastKeyRef.current = "";
    pageWindowFetchInFlightRef.current = false;
    pageWindowRequestSeqRef.current += 1;

    (async () => {
      const ping = await whiteboardPing({
        code: roomCode,
        path: `${window.location.pathname || ""}${window.location.search || ""}`,
      });
      if (!alive) return;
      if (ping?.ok) setMe(ping.user || null);

      const res = await whiteboardJoinRoom(roomCode);
      if (!alive) return;
      if (!res?.ok) {
        if (res?.error === "room_read_only" && authorizedFetch) {
          const archiveResponse = await authorizedFetch(`/api/whiteboards/${roomCode}/read-only`);
          const archiveData = await archiveResponse.json().catch(() => null);
          if (!alive) return;
          if (!archiveResponse.ok || !archiveData?.room?.boardState) {
            setErr(archiveData?.error || "archive_load_failed");
            return;
          }
          setArchiveReadOnly(true);
          setArchiveReadOnlyAt(archiveData.archivedAt || null);
          setConnectionBannerState(null);
          setTool("select");
          setRoom(archiveData.room);
          applyIncomingBoardState(archiveData.room.boardState, { force: true, allowSameVersion: true });
          setErr("");
          return;
        }
        setErr(res?.error || "join_failed");
        return;
      }
      setArchiveReadOnly(false);
      setArchiveReadOnlyAt(null);
      setRoom(res.room);
      applyIncomingBoardState(res.room?.boardState, { force: true, allowSameVersion: true });
    })().catch((e) => {
      if (!alive) return;
      setErr(String(e?.message || e));
    });

    return () => {
      alive = false;
    };
  }, [authorizedFetch, demoMode, roomCode]);

  useEffect(() => {
    if (demoMode || archiveReadOnly || typeof window === "undefined") return undefined;
    const heartbeatIntervalMs = 55 * 1000;

    const sendActivityHeartbeat = () => {
      if (!socket.connected) return;
      if (typeof document !== "undefined" && document.hidden) return;
      void whiteboardPing({
        code: roomCode,
        path: `${window.location.pathname || ""}${window.location.search || ""}`,
      });
    };

    const intervalId = window.setInterval(sendActivityHeartbeat, heartbeatIntervalMs);
    window.addEventListener("focus", sendActivityHeartbeat);
    window.addEventListener("online", sendActivityHeartbeat);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", sendActivityHeartbeat);
    }

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", sendActivityHeartbeat);
      window.removeEventListener("online", sendActivityHeartbeat);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", sendActivityHeartbeat);
      }
    };
  }, [archiveReadOnly, demoMode, roomCode, socket]);

  useEffect(() => {
    if (demoMode || archiveReadOnly) return undefined;

    const onRoomState = (payload) => {
      if (!payload?.ok) return;
      if (String(payload.room?.code || "") !== roomCode) return;
      setRoom(payload.room);
      const applied = applyIncomingBoardState(payload.room?.boardState);
      if (applied) {
        recoveryNeededRef.current = false;
        lastRemoteDeltaAtRef.current = Date.now();
      }
    };

    const onRoomDelta = (payload) => {
      if (!payload?.ok) return;
      if (String(payload.code || "") !== roomCode) return;
      const delta = payload.delta;
      if (!delta || typeof delta !== "object") return;
      lastRemoteDeltaAtRef.current = Date.now();

      const localVersion = Number(latestBoardStateRef.current?.version || 1);
      const payloadBaseVersion = Number(payload.baseVersion);
      const payloadVersion = Number(payload.version || localVersion + 1);
      const hasPayloadBaseVersion = Number.isFinite(payloadBaseVersion);
      const hasVersionGapWithoutBase =
        !hasPayloadBaseVersion && Number.isFinite(payloadVersion) && payloadVersion > localVersion + 1;

      if (hasPayloadBaseVersion) {
        if (payloadBaseVersion < localVersion) {
          if (!isRebasableElementAddDelta(delta)) return;
        }
        if (payloadBaseVersion > localVersion) {
          recoveryNeededRef.current = true;
          scheduleGapRecoverySync(240);
          return;
        }
      }

      if (hasVersionGapWithoutBase) {
        recoveryNeededRef.current = true;
        scheduleGapRecoverySync(240);
        return;
      }

      setLocalBoardState((prev) => {
        const base = normalizeBoardState(prev);
        const currentVersion = Number(base.version || 1);
        const nextVersion = Number(payload.version || currentVersion + 1);
        if (nextVersion <= currentVersion && !isRebasableElementAddDelta(delta)) return prev;

        const nextState = applyDeltaToBoardState(base, delta);
        if (!nextState) return prev;
        nextState.version = Math.max(currentVersion, nextVersion);
        nextState.updatedAt = Date.now();
        nextState.updatedBy = String(payload.updatedBy || base.updatedBy || "");
        const nextForLocal = pageWindowModeRef.current
          ? {
              ...nextState,
              elements: filterElementsForPageWindow(
                nextState.elements,
                currentCanvasPageRef.current,
                currentCanvasPageRef.current,
                nextState.pageCount,
                PAGE_WINDOW_PADDING_BEFORE,
                PAGE_WINDOW_PADDING_AFTER
              ),
            }
          : nextState;
        latestBoardStateRef.current = nextForLocal;
        recoveryNeededRef.current = false;
        scheduleConsistencyAudit(2400, 7000);
        return nextForLocal;
      });
    };

    const onRemoteCursor = (payload) => {
      if (!payload?.ok) return;
      const userId = String(payload.userId || "");
      if (!userId) return;

      setRemoteCursorsByUser((prev) => {
        if (!payload.point) {
          const next = { ...prev };
          delete next[userId];
          return next;
        }

        return {
          ...prev,
          [userId]: {
            userId,
            name: payload.name || "Uczestnik",
            point: payload.point,
          },
        };
      });
    };

    socket.on("WHITEBOARD:ROOM_STATE", onRoomState);
    socket.on("WHITEBOARD:DELTA", onRoomDelta);
    socket.on("WHITEBOARD:REMOTE_CURSOR", onRemoteCursor);

    return () => {
      clearGapSyncTimer();
      clearConsistencyAuditTimer();
      socket.off("WHITEBOARD:ROOM_STATE", onRoomState);
      socket.off("WHITEBOARD:DELTA", onRoomDelta);
      socket.off("WHITEBOARD:REMOTE_CURSOR", onRemoteCursor);
    };
  }, [archiveReadOnly, demoMode, roomCode, socket]);

  useEffect(() => {
    if (demoMode || archiveReadOnly) return undefined;

    let mounted = true;
    let recoveryTimer = null;
    let recoveryInFlight = false;
    const RECOVERY_RETRY_MS = 4000;

    function clearRecoveryTimer() {
      if (!recoveryTimer) return;
      clearTimeout(recoveryTimer);
      recoveryTimer = null;
    }

    function scheduleRecovery(delayMs = RECOVERY_RETRY_MS) {
      clearRecoveryTimer();
      const waitMs = Math.max(1200, Number(delayMs) || RECOVERY_RETRY_MS);
      recoveryTimer = setTimeout(() => {
        requestRecoverySync().catch(() => {
          scheduleRecovery(RECOVERY_RETRY_MS);
        });
      }, waitMs);
    }

    async function requestRecoverySync() {
      if (!mounted || recoveryInFlight) return;
      const now = Date.now();
      const recentActivityMs = 12000;
      const hasRecentLocalActivity = now - Number(lastLocalEditAtRef.current || 0) < recentActivityMs;
      const hasRecentRemoteActivity = now - Number(lastRemoteDeltaAtRef.current || 0) < recentActivityMs;
      if (!recoveryNeededRef.current && (hasRecentLocalActivity || hasRecentRemoteActivity)) return;
      if (!socket.connected) {
        scheduleRecovery(RECOVERY_RETRY_MS);
        return;
      }
      recoveryInFlight = true;
      const res = await whiteboardGetRoomState(roomCode).catch(() => null);
      recoveryInFlight = false;
      if (!mounted || !res?.ok || String(res.room?.code || "") !== roomCode) {
        scheduleRecovery(RECOVERY_RETRY_MS);
        return;
      }
      clearRecoveryTimer();
      setRoom(res.room);
      clearAllRemoteDraftTimers();
      setRemoteDraftsByUser({});
      const applied = applyIncomingBoardState(res.room?.boardState, { force: true, allowSameVersion: true });
      if (applied) {
        recoveryNeededRef.current = false;
        lastRemoteDeltaAtRef.current = Date.now();
      }
    }

    function handleReconnect() {
      recoveryNeededRef.current = true;
      whiteboardJoinRoom(roomCode)
        .then((res) => {
          if (!mounted || !res?.ok) return;
          clearRecoveryTimer();
          setRoom(res.room);
          clearAllRemoteDraftTimers();
          setRemoteDraftsByUser({});
          const applied = applyIncomingBoardState(res.room?.boardState, { force: true, allowSameVersion: true });
          if (applied) {
            recoveryNeededRef.current = false;
            lastRemoteDeltaAtRef.current = Date.now();
          }
        })
        .catch(() => {
          scheduleRecovery(RECOVERY_RETRY_MS);
        });
    }

    function handleDisconnect() {
      recoveryNeededRef.current = true;
      scheduleRecovery(RECOVERY_RETRY_MS);
    }

    function handleVisibilityOrFocus() {
      if (!mounted) return;
      if (typeof document !== "undefined" && document.hidden) return;
      requestRecoverySync().catch(() => {
        scheduleRecovery(RECOVERY_RETRY_MS);
      });
      void runConsistencyAudit(1200);
    }

    socket.on("connect", handleReconnect);
    socket.on("reconnect", handleReconnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleDisconnect);
    window.addEventListener("focus", handleVisibilityOrFocus);
    window.addEventListener("online", handleVisibilityOrFocus);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityOrFocus);
    }

    return () => {
      mounted = false;
      clearRecoveryTimer();
      clearAllRemoteDraftTimers();
      socket.off("connect", handleReconnect);
      socket.off("reconnect", handleReconnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleDisconnect);
      window.removeEventListener("focus", handleVisibilityOrFocus);
      window.removeEventListener("online", handleVisibilityOrFocus);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
      }
    };
  }, [archiveReadOnly, demoMode, roomCode, socket]);

  useEffect(() => {
    if (demoMode || archiveReadOnly) return undefined;
    if (typeof window === "undefined") return undefined;

    function onOnline() {
      if (socket.connected) {
        showConnectedBannerBriefly();
        return;
      }
      setConnectionBannerState("reconnecting");
    }

    function onOffline() {
      clearConnectionBannerTimer();
      setConnectionBannerState("offline");
    }

    function onSocketConnect() {
      const current = connectionBannerStateRef.current;
      if (current === "offline" || current === "reconnecting") {
        showConnectedBannerBriefly();
        return;
      }
      clearConnectionBannerTimer();
      setConnectionBannerState(null);
    }

    function onSocketDisconnect() {
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        clearConnectionBannerTimer();
        setConnectionBannerState("offline");
        return;
      }
      clearConnectionBannerTimer();
      setConnectionBannerState("reconnecting");
    }

    function onSocketConnecting() {
      if (typeof navigator !== "undefined" && !navigator.onLine) return;
      setConnectionBannerState((prev) => (prev === "connected" ? prev : "reconnecting"));
    }

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    socket.on("connect", onSocketConnect);
    socket.on("disconnect", onSocketDisconnect);
    socket.on("connect_error", onSocketConnecting);
    socket.on("reconnect_attempt", onSocketConnecting);

    return () => {
      clearConnectionBannerTimer();
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      socket.off("connect", onSocketConnect);
      socket.off("disconnect", onSocketDisconnect);
      socket.off("connect_error", onSocketConnecting);
      socket.off("reconnect_attempt", onSocketConnecting);
    };
  }, [archiveReadOnly, demoMode, socket]);

  useEffect(() => {
    function onFullscreenChange() {
      setIsFullscreen(document.fullscreenElement === fullscreenRef.current);
    }

    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
    };
  }, []);

  useEffect(() => {
    return () => {
      clearAllRemoteDraftTimers();
      clearConnectionBannerTimer();
      clearPersistFlushTimer();
      const draftRef = draftThrottleRef.current;
      if (draftRef.timer) clearTimeout(draftRef.timer);

      const cursorRef = cursorThrottleRef.current;
      if (cursorRef.timer) clearTimeout(cursorRef.timer);
      if (cursorRef.hideTimer) clearTimeout(cursorRef.hideTimer);
    };
  }, []);

  const isHost = !!me?._id && !!room?.hostUserId && String(me._id) === String(room.hostUserId);
  const isMobileNarrow = typeof window !== "undefined" ? window.innerWidth <= 768 : false;
  const participantColorMap = useMemo(
    () => buildParticipantColorMap(room),
    [room?.hostUserId, room?.participants]
  );
  const assignedUserColor = me?._id
    ? participantColorMap[String(me._id)] || colorForUserId(me._id)
    : PARTICIPANT_COLORS[0];
  const shareUrl = demoMode
    ? `${window.location.origin}/demo/tablica`
    : `${window.location.origin}/tablica/${roomCode}`;
  const participantNames = (room?.participants || []).map((p) => p.name).filter(Boolean);
  const remoteDrafts = [];
  const remoteCursors = Object.values(remoteCursorsByUser || {}).map((cursor) => ({
    ...cursor,
    color: participantColorMap[String(cursor.userId)] || colorForUserId(cursor.userId),
  }));
  const myUserId = String(me?._id || "");
  const canUndo =
    (localBoardState.actionLogByUser?.[myUserId] || []).length > 0 ||
    (localBoardState.elements || []).some((element) => String(element?.createdBy || "") === myUserId);
  const canRedo =
    (localBoardState.redoActionLogByUser?.[myUserId] || []).length > 0 ||
    (localBoardState.redoByUser?.[myUserId] || []).length > 0;
  const fullscreenActive = isFullscreen || isPseudoFullscreen;
  const modalPortalTarget = fullscreenActive ? fullscreenRef.current : null;
  const taskBlocks = useMemo(
    () => (Array.isArray(localBoardState.taskBlocks) ? localBoardState.taskBlocks : []),
    [localBoardState.taskBlocks]
  );
  const activeTaskBlockId = resolveActiveTaskBlockId(taskBlocks, localBoardState.activeTaskBlockId);
  const taskBlocksWithLocalCollapse = useMemo(() => taskBlocks.map((block) => {
    const blockId = String(block?.id || "");
    if (!blockId || !Object.prototype.hasOwnProperty.call(taskCollapsePrefs, blockId)) return block;
    return { ...block, collapsed: !!taskCollapsePrefs[blockId] };
  }), [taskBlocks, taskCollapsePrefs]);
  const activeTaskBlock = taskBlocksWithLocalCollapse.find((block) => String(block?.id || "") === String(activeTaskBlockId || "")) || null;
  useEffect(() => {
    setTaskCollapsePrefs(readTaskCollapsePrefs(roomCode));
    setTaskDockHidden(readTaskDockHidden(roomCode));
  }, [roomCode]);

  const selectedSectionOption = SECTION_OPTIONS[taskSectionIndex] || SECTION_OPTIONS[0] || null;
  const selectedTopicOptions = selectedSectionOption?.topics || [];
  const selectedTopicOption = selectedTopicOptions[taskTopicIndex] || selectedTopicOptions[0] || null;
  const selectedSubtopicOptions = (selectedTopicOption?.subtopics || []).filter(
    (subtopic) => subtopic && subtopic.id && !subtopic.type && !subtopic.standaloneQuiz
  );

  const activeTaskSubtopicId = taskSubtopicId || String(selectedSubtopicOptions[0]?.id || "");
  const activeTaskAdapter = taskAdapters[String(activeTaskSubtopicId || "")] || null;
  const activeTaskGenerationModes = normalizeTaskGenerationModes(activeTaskAdapter).filter(
    (mode) => isAdminUser || !(
      mode?.generationOptions?.variant === "original" &&
      mode?.generationOptions?.session === "2026_MAJ"
    )
  );

  useEffect(() => {
    if (!fullscreenActive || typeof document === "undefined") return undefined;
    const body = document.body;
    const html = document.documentElement;
    const scrollY = typeof window !== "undefined" ? window.scrollY || window.pageYOffset || 0 : 0;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyPosition = body.style.position;
    const prevBodyTop = body.style.top;
    const prevBodyLeft = body.style.left;
    const prevBodyRight = body.style.right;
    const prevBodyWidth = body.style.width;
    const prevBodyOverscroll = body.style.overscrollBehavior;
    const prevHtmlOverflow = html?.style?.overflow;
    const prevHtmlOverscroll = html?.style?.overscrollBehavior;
    document.body.classList.add("whiteboard-room-fullscreen-active");
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overscrollBehavior = "none";
    if (html) {
      html.style.overflow = "hidden";
      html.style.overscrollBehavior = "none";
    }
    return () => {
      body.style.overflow = prevBodyOverflow;
      body.style.position = prevBodyPosition;
      body.style.top = prevBodyTop;
      body.style.left = prevBodyLeft;
      body.style.right = prevBodyRight;
      body.style.width = prevBodyWidth;
      body.style.overscrollBehavior = prevBodyOverscroll;
      if (html) {
        html.style.overflow = prevHtmlOverflow;
        html.style.overscrollBehavior = prevHtmlOverscroll;
      }
      document.body.classList.remove("whiteboard-room-fullscreen-active");
      if (typeof window !== "undefined") {
        window.scrollTo(0, scrollY);
      }
    };
  }, [fullscreenActive]);

  useEffect(() => {
    if (hasCustomColor) return;
    setPenColor(assignedUserColor);
  }, [assignedUserColor, hasCustomColor]);

  useEffect(() => {
    if (HIGHLIGHTER_COLORS.includes(highlighterColor)) return;
    setHighlighterColor(HIGHLIGHTER_COLORS[0]);
  }, [highlighterColor]);

  useEffect(() => {
    ensureTaskAdaptersForBlocks(taskBlocks);
  }, [taskBlocks]);

  useEffect(() => {
    const el = activeTaskBodyRef.current;
    if (!activeTaskBlock || !el || activeTaskBlock?.collapsed) {
      setActiveTaskScrollable(false);
      return undefined;
    }

    const updateOverflow = () => {
      const node = activeTaskBodyRef.current;
      if (!node) return;
      const scrollHeight = Number(node.scrollHeight || 0);
      const clientHeight = Number(node.clientHeight || 0);
      setActiveTaskScrollable((prev) => {
        // Histereza: ogranicza miganie przy granicznych wartościach wysokości.
        const onThreshold = clientHeight + 12;
        const offThreshold = clientHeight + 4;
        if (prev) return scrollHeight > offThreshold;
        return scrollHeight > onThreshold;
      });
    };

    updateOverflow();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateOverflow);
      return () => window.removeEventListener("resize", updateOverflow);
    }

    const ro = new ResizeObserver(() => updateOverflow());
    ro.observe(el);
    window.addEventListener("resize", updateOverflow);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", updateOverflow);
    };
  }, [
    activeTaskBlock?.id,
    activeTaskBlock?.collapsed,
    activeTaskBlock?.answerShown,
    activeTaskBlock?.explanationShown,
    activeTaskBlock?.fullHeight,
  ]);

  useEffect(() => {
    const section = SECTION_OPTIONS[taskSectionIndex] || SECTION_OPTIONS[0] || null;
    const topicCount = Array.isArray(section?.topics) ? section.topics.length : 0;
    if (!topicCount) {
      if (taskTopicIndex !== 0) setTaskTopicIndex(0);
      if (taskSubtopicId) setTaskSubtopicId("");
      return;
    }
    if (taskTopicIndex >= topicCount) {
      setTaskTopicIndex(0);
      return;
    }

    const topic = section.topics[taskTopicIndex] || section.topics[0];
    const subtopics = (topic?.subtopics || []).filter((subtopic) => subtopic && subtopic.id && !subtopic.type && !subtopic.standaloneQuiz);
    const firstSubtopicId = String(subtopics[0]?.id || "");
    if (!subtopics.some((subtopic) => String(subtopic.id) === String(taskSubtopicId))) {
      setTaskSubtopicId(firstSubtopicId);
    }
  }, [taskSectionIndex, taskTopicIndex, taskSubtopicId]);

  useEffect(() => {
    if (!taskPickerOpen) return;

    const subtopicId = String(activeTaskSubtopicId || "");
    if (!subtopicId) {
      setTaskChoices([]);
      setTaskChoiceKey("");
      setTaskGenerationMode("");
      return;
    }

    let cancelled = false;
    async function loadTaskChoices() {
      try {
        setTaskChoicesLoading(true);
        const adapter = await ensureTaskAdapter(subtopicId);
        const mode = resolveTaskGenerationMode(adapter, taskGenerationMode);
        const generatedRaw = generateTasksForChoice(adapter, {
          generationMode: mode?.value || "",
          generationOptions: mode?.generationOptions || null,
        });
        const generated = Array.isArray(generatedRaw) ? generatedRaw : [];
        const nextChoices = buildTaskTypeChoices(generated).map((choice) => {
          return {
            ...choice,
            generationMode: mode?.value || "",
            generationOptions: mode?.generationOptions ? { ...mode.generationOptions } : null,
          };
        });

        if (cancelled) return;
        setTaskGenerationMode(mode?.value || "");
        setTaskChoices(nextChoices);
        setTaskChoiceKey((prev) => (nextChoices.some((choice) => choice.key === prev) ? prev : String(nextChoices[0]?.key || "")));
      } catch {
        if (cancelled) return;
        setTaskChoices([]);
        setTaskChoiceKey("");
      } finally {
        if (!cancelled) setTaskChoicesLoading(false);
      }
    }

    loadTaskChoices();
    return () => {
      cancelled = true;
    };
  }, [activeTaskSubtopicId, taskGenerationMode, taskPickerOpen]);

  useEffect(() => {
    function handleKeyDown(e) {
      if (isEditableTarget(e.target)) return;

      const key = String(e.key || "").toLowerCase();
      const ctrlOrMeta = e.ctrlKey || e.metaKey;
      if (archiveReadOnly) {
        if (key === "escape" && fullscreenActive) {
          e.preventDefault();
          setIsPseudoFullscreen(false);
          setIsFullscreen(false);
        }
        return;
      }

      if (key === "escape") {
        e.preventDefault();
        if (tool !== "select") {
          setTool("select");
          return;
        }
        if (fullscreenActive) {
          setIsPseudoFullscreen(false);
          setIsFullscreen(false);
        }
        return;
      }

      if (ctrlOrMeta) {
        if (key === "z" && !e.shiftKey) {
          if (canUndo) {
            e.preventDefault();
            handleUndo();
          }
          return;
        }

        if (key === "y" || (key === "z" && e.shiftKey)) {
          if (canRedo) {
            e.preventDefault();
            handleRedo();
          }
          return;
        }

        if (key === "+" || key === "=") {
          e.preventDefault();
          zoomIn();
          return;
        }

        if (key === "-" || key === "_") {
          e.preventDefault();
          zoomOut();
          return;
        }

        if (key === "0") {
          e.preventDefault();
          setZoomPercent(100);
          return;
        }
      }

      if (e.altKey || ctrlOrMeta) return;

      if (key === "p") {
        e.preventDefault();
        setTool("pen");
        return;
      }

      if (key === "s") {
        e.preventDefault();
        setTool("select");
        return;
      }

      if (key === "l") {
        e.preventDefault();
        setTool("highlighter");
        return;
      }

      if (key === "e") {
        e.preventDefault();
        setTool("eraser");
        return;
      }

      if (key === "i") {
        e.preventDefault();
        openImagePicker();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [archiveReadOnly, canRedo, canUndo, penColor, highlighterColor, tool, fullscreenActive]);

  function handleColorChange(nextColor) {
    if (tool === "highlighter") {
      setHighlighterColor(nextColor);
      return;
    }
    setHasCustomColor(true);
    setPenColor(nextColor);
  }

  function isQuickConfigurableTool(toolName) {
    return ["pen", "highlighter", "line", "polygon", "circle"].includes(String(toolName || ""));
  }

  function getQuickToolColor(toolName) {
    return String(toolName || "") === "highlighter" ? highlighterColor : penColor;
  }

  function setQuickToolColor(toolName, nextColor) {
    if (String(toolName || "") === "highlighter") {
      setHighlighterColor(nextColor);
      return;
    }
    setHasCustomColor(true);
    setPenColor(nextColor);
  }

  function getQuickToolSize(toolName) {
    return String(toolName || "") === "highlighter" ? highlighterSize : brushSize;
  }

  function setQuickToolSize(toolName, nextSize) {
    const parsed = Math.max(1, Number(nextSize || 0));
    if (String(toolName || "") === "highlighter") {
      setHighlighterSize(parsed);
      return;
    }
    setBrushSize(parsed);
  }

  function acknowledgePersistedVersion(serverVersion) {
    const acknowledgedVersion = Number(serverVersion);
    if (!Number.isFinite(acknowledgedVersion)) return;

    setLocalBoardState((prev) => {
      const normalized = normalizeBoardState(prev);
      const currentVersion = Number(normalized.version || 1);
      if (acknowledgedVersion <= currentVersion) return prev;
      const next = {
        ...normalized,
        version: acknowledgedVersion,
        updatedAt: Date.now(),
      };
      latestBoardStateRef.current = next;
      return next;
    });

    setRoom((prev) => {
      if (!prev?.boardState) return prev;
      const normalized = normalizeBoardState(prev.boardState);
      if (acknowledgedVersion <= Number(normalized.version || 1)) return prev;
      return {
        ...prev,
        boardState: {
          ...normalized,
          version: acknowledgedVersion,
          updatedAt: Date.now(),
        },
      };
    });
  }

  async function persistBoardState(nextState, retryContext = null, delta = null, opMeta = null, targetRoomCode = roomCode) {
    const startedAt = Date.now();
    lastPersistErrorRef.current = "";
    const normalizedTargetRoomCode = String(targetRoomCode || "").trim().toUpperCase();
    const isStillActiveRoom = () => activeRoomCodeRef.current === normalizedTargetRoomCode;
    if (!normalizedTargetRoomCode || !isStillActiveRoom()) {
      lastPersistErrorRef.current = "room_changed";
      return false;
    }
    const baseVersion = Number(nextState?.version || 1) - 1;
    const pageWindowMode = pageWindowModeRef.current === true;
    const isDrawAddPersist =
      opMeta?.kind === "draw" &&
      isRebasableElementAddDelta(delta);
    const updateAckTimeoutMs = isDrawAddPersist
      ? DRAW_UPDATE_ACK_TIMEOUT_MS
      : DEFAULT_UPDATE_ACK_TIMEOUT_MS;
    const sendUpdate = async (preferDeltaOnly) => whiteboardUpdateState(normalizedTargetRoomCode, nextState, baseVersion, delta, {
      preferDeltaOnly,
      opId: opMeta?.opId,
      clientId: opMeta?.clientId,
      timeoutMs: updateAckTimeoutMs,
    });

    // Zawsze próbujemy najpierw lekką ścieżkę delta-only.
    // Full state jest fallbackiem awaryjnym, gdy delta nie przechodzi.
    let res = await sendUpdate(true);
    if (!isStillActiveRoom()) {
      lastPersistErrorRef.current = "room_changed";
      return false;
    }
    if (
      !res?.ok &&
      !pageWindowMode &&
      res?.error !== "stale_state" &&
      res?.error !== "forbidden" &&
      res?.error !== "not_authenticated" &&
      !isBoardSizePersistError(res?.error) &&
      !isTransientPersistError(res?.error)
    ) {
      // Compatibility fallback: if delta-only failed (e.g. unsupported delta/apply mismatch),
      // retry once with full state payload.
      res = await sendUpdate(false);
      if (!isStillActiveRoom()) {
        lastPersistErrorRef.current = "room_changed";
        return false;
      }
    }
    if (!res?.ok) {
      lastPersistErrorRef.current = String(res?.error || "update_failed");
      logWhiteboardPersistIssue({
        error: lastPersistErrorRef.current,
        kind: String(opMeta?.kind || ""),
        baseVersion,
        delta: getDeltaDebugSummary(delta),
        pageWindowMode,
      });
      if (isBoardSizePersistError(res?.error)) {
        setStorageLimitError({
          error: String(res?.error || "state_too_large"),
          maxBytes: Number(res?.maxBytes || 8 * 1024 * 1024),
          currentBytes: Number(res?.currentBytes || 0),
        });
        setHardSyncActive(false);
        if (
          socket.connected &&
          (connectionBannerStateRef.current === "reconnecting" || connectionBannerStateRef.current === "syncing")
        ) {
          setConnectionBannerState(null);
        }
        recoveryNeededRef.current = true;
        return false;
      }
      if (res?.error === "stale_state") {
        recoveryNeededRef.current = true;
        const currentServerState = normalizeBoardState(res?.currentState);

        // Try the light replay before replacing local state with the server snapshot.
        // This avoids a visible "stroke appears and disappears" flicker on Firefox/slow links.
        if (delta && typeof delta === "object") {
          const replayRes = await whiteboardUpdateState(
            normalizedTargetRoomCode,
            currentServerState,
            Number(currentServerState.version || 1),
            delta,
            {
              preferDeltaOnly: true,
              opId: opMeta?.opId,
              clientId: opMeta?.clientId,
              timeoutMs: updateAckTimeoutMs,
            }
          );
          if (!isStillActiveRoom()) {
            lastPersistErrorRef.current = "room_changed";
            return false;
          }
          if (replayRes?.ok) {
            lastPersistErrorRef.current = "";
            const replayed = applyDeltaToBoardState(currentServerState, delta);
            if (replayed) {
              replayed.version = Number(replayRes.serverVersion || Number(currentServerState.version || 1) + 1);
              replayed.updatedAt = Date.now();
              latestBoardStateRef.current = replayed;
              setLocalBoardState(replayed);
              setRoom((prev) => (prev ? { ...prev, boardState: replayed } : prev));
            }
            return true;
          }
          lastPersistErrorRef.current = String(replayRes?.error || "replay_failed");
          logWhiteboardPersistIssue({
            error: lastPersistErrorRef.current,
            phase: "stale_replay",
            kind: String(opMeta?.kind || ""),
            baseVersion: Number(currentServerState.version || 1),
            delta: getDeltaDebugSummary(delta),
          });
          if (isTransientPersistError(replayRes?.error)) {
            return false;
          }
        }

        setRoom((prev) => (prev ? { ...prev, boardState: currentServerState } : prev));
        setRemoteDraftsByUser({});
        applyIncomingBoardState(currentServerState, { force: true, allowSameVersion: true });

        if (retryContext) {
          const rebased = computeNextBoardState(
            currentServerState,
            retryContext.normalizedIncoming,
            retryContext.options,
            String(me?._id || "")
          );
          if (rebased) {
            latestBoardStateRef.current = rebased;
            setLocalBoardState(rebased);
            const retryDelta = buildDeltaPayload(currentServerState, rebased, retryContext.options);
            const retryRes = await whiteboardUpdateState(
              normalizedTargetRoomCode,
              rebased,
              Number(currentServerState.version || 1),
              retryDelta,
              {
                preferDeltaOnly: true,
                opId: opMeta?.opId,
                clientId: opMeta?.clientId,
                timeoutMs: updateAckTimeoutMs,
              }
            );
            if (!isStillActiveRoom()) {
              lastPersistErrorRef.current = "room_changed";
              return false;
            }
            if (retryRes?.ok) {
              lastPersistErrorRef.current = "";
              return true;
            }
          }
        }
        await requestRecoverySyncThrottled(1200, { roomCode: normalizedTargetRoomCode });
        return false;
      }

      if (isTransientPersistError(res?.error)) {
        recoveryNeededRef.current = true;
        return false;
      }

      setErr(res?.error || "update_failed");
      recoveryNeededRef.current = true;
      await requestRecoverySyncThrottled(1800, { roomCode: normalizedTargetRoomCode });
      return false;
    }
    const tookMs = Math.max(1, Date.now() - startedAt);
    const prevEma = Math.max(20, Number(persistLatencyEmaRef.current || tookMs));
    persistLatencyEmaRef.current = Math.round(prevEma * 0.82 + tookMs * 0.18);
    lastPersistErrorRef.current = "";
    setStorageLimitError(null);
    const currentBytes = Number(res?.currentBytes || 0);
    const maxBytes = Number(res?.maxBytes || 0);
    if (
      Number.isFinite(currentBytes) &&
      Number.isFinite(maxBytes) &&
      currentBytes > 0 &&
      maxBytes > 0 &&
      currentBytes / maxBytes >= BOARD_STORAGE_WARNING_RATIO
    ) {
      setStorageUsageWarning({ currentBytes, maxBytes });
    } else if (maxBytes > 0) {
      setStorageUsageWarning(null);
    }
    acknowledgePersistedVersion(res?.serverVersion);
    return true;
  }

  function queuePersist(nextState, retryContext = null, delta = null, opMeta = null) {
    const nextJob = {
      nextState,
      retryContext,
      delta,
      opMeta,
      roomCode,
      baseVersion: Number(nextState?.version || 1) - 1,
      attempts: 0,
      createdAt: Date.now(),
    };

    // Kolejka FIFO: nie gubimy operacji po drodze (ważne przy równoległym pisaniu).
    // Zostawiamy tylko bezpieczny limit, żeby uniknąć niekontrolowanego wzrostu pamięci.
    persistQueueRef.current = [...persistQueueRef.current, nextJob];
    if (persistQueueRef.current.length > PERSIST_QUEUE_MAX) {
      if (persistInFlightRef.current && persistQueueRef.current.length > 1) {
        const active = persistQueueRef.current[0];
        const pendingTail = persistQueueRef.current.slice(-(PERSIST_QUEUE_MAX - 1));
        persistQueueRef.current = [active, ...pendingTail];
      } else {
        persistQueueRef.current = persistQueueRef.current.slice(-PERSIST_QUEUE_MAX);
      }
    }

    maybeWarnAboutSlowDrawQueue();
    schedulePersistFlush();
  }

  async function flushPersistQueue() {
    if (persistInFlightRef.current) return;
    clearPersistFlushTimer();
    persistInFlightRef.current = true;
    try {
      while (persistQueueRef.current.length > 0) {
        const job = persistQueueRef.current[0];
        if (String(job?.roomCode || "") !== activeRoomCodeRef.current) {
          persistQueueRef.current.shift();
          continue;
        }
        const isDurableDrawAdd = isDurableDrawPersistJob(job);
        maybeWarnAboutSlowDrawQueue();

        let slowDrawTimer = null;
        let hardDrawTimer = null;
        if (isDurableDrawAdd) {
          const ageMs = getPersistJobAgeMs(job);
          slowDrawTimer = setTimeout(() => {
            if (!isSamePersistJob(persistQueueRef.current?.[0], job)) return;
            if (String(job?.roomCode || "") !== activeRoomCodeRef.current) return;
            setConnectionBannerState((prev) => {
              if (prev === "offline" || prev === "reconnecting") return prev;
              return socket.connected ? "syncing" : "reconnecting";
            });
          }, Math.max(0, DRAW_SYNC_SOFT_WARNING_MS - ageMs));
          hardDrawTimer = setTimeout(() => {
            if (!isSamePersistJob(persistQueueRef.current?.[0], job)) return;
            if (String(job?.roomCode || "") !== activeRoomCodeRef.current) return;
            setHardSyncActive(true);
          }, Math.max(0, DRAW_SYNC_HARD_BLOCK_MS - ageMs));
        }

        let ok = false;
        try {
          ok = await persistBoardState(job.nextState, job.retryContext, job.delta, job.opMeta, job.roomCode);
        } finally {
          if (slowDrawTimer) clearTimeout(slowDrawTimer);
          if (hardDrawTimer) clearTimeout(hardDrawTimer);
        }
        if (
          String(job?.roomCode || "") !== activeRoomCodeRef.current ||
          !isSamePersistJob(persistQueueRef.current?.[0], job)
        ) {
          continue;
        }
        if (ok) {
          persistQueueRef.current.shift();
          if (persistQueueRef.current.length === 0) {
            setHardSyncActive(false);
            if (
              connectionBannerStateRef.current === "reconnecting" ||
              connectionBannerStateRef.current === "syncing"
            ) {
              showConnectedBannerBriefly();
            }
          }
          continue;
        }

        const persistError = String(lastPersistErrorRef.current || "");
        if (isBoardSizePersistError(persistError)) {
          const failedRoomCode = String(job?.roomCode || "");
          persistQueueRef.current = persistQueueRef.current.filter(
            (queuedJob) => String(queuedJob?.roomCode || "") !== failedRoomCode
          );
          setHardSyncActive(false);
          recoveryPullLastAtRef.current = 0;
          await requestRecoverySyncThrottled(600, { force: true, roomCode: failedRoomCode });
          continue;
        }
        const isTransientDrawFailure = isDurableDrawAdd && isTransientPersistError(persistError);
        const maxAttempts = isTransientDrawFailure ? Number.POSITIVE_INFINITY : (isDurableDrawAdd ? 20 : 8);
        const maxBackoffMs = isTransientDrawFailure ? 6500 : (isDurableDrawAdd ? 5000 : 2200);
        const nextAttempts = Number(job.attempts || 0) + 1;
        const jobAgeMs = getPersistJobAgeMs(job);
        if (nextAttempts <= maxAttempts) {
          persistQueueRef.current[0] = {
            ...job,
            attempts: nextAttempts,
          };
          if (isTransientDrawFailure && (nextAttempts >= 1 || jobAgeMs >= DRAW_SYNC_SOFT_WARNING_MS)) {
            setConnectionBannerState(socket.connected ? "syncing" : "reconnecting");
          }
          if (
            isTransientDrawFailure &&
            (nextAttempts >= DRAW_SYNC_HARD_ATTEMPT_COUNT || jobAgeMs >= DRAW_SYNC_HARD_BLOCK_MS)
          ) {
            setHardSyncActive(true);
          }
          await new Promise((resolve) => setTimeout(resolve, Math.min(maxBackoffMs, 300 * nextAttempts)));
          continue;
        }

        // Drop only this single op after max retries and continue with next ops.
        persistQueueRef.current.shift();
        await runHardRecoverySync("persist_retries_exceeded", job.roomCode);
      }
    } finally {
      persistInFlightRef.current = false;
      if (persistQueueRef.current.length > 0 && !persistInFlightRef.current) {
        schedulePersistFlush();
      }
    }
  }

  async function handleBoardChange(nextState, options = {}) {
    if (archiveReadOnly) return false;
    const baseState = normalizeBoardState(latestBoardStateRef.current);
    const incoming = nextState && typeof nextState === "object" ? nextState : {};
    const appendedDrawElement =
      String(options?.kind || "") === "draw" &&
      options?.appendedElement &&
      typeof options.appendedElement === "object"
        ? cloneElements([options.appendedElement])[0]
        : null;
    const incomingForChange = appendedDrawElement
      ? {
          ...incoming,
          elements: [...cloneElements(baseState.elements), appendedDrawElement],
        }
      : incoming;
    if (String(options?.kind || "") === "draw") {
      const baseCount = Array.isArray(baseState.elements) ? baseState.elements.length : 0;
      const incomingElements = Array.isArray(incomingForChange.elements) ? incomingForChange.elements : [];
      const addedElement = incomingElements.length > baseCount ? incomingElements[incomingElements.length - 1] : null;
      if (String(addedElement?.tool || "") === "pen" || String(addedElement?.tool || "") === "highlighter") {
        logWhiteboardPersistIssue({
          phase: "handle_draw",
          tool: String(addedElement?.tool || ""),
          baseElements: baseCount,
          incomingElements: incomingElements.length,
          points: Array.isArray(addedElement?.points) ? addedElement.points.length : 0,
        });
      }
    }
    const incomingTaskBlocks = Array.isArray(incomingForChange.taskBlocks)
      ? cloneTaskBlocks(incomingForChange.taskBlocks)
      : cloneTaskBlocks(baseState.taskBlocks);
    const incomingActiveTaskBlockId = Object.prototype.hasOwnProperty.call(incomingForChange, "activeTaskBlockId")
      ? incomingForChange.activeTaskBlockId
      : baseState.activeTaskBlockId;
    const normalizedIncoming = normalizeBoardState({
      ...incomingForChange,
      taskBlocks: incomingTaskBlocks,
      activeTaskBlockId: incomingActiveTaskBlockId,
    });
    const finalState = computeNextBoardState(baseState, normalizedIncoming, options, String(me?._id || ""));
    if (!finalState) {
      if (String(options?.kind || "") === "draw") {
        logWhiteboardPersistIssue({
          phase: "local_noop",
          kind: String(options?.kind || ""),
          baseElements: Array.isArray(baseState.elements) ? baseState.elements.length : 0,
          incomingElements: Array.isArray(normalizedIncoming.elements) ? normalizedIncoming.elements.length : 0,
        });
      }
      return;
    }
    const delta = buildDeltaPayload(baseState, finalState, options);
    if (pageWindowModeRef.current && (!delta || typeof delta !== "object")) {
      setErr("Synchronizacja tablicy: brak delty dla bieżącej operacji. Odświeżam stan.");
      recoveryNeededRef.current = true;
      void requestRecoverySyncThrottled(900);
      return;
    }

    latestBoardStateRef.current = finalState;
    setLocalBoardState(finalState);
    lastLocalEditAtRef.current = Date.now();
    if (demoMode) {
      writeDemoBoardState(finalState);
      setRoom((prev) => createDemoRoom(roomCode, finalState));
      return;
    }
    if (isApplyingRemoteRef.current) return;
    operationCounterRef.current = Number(operationCounterRef.current || 0) + 1;
    const opMeta = {
      clientId: String(clientIdRef.current || ""),
      opId: makeOperationId(operationCounterRef.current),
      kind: String(options?.kind || ""),
    };
    queuePersist(finalState, { normalizedIncoming, options }, delta, opMeta);
    scheduleConsistencyAudit(3200, 7000);
  }

  function handleDraftChange(draft) {
    // Live preview draft intentionally disabled to reduce socket traffic/flicker.
    void draft;
  }

  function flushCursorNow(point) {
    if (demoMode) return;
    cursorThrottleRef.current.lastSentAt = Date.now();
    whiteboardSendCursor(roomCode, point).catch(() => {});
  }

  function handleCursorMove(point) {
    if (point && Number.isFinite(Number(point.x)) && Number.isFinite(Number(point.y))) {
      lastLocalCursorPointRef.current = {
        x: Number(point.x),
        y: Number(point.y),
      };
    }

    const now = Date.now();
    const latencyEma = Math.max(20, Number(persistLatencyEmaRef.current || 90));
    const throttleMs = Math.max(100, Math.min(260, Math.round(120 + Math.max(0, latencyEma - 80) * 0.6)));
    const hideDelayMs = Math.max(180, Math.min(360, throttleMs + 90));
    const ref = cursorThrottleRef.current;

    if (!point) {
      if (ref.hideTimer) clearTimeout(ref.hideTimer);
      ref.hideTimer = setTimeout(() => {
        ref.hideTimer = null;
        if (ref.timer) {
          clearTimeout(ref.timer);
          ref.timer = null;
        }
        ref.queued = null;
        flushCursorNow(null);
      }, hideDelayMs);
      return;
    }

    if (ref.hideTimer) {
      clearTimeout(ref.hideTimer);
      ref.hideTimer = null;
    }

    const elapsed = now - ref.lastSentAt;
    if (elapsed >= throttleMs) {
      flushCursorNow(point);
      return;
    }

    ref.queued = point;
    if (ref.timer) return;

    ref.timer = setTimeout(() => {
      ref.timer = null;
      if (!ref.queued) return;
      const queuedPoint = ref.queued;
      ref.queued = null;
      flushCursorNow(queuedPoint);
    }, throttleMs - elapsed);
  }

  function requestClear() {
    if (!localBoardState.elements.length) return;
    setClearOpen(true);
  }

  async function confirmClear() {
    await handleBoardChange(
      {
        ...localBoardState,
        elements: [],
      },
      { kind: "clear" }
    );
    setClearOpen(false);
  }

  async function handleUndo() {
    await handleBoardChange(localBoardState, { history: "undo" });
  }

  async function handleRedo() {
    await handleBoardChange(localBoardState, { history: "redo" });
  }

  async function handleAddPage() {
    if (Number(localBoardState.pageCount || DEFAULT_PAGE_COUNT) >= MAX_PAGE_COUNT) return;
    await handleBoardChange(
      {
        ...localBoardState,
        pageCount: Number(localBoardState.pageCount || DEFAULT_PAGE_COUNT) + 1,
      },
      { history: "skip", kind: "page_count" }
    );
  }

  async function insertImageFromFile(file) {
    if (!file || !String(file.type || "").startsWith("image/")) return;
    if (Number(file.size || 0) > MAX_IMAGE_FILE_BYTES) {
      setErr("Obraz jest za duży. Maksymalny rozmiar pliku to 3 MB.");
      return;
    }
    try {
      const optimized = await optimizeImageDataUrl(file);
      const src = optimized.src;
      if (src.length > MAX_IMAGE_DATA_URL_CHARS) {
        setErr("Obraz po wczytaniu jest zbyt ciężki dla tablicy. Użyj mniejszego pliku.");
        return;
      }
      const size = optimized.size || (await getImageSize(src));
      const maxWidth = Math.round(BOARD_WIDTH * 0.62);
      const sourceWidth = Math.max(1, Number(size.width || 1));
      const sourceHeight = Math.max(1, Number(size.height || 1));
      const downscaleRatio = Math.min(1, maxWidth / sourceWidth);
      const width = Math.max(1, Math.round(sourceWidth * downscaleRatio));
      const height = Math.max(1, Math.round(sourceHeight * downscaleRatio));
      const pageIndex = Math.max(0, Math.min(Number(localBoardState.pageCount || DEFAULT_PAGE_COUNT) - 1, currentCanvasPage));
      const pageTop = pageIndex * PAGE_HEIGHT;
      const pageBottom = pageTop + PAGE_HEIGHT;
      const cursorPoint = lastLocalCursorPointRef.current;
      const cursorInsideCurrentPage = !!(
        cursorPoint &&
        Number.isFinite(Number(cursorPoint.x)) &&
        Number.isFinite(Number(cursorPoint.y)) &&
        Number(cursorPoint.y) >= pageTop &&
        Number(cursorPoint.y) <= pageBottom
      );
      const targetCenterX = cursorInsideCurrentPage ? Number(cursorPoint.x) : BOARD_WIDTH / 2;
      const targetCenterY = cursorInsideCurrentPage ? Number(cursorPoint.y) : pageTop + 140;
      const x = Math.max(0, Math.min(BOARD_WIDTH - width, Math.round(targetCenterX - width / 2)));
      const y = Math.max(pageTop, Math.min(pageBottom - height, Math.round(targetCenterY - height / 2)));

      await handleBoardChange({
        ...latestBoardStateRef.current,
        elements: [
          ...cloneElements(latestBoardStateRef.current.elements),
          {
            id: `image_${Date.now()}_${Math.random().toString(16).slice(2)}`,
            type: "image",
            createdBy: myUserId || undefined,
            src,
            x,
            y,
            width,
            height,
            naturalWidth: size.width || width,
            naturalHeight: size.height || height,
          },
        ],
      }, { kind: "draw" });
    } catch (e) {
      setErr(String(e?.message || "image_insert_failed"));
    }
  }

  function openImagePicker() {
    imageInputRef.current?.click();
  }

  function parseGraphRangeFields(fields) {
    const xmin = Number(fields?.xmin);
    const xmax = Number(fields?.xmax);
    const ymin = Number(fields?.ymin);
    const ymax = Number(fields?.ymax);
    if (![xmin, xmax, ymin, ymax].every(Number.isFinite) || xmin >= xmax || ymin >= ymax) {
      return { x: [-10, 10], y: [-10, 10] };
    }
    return { x: [xmin, xmax], y: [ymin, ymax] };
  }

  function formatGraphRangeFields(range) {
    const x = Array.isArray(range?.x) ? range.x : [-10, 10];
    const y = Array.isArray(range?.y) ? range.y : [-10, 10];
    return {
      xmin: Number(x[0] ?? -10),
      xmax: Number(x[1] ?? 10),
      ymin: Number(y[0] ?? -10),
      ymax: Number(y[1] ?? 10),
    };
  }

  function updateGraphRangeField(key, value) {
    setGraphRangeFields((fields) => ({
      ...fields,
      [key]: value === "" ? "" : Number(value),
    }));
  }

  function getGraphPointLabels() {
    const labels = [];
    graphPoints.forEach((pointInput) => {
      const point = parseGraphPointInput(pointInput);
      if (point?.label && !labels.includes(point.label)) labels.push(point.label);
    });
    return labels;
  }

  function addGraphSegment() {
    const labels = getGraphPointLabels();
    if (labels.length < 2) return;
    setGraphSegments((items) => [...items, `${labels[0]}-${labels[1]}`]);
  }

  function updateGraphSegmentEndpoint(index, endpoint, value) {
    const parsed = parseGraphSegmentEndpointsLoose(graphSegments[index]);
    const a = endpoint === "a" ? value : parsed.a;
    const b = endpoint === "b" ? value : parsed.b;
    const next = [...graphSegments];
    next[index] = `${a}-${b}`;
    setGraphSegments(next);
  }

  function insertGraphInputToken(index, token) {
    setGraphInputs((items) => {
      const next = [...items];
      const input = graphInputRefs.current[index];
      const current = String(next[index] || "");
      const start = Number.isInteger(input?.selectionStart) ? input.selectionStart : current.length;
      const end = Number.isInteger(input?.selectionEnd) ? input.selectionEnd : start;
      next[index] = `${current.slice(0, start)}${token}${current.slice(end)}`;
      window.setTimeout(() => {
        input?.focus();
        const nextCursor = token === "√()" ? start + 2 : start + token.length;
        input?.setSelectionRange?.(nextCursor, nextCursor);
      }, 0);
      return next;
    });
  }

  function requestGraphItemDelete(kind, index) {
    setGraphDeleteTarget({ kind, index });
  }

  function cancelGraphItemDelete() {
    setGraphDeleteTarget(null);
  }

  function confirmGraphItemDelete() {
    const kind = String(graphDeleteTarget?.kind || "");
    const index = Number(graphDeleteTarget?.index);
    if (!Number.isInteger(index) || index < 0) {
      setGraphDeleteTarget(null);
      return;
    }

    if (kind === "input") {
      setGraphInputs((items) => items.filter((_, i) => i !== index));
    } else if (kind === "point") {
      const removedLabel = parseGraphPointInput(graphPoints[index])?.label || "";
      setGraphPoints((items) => items.filter((_, i) => i !== index));
      if (removedLabel) {
        setGraphSegments((items) => items.filter((segment) => {
          const parsed = parseGraphSegmentInput(segment);
          return parsed?.a !== removedLabel && parsed?.b !== removedLabel;
        }));
      }
    } else if (kind === "segment") {
      setGraphSegments((items) => items.filter((_, i) => i !== index));
    }
    setGraphDeleteTarget(null);
  }

  function graphDeleteLabel(kind) {
    if (kind === "input") return "wzór";
    if (kind === "point") return "punkt";
    if (kind === "segment") return "odcinek";
    return "element";
  }

  function openTextCreator() {
    setErr("");
    setTextEditTargetId("");
    setTextInput("");
    setTextPickerOpen(true);
  }

  function openTextEditor(element, elementId) {
    setErr("");
    setTextEditTargetId(String(elementId || element?.id || ""));
    setTextInput(String(element?.textWidget?.text || ""));
    setTextPickerOpen(true);
  }

  async function insertTextWidget() {
    const text = String(textInput || "").trim();
    if (!text) {
      setErr("Wpisz tekst, który chcesz dodać do tablicy.");
      return;
    }

    try {
      const existingElement = textEditTargetId
        ? latestBoardStateRef.current.elements.find((element) => String(element?.id || "") === String(textEditTargetId))
        : null;
      const textColor = String(existingElement?.textWidget?.color || penColor || "#f8fafc");
      const fontSize = Number(existingElement?.textWidget?.fontSize || 36);
      const fontWeight = Number(existingElement?.textWidget?.fontWeight || 400);
      const { src, layout } = createTextWidgetSvgDataUrl({ text, color: textColor, fontSize, fontWeight });
      const textPayload = {
        version: 1,
        text,
        color: textColor,
        fontFamily: "Rubik",
        fontSize,
        fontWeight,
      };

      if (textEditTargetId) {
        const targetId = String(textEditTargetId);
        const nextElements = cloneElements(latestBoardStateRef.current.elements).map((element) => {
          if (String(element?.id || "") !== targetId) return element;
          const width = Math.max(1, Number(element?.width || layout.svgWidth));
          return {
            ...element,
            src,
            width,
            height: Math.max(1, Math.round(width * (layout.svgHeight / layout.svgWidth))),
            naturalWidth: layout.svgWidth,
            naturalHeight: layout.svgHeight,
            textWidget: textPayload,
          };
        });
        await handleBoardChange({
          ...latestBoardStateRef.current,
          elements: nextElements,
        }, { kind: "image_transform" });
      } else {
        const maxDefaultWidth = 620;
        const scale = Math.min(1, maxDefaultWidth / Math.max(1, layout.svgWidth));
        const width = Math.max(80, Math.round(layout.svgWidth * scale));
        const height = Math.max(40, Math.round(layout.svgHeight * scale));
        const pageIndex = Math.max(0, Math.min(Number(localBoardState.pageCount || DEFAULT_PAGE_COUNT) - 1, currentCanvasPage));
        const pageTop = pageIndex * PAGE_HEIGHT;
        const pageBottom = pageTop + PAGE_HEIGHT;
        const cursorPoint = lastLocalCursorPointRef.current;
        const cursorInsideCurrentPage = !!(
          cursorPoint &&
          Number.isFinite(Number(cursorPoint.x)) &&
          Number.isFinite(Number(cursorPoint.y)) &&
          Number(cursorPoint.y) >= pageTop &&
          Number(cursorPoint.y) <= pageBottom
        );
        const targetCenterX = cursorInsideCurrentPage ? Number(cursorPoint.x) : BOARD_WIDTH / 2;
        const targetCenterY = cursorInsideCurrentPage ? Number(cursorPoint.y) : pageTop + 140;
        const x = Math.max(0, Math.min(BOARD_WIDTH - width, Math.round(targetCenterX - width / 2)));
        const y = Math.max(pageTop, Math.min(pageBottom - height, Math.round(targetCenterY - height / 2)));

        await handleBoardChange({
          ...latestBoardStateRef.current,
          elements: [
            ...cloneElements(latestBoardStateRef.current.elements),
            {
              id: `text_${Date.now()}_${Math.random().toString(16).slice(2)}`,
              type: "image",
              createdBy: myUserId || undefined,
              src,
              x,
              y,
              width,
              height,
              naturalWidth: layout.svgWidth,
              naturalHeight: layout.svgHeight,
              textWidget: textPayload,
            },
          ],
        }, { kind: "draw" });
      }

      setTextPickerOpen(false);
      setTextEditTargetId("");
      setTool("select");
    } catch (e) {
      setErr(String(e?.message || "text_insert_failed"));
    }
  }

  function openGraphCreator() {
    setGraphEditTargetId("");
    setGraphInputs(["f(x)=x^2-4"]);
    setGraphPoints([]);
    setGraphSegments([]);
    setGraphRangeFields({ xmin: -10, xmax: 10, ymin: -10, ymax: 10 });
    setGraphThemeChoice(resolveDefaultGraphTheme(themeMode, boardSurface));
    setGraphPickerOpen(true);
  }

  function openGraphEditor(element, elementId) {
    const graph = element?.graph || {};
    setGraphEditTargetId(String(elementId || element?.id || ""));
    setGraphInputs(Array.isArray(graph.inputs) && graph.inputs.length ? graph.inputs.map((x) => String(x || "")) : [""]);
    setGraphPoints(Array.isArray(graph.points) ? graph.points.map((x) => String(x || "")) : []);
    setGraphSegments(Array.isArray(graph.segments) ? graph.segments.map((x) => String(x || "")) : []);
    setGraphRangeFields(formatGraphRangeFields(graph.range));
    setGraphThemeChoice(String(graph.themeMode || resolveDefaultGraphTheme(themeMode, boardSurface)) === "light" ? "light" : "dark");
    setGraphPickerOpen(true);
  }

  async function insertGraphWidget() {
    try {
      const cleanInputs = graphInputs.map((input) => String(input || "").trim()).filter(Boolean);
      const cleanPoints = graphPoints.map((input) => String(input || "").trim()).filter(Boolean);
      const cleanSegments = graphSegments.map((input) => String(input || "").trim()).filter(Boolean);
      if (!cleanInputs.length && !cleanPoints.length) {
        setErr("Wpisz wzór wykresu albo dodaj punkt.");
        return;
      }
      const range = parseGraphRangeFields(graphRangeFields);
      const src = createGraphSvgDataUrl({
        inputs: cleanInputs,
        points: cleanPoints,
        segments: cleanSegments,
        range,
        themeMode: graphThemeChoice,
      });
      const graphSvgSize = getGraphSvgLayout(cleanInputs.length);
      const defaultWidth = 520;
      const defaultHeight = Math.round(defaultWidth * (graphSvgSize.svgHeight / graphSvgSize.svgWidth));
      const graphPayload = {
        version: 1,
        inputs: cleanInputs,
        points: cleanPoints,
        segments: cleanSegments,
        range,
        themeMode: graphThemeChoice,
      };

      if (graphEditTargetId) {
        const targetId = String(graphEditTargetId);
        const nextElements = cloneElements(latestBoardStateRef.current.elements).map((element) => {
          if (String(element?.id || "") !== targetId) return element;
          return {
            ...element,
            src,
            width: Number(element?.width || defaultWidth),
            height: Math.max(1, Math.round(Number(element?.width || defaultWidth) * (graphSvgSize.svgHeight / graphSvgSize.svgWidth))),
            naturalWidth: graphSvgSize.svgWidth,
            naturalHeight: graphSvgSize.svgHeight,
            graph: graphPayload,
          };
        });
        await handleBoardChange({
          ...latestBoardStateRef.current,
          elements: nextElements,
        }, { kind: "image_transform" });
        setGraphEditTargetId("");
        setGraphPickerOpen(false);
        setTool("select");
        return;
      }

      const pageIndex = Math.max(0, Math.min(Number(localBoardState.pageCount || DEFAULT_PAGE_COUNT) - 1, currentCanvasPage));
      const pageTop = pageIndex * PAGE_HEIGHT;
      const pageBottom = pageTop + PAGE_HEIGHT;
      const cursorPoint = lastLocalCursorPointRef.current;
      const cursorInsideCurrentPage = !!(
        cursorPoint &&
        Number.isFinite(Number(cursorPoint.x)) &&
        Number.isFinite(Number(cursorPoint.y)) &&
        Number(cursorPoint.y) >= pageTop &&
        Number(cursorPoint.y) <= pageBottom
      );
      const targetCenterX = cursorInsideCurrentPage ? Number(cursorPoint.x) : BOARD_WIDTH / 2;
      const targetCenterY = cursorInsideCurrentPage ? Number(cursorPoint.y) : pageTop + 180;
      const width = defaultWidth;
      const height = defaultHeight;
      const x = Math.max(0, Math.min(BOARD_WIDTH - width, Math.round(targetCenterX - width / 2)));
      const y = Math.max(pageTop, Math.min(pageBottom - height, Math.round(targetCenterY - height / 2)));

      await handleBoardChange({
        ...latestBoardStateRef.current,
        elements: [
          ...cloneElements(latestBoardStateRef.current.elements),
          {
            id: `graph_${Date.now()}_${Math.random().toString(16).slice(2)}`,
            type: "image",
            createdBy: myUserId || undefined,
            src,
            x,
            y,
            width,
            height,
            naturalWidth: graphSvgSize.svgWidth,
            naturalHeight: graphSvgSize.svgHeight,
            graph: graphPayload,
          },
        ],
      }, { kind: "draw" });
      setGraphPickerOpen(false);
      setTool("select");
    } catch (e) {
      setErr(String(e?.message || "graph_insert_failed"));
    }
  }

  async function handleImageInputChange(e) {
    const file = e.target.files?.[0];
    if (file) await insertImageFromFile(file);
    e.target.value = "";
  }

  async function ensureTaskAdapter(subtopicId) {
    const key = String(subtopicId || "");
    if (!key) return null;
    if (taskAdapterCacheRef.current[key]) {
      setTaskAdapterStatus((prev) => ({ ...prev, [key]: { state: "loaded", error: "" } }));
      return taskAdapterCacheRef.current[key];
    }
    if (taskAdapterLoadsRef.current[key]) return taskAdapterLoadsRef.current[key];

    const loader = lessonLoaders?.[key];
    if (typeof loader !== "function") {
      setTaskAdapterStatus((prev) => ({
        ...prev,
        [key]: { state: "error", error: "Brak adaptera zadań dla wybranego podtematu." },
      }));
      throw new Error("Brak adaptera zadań dla wybranego podtematu.");
    }

    const loadOnce = async () => {
      const lessonModule = await withTimeout(
        loader(),
        TASK_ADAPTER_LOAD_TIMEOUT_MS,
        "Ładowanie adaptera zadania trwało zbyt długo."
      );
      const lessonEntries = Array.isArray(lessonModule?.default) ? lessonModule.default : null;
      const lessonTaskComponent = lessonEntries?.[1];

      if (typeof lessonTaskComponent !== "function") {
        throw new Error("Nie udało się odczytać modułu zadań dla podtematu.");
      }

      let element = null;
      try {
        element = lessonTaskComponent({});
      } catch {
        element = null;
      }

      const props = element?.props || {};
      const adapter = {
        sectionTitle: String(props.sectionTitle || ""),
        generateTasks: typeof props.generateTasks === "function" ? props.generateTasks : null,
        renderTask: typeof props.renderTask === "function" ? props.renderTask : null,
        renderAnswer: typeof props.renderAnswer === "function" ? props.renderAnswer : null,
        renderExplanation: typeof props.renderExplanation === "function" ? props.renderExplanation : null,
        generationModes: Array.isArray(props.generationModes)
          ? props.generationModes.map((mode) => ({
              value: String(mode?.value || ""),
              label: String(mode?.label || mode?.value || ""),
              generationOptions:
                mode?.generationOptions && typeof mode.generationOptions === "object"
                  ? { ...mode.generationOptions }
                  : null,
            }))
          : [],
        defaultGenerationMode: String(props.defaultGenerationMode || "random"),
      };

      if (!adapter.generateTasks || !adapter.renderTask || !adapter.renderAnswer || !adapter.renderExplanation) {
        throw new Error("Wybrany podtemat nie obsługuje jeszcze pełnego widoku zadania.");
      }

      return adapter;
    };

    const loadPromise = (async () => {
      setTaskAdapterStatus((prev) => ({ ...prev, [key]: { state: "loading", error: "" } }));
      try {
        let adapter = null;
        try {
          adapter = await loadOnce();
        } catch {
          await delay(700);
          adapter = await loadOnce();
        }
        taskAdapterCacheRef.current[key] = adapter;
        setTaskAdapters((prev) => ({ ...prev, [key]: adapter }));
        setTaskAdapterStatus((prev) => ({ ...prev, [key]: { state: "loaded", error: "" } }));
        return adapter;
      } catch (e) {
        const message = String(e?.message || "Nie udało się załadować adaptera zadania.");
        setTaskAdapterStatus((prev) => ({ ...prev, [key]: { state: "error", error: message } }));
        throw e;
      } finally {
        delete taskAdapterLoadsRef.current[key];
      }
    })();

    taskAdapterLoadsRef.current[key] = loadPromise;
    return loadPromise;
  }

  function retryTaskAdapter(subtopicId) {
    const key = String(subtopicId || "");
    if (!key) return;
    delete taskAdapterCacheRef.current[key];
    delete taskAdapterLoadsRef.current[key];
    setTaskAdapters((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    void ensureTaskAdapter(key).catch(() => {});
  }

  async function ensureTaskAdaptersForBlocks(blocks) {
    const uniqueIds = Array.from(
      new Set(
        (Array.isArray(blocks) ? blocks : [])
          .map((block) => String(block?.subtopicId || ""))
          .filter(Boolean)
      )
    );

    for (const subtopicId of uniqueIds) {
      if (taskAdapterCacheRef.current[subtopicId]) continue;
      try {
        // eslint-disable-next-line no-await-in-loop
        await ensureTaskAdapter(subtopicId);
      } catch {
        // Ignore adapter load errors for stale blocks.
      }
    }
  }

  function replaceTaskBlock(blockId, updater) {
    const blocks = cloneTaskBlocks(latestBoardStateRef.current?.taskBlocks || []);
    const idx = blocks.findIndex((block) => String(block?.id) === String(blockId));
    if (idx < 0) return null;
    const nextBlock = updater(blocks[idx]);
    if (!nextBlock) return null;
    blocks[idx] = nextBlock;
    return blocks;
  }

  async function updateTaskBlocks(nextBlocks, nextActiveTaskBlockId) {
    const resolvedActiveTaskBlockId = resolveActiveTaskBlockId(
      nextBlocks,
      typeof nextActiveTaskBlockId === "undefined"
        ? latestBoardStateRef.current?.activeTaskBlockId
        : nextActiveTaskBlockId
    );
    await handleBoardChange(
      {
        ...latestBoardStateRef.current,
        taskBlocks: cloneTaskBlocks(nextBlocks),
        activeTaskBlockId: resolvedActiveTaskBlockId,
      },
      { history: "skip", kind: "task_blocks" }
    );
  }

  async function handleAddTaskBlock() {
    const subtopicId = String(activeTaskSubtopicId || "");
    if (!subtopicId) {
      setErr("Najpierw wybierz podtemat zadania.");
      return;
    }

    try {
      setTaskPickerBusy(true);
      const adapter = await ensureTaskAdapter(subtopicId);
      const choice = taskChoices.find((item) => item.key === taskChoiceKey) || taskChoices[0] || null;
      const generated = generateTasksForChoice(adapter, choice || { generationMode: taskGenerationMode });
      const task = pickTaskByChoice(generated, choice);
      if (!task) throw new Error("Nie udało się wygenerować zadania dla wybranego podtematu.");

      const sectionLabel = selectedSectionOption?.label || "";
      const topicLabel = selectedTopicOption?.name || "";
      const subtopicLabel =
        selectedSubtopicOptions.find((subtopic) => String(subtopic.id) === subtopicId)?.name || subtopicId;

      const currentBlocks = cloneTaskBlocks(latestBoardStateRef.current?.taskBlocks || []);
      if (currentBlocks.length >= MAX_TASK_BLOCKS) {
        setTaskLimitContext("add");
        setTaskLimitOpen(true);
        return;
      }

      const newBlockId = makeTaskBlockId();
      const nextBlocks = [
        ...currentBlocks,
        {
          id: newBlockId,
          subtopicId,
          sectionLabel,
          topicLabel,
          subtopicLabel,
          taskChoice: choice
            ? {
                key: choice.key,
                index: choice.index,
                type: choice.type,
                label: choice.label,
                generationMode: choice.generationMode || "",
                generationOptions: choice.generationOptions ? { ...choice.generationOptions } : null,
              }
            : null,
          task,
          answerShown: false,
          explanationShown: false,
          collapsed: false,
          fullHeight: false,
          locked: false,
          metaExpanded: false,
          createdAt: Date.now(),
        },
      ];

      await updateTaskBlocks(nextBlocks, newBlockId);
      setTaskDockHidden(false);
      writeTaskDockHidden(roomCode, false);
      setTaskCollapsePrefs((prev) => ({ ...prev, [newBlockId]: false }));
      setTaskPickerOpen(false);
    } catch (e) {
      setErr(String(e?.message || "Nie udało się dodać zadania."));
    } finally {
      setTaskPickerBusy(false);
    }
  }

  async function handleRerollTaskBlock(blockId, options = {}) {
    const targetId = String(blockId || "");
    if (!options?.confirmed) {
      setTaskRerollTargetId(targetId);
      setTaskRerollOpen(true);
      return;
    }

    const block = taskBlocks.find((item) => String(item?.id) === targetId);
    if (!block || block.locked) return;

    try {
      const adapter = await ensureTaskAdapter(block.subtopicId);
      const generated = generateTasksForChoice(adapter, block.taskChoice);
      const task = pickTaskByChoice(generated, block.taskChoice);
      if (!task) return;

      const nextBlocks = replaceTaskBlock(blockId, (prev) => ({
        ...prev,
        task,
        answerShown: false,
        explanationShown: false,
        locked: false,
      }));
      if (!nextBlocks) return;
      await updateTaskBlocks(nextBlocks);
    } catch (e) {
      setErr(String(e?.message || "Nie udało się wylosować nowego zadania."));
    }
  }

  async function confirmTaskReroll() {
    const targetId = String(taskRerollTargetId || "");
    setTaskRerollOpen(false);
    setTaskRerollTargetId("");
    if (!targetId) return;
    await handleRerollTaskBlock(targetId, { confirmed: true });
  }

  async function handleDuplicateTaskBlock(blockId, options = {}) {
    const targetId = String(blockId || "");
    if (!options?.confirmed) {
      setTaskDuplicateTargetId(targetId);
      setTaskDuplicateOpen(true);
      return;
    }
    const blocks = cloneTaskBlocks(latestBoardStateRef.current?.taskBlocks || []);
    const idx = blocks.findIndex((item) => String(item?.id) === targetId);
    if (idx < 0) return;
    if (blocks.length >= MAX_TASK_BLOCKS) {
      setTaskLimitContext("duplicate");
      setTaskLimitOpen(true);
      return;
    }
    const source = blocks[idx];
    let clonedTask = source?.task ? JSON.parse(JSON.stringify(source.task)) : source?.task;
    try {
      const subtopicId = String(source?.subtopicId || "");
      if (subtopicId) {
        const adapter = await ensureTaskAdapter(subtopicId);
        const generated = generateTasksForChoice(adapter, source?.taskChoice);
        const rerolled = pickTaskByChoice(generated, source?.taskChoice);
        if (rerolled) clonedTask = rerolled;
      }
    } catch {
      // Fallback: keep plain clone when reroll adapter is unavailable.
    }
    const duplicated = {
      ...source,
      id: makeTaskBlockId(),
      task: clonedTask,
      answerShown: false,
      explanationShown: false,
      collapsed: false,
      fullHeight: false,
      locked: false,
      metaExpanded: false,
      createdAt: Date.now(),
    };
    const nextBlocks = [...blocks.slice(0, idx + 1), duplicated, ...blocks.slice(idx + 1)];
    await updateTaskBlocks(nextBlocks, duplicated.id);
  }

  async function confirmTaskDuplicate() {
    const targetId = String(taskDuplicateTargetId || "");
    setTaskDuplicateOpen(false);
    setTaskDuplicateTargetId("");
    if (!targetId) return;
    await handleDuplicateTaskBlock(targetId, { confirmed: true });
  }

  async function confirmTaskDelete() {
    const targetId = String(taskDeleteTargetId || "");
    setTaskDeleteOpen(false);
    setTaskDeleteTargetId("");
    if (!targetId) return;
    const blocks = cloneTaskBlocks(latestBoardStateRef.current?.taskBlocks || []);
    const filtered = blocks.filter((item) => String(item?.id) !== targetId);
    await updateTaskBlocks(filtered);
  }

  async function handleTaskBlockAction(blockId, action) {
      if (action === "remove") {
        setTaskDeleteTargetId(String(blockId || ""));
        setTaskDeleteOpen(true);
      return;
    }

    if (action === "toggle_collapse") {
      const targetId = String(blockId || "");
      if (!targetId) return;
      const currentBlock = taskBlocks.find((item) => String(item?.id || "") === targetId);
      if (!currentBlock) return;
      const currentCollapsed = Object.prototype.hasOwnProperty.call(taskCollapsePrefs, targetId)
        ? !!taskCollapsePrefs[targetId]
        : !!currentBlock.collapsed;
      const nextPrefs = {
        ...taskCollapsePrefs,
        [targetId]: !currentCollapsed,
      };
      setTaskCollapsePrefs(nextPrefs);
      writeTaskCollapsePrefs(roomCode, nextPrefs);
      return;
    }

    const nextBlocks = replaceTaskBlock(blockId, (prev) => {
      if (action === "activate") return prev;
      if (action === "toggle_meta") return { ...prev, metaExpanded: !prev.metaExpanded };
      if (action === "toggle_full") return { ...prev, fullHeight: !prev.fullHeight };
      if (action === "show_answer") return { ...prev, answerShown: true, explanationShown: false, locked: true };
      if (action === "toggle_explanation") return { ...prev, explanationShown: !prev.explanationShown };
      return prev;
    });
    if (!nextBlocks) return;

    const nextActiveId = action === "activate" ? String(blockId) : undefined;
    await updateTaskBlocks(nextBlocks, nextActiveId);
  }

  async function handleLeave() {
    handleCursorMove(null);
    if (!demoMode) await whiteboardLeaveRoom();
    nav(demoMode ? "/korepetycje" : "/tablica");
  }

  async function handleDeleteBoard() {
    if (!authorizedFetch) return;
    setErr("");
    try {
      const res = await authorizedFetch(`/api/whiteboards/${roomCode}`, {
        method: "DELETE",
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        setErr(data?.error || "delete_failed");
        return;
      }
      setDeleteOpen(false);
      handleCursorMove(null);
      await whiteboardLeaveRoom();
      nav("/tablica");
    } catch {
      setErr("delete_failed");
    }
  }

  async function toggleFullscreen() {
    if (fullscreenActive) {
      setIsPseudoFullscreen(false);
      setIsFullscreen(false);
      return;
    }

    // Keep behavior consistent with inline task whiteboard:
    // browser-window fullscreen (pseudo fullscreen), not native Fullscreen API.
    setIsPseudoFullscreen(true);
    setIsFullscreen(false);
  }

  useEffect(() => {
    // Start tablicy domyślnie w fullscreen (pseudo fullscreen) po wejściu
    // przez "Otwórz" lub bezpośredni link /tablica/:code.
    setIsPseudoFullscreen(true);
    setIsFullscreen(false);
  }, [roomCode]);

  function zoomIn() {
    setZoomPercent((prev) => {
      const idx = ZOOM_LEVELS.indexOf(prev);
      if (idx < 0) return 100;
      return ZOOM_LEVELS[Math.min(ZOOM_LEVELS.length - 1, idx + 1)];
    });
  }

  function zoomOut() {
    setZoomPercent((prev) => {
      const idx = ZOOM_LEVELS.indexOf(prev);
      if (idx <= 0) return ZOOM_LEVELS[0];
      return ZOOM_LEVELS[idx - 1];
    });
  }

  useEffect(() => {
    if (demoMode) return;
    if (!pageWindowModeRef.current) return;
    void requestPageWindowSlice(currentCanvasPage, { force: false });
  }, [currentCanvasPage, demoMode, roomCode]);

  useEffect(() => {
    async function handlePaste(e) {
      const items = Array.from(e.clipboardData?.items || []);
      const imageItem = items.find((item) => String(item.type || "").startsWith("image/"));
      if (!imageItem) return;
      const file = imageItem.getAsFile();
      if (!file) return;
      e.preventDefault();
      await insertImageFromFile(file);
    }

    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [currentCanvasPage, localBoardState.pageCount]);

  const isBoardEmpty = !Array.isArray(localBoardState.elements) || localBoardState.elements.length === 0;
  const isBrushTool = tool === "pen" || tool === "highlighter" || tool === "line" || tool === "polygon" || tool === "circle";
  const activePalette = tool === "highlighter" ? HIGHLIGHTER_COLORS : PEN_COLORS;
  const activeColor = tool === "highlighter" ? highlighterColor : penColor;
  const activeSize = tool === "highlighter" ? highlighterSize : brushSize;
  const quickConfigTool = isQuickConfigurableTool(quickToolConfigTool) ? quickToolConfigTool : "";
  const quickConfigPalette = quickConfigTool === "highlighter" ? HIGHLIGHTER_COLORS : PEN_COLORS;
  const quickConfigColor = quickConfigTool ? getQuickToolColor(quickConfigTool) : penColor;
  const quickConfigSize = quickConfigTool ? getQuickToolSize(quickConfigTool) : brushSize;
  const quickConfigRangeMin = quickConfigTool === "highlighter" ? 8 : 2;
  const quickConfigRangeMax = quickConfigTool === "highlighter" ? 36 : 24;
  const touchSafeButtonStyle = {
    userSelect: "none",
    WebkitUserSelect: "none",
    WebkitTouchCallout: "none",
    WebkitTapHighlightColor: "transparent",
    touchAction: "manipulation",
  };
  const compactButtonSizePx = 34;
  const compactButtonGapPx = 6;

  useEffect(() => {
    if (!quickToolConfigTool) return;
    if (!isQuickConfigurableTool(quickToolConfigTool)) {
      setQuickToolConfigTool("");
    }
  }, [quickToolConfigTool]);

  useEffect(() => {
    if (!quickToolConfigTool) return;
    if (!isQuickConfigurableTool(tool)) {
      setQuickToolConfigTool("");
      return;
    }
    if (tool !== quickToolConfigTool) {
      setQuickToolConfigTool(tool);
    }
  }, [tool, quickToolConfigTool]);

  const ribbon = (
    <div
      style={{
        display: "grid",
        gap: 10,
        border: themeMode === "light" ? "1px solid rgba(15,23,42,0.14)" : "1px solid rgba(255,255,255,0.14)",
        borderRadius: 18,
        padding: 12,
        boxShadow: "0 18px 40px rgba(0,0,0,0.28)",
        backdropFilter: "blur(12px)",
        background: themeMode === "light" ? "rgba(225,243,252,0.98)" : "rgba(12,16,40,0.88)",
        color: themeMode === "light" ? "#0f172a" : "#f8fafc",
      }}
    >
      <div style={{ display: "grid", gap: 4, minWidth: 0 }}>
        <div style={{ fontWeight: 900, fontSize: 14 }}>Ustawienia tablicy</div>
        <div style={{ fontWeight: 800, fontSize: 16, lineHeight: 1.35, overflowWrap: "anywhere" }}>
          {room?.settings?.title || ("Tablica " + roomCode)}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <QuickIconButton
          label={<PenIcon />}
          title="Pióro"
          active={tool === "pen"}
          indicatorColor={penColor}
          onClick={() => setTool("pen")}
        />
        <QuickIconButton
          label={<HighlighterIcon />}
          title="Zakreślacz"
          active={tool === "highlighter"}
          indicatorColor={highlighterColor}
          onClick={() => setTool("highlighter")}
        />
        <QuickIconButton
          label={<LineIcon />}
          title="Linia"
          active={tool === "line"}
          indicatorColor={penColor}
          onClick={() => setTool("line")}
        />
        <QuickIconButton
          label={<PolygonIcon />}
          title="Wielokąt"
          active={tool === "polygon"}
          indicatorColor={penColor}
          onClick={() => setTool("polygon")}
        />
        <QuickIconButton
          label={<CircleIcon />}
          title="Kółko"
          active={tool === "circle"}
          indicatorColor={penColor}
          onClick={() => setTool("circle")}
        />
        <QuickIconButton
          label={<TrashIcon />}
          title={isBoardEmpty ? "Tablica jest pusta" : "Wyczyść tablicę"}
          disabled={isBoardEmpty}
          onClick={requestClear}
        />
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ fontWeight: 800, fontSize: 13, opacity: 0.9 }}>Przytrzymanie</div>
        <QuickIconButton
          label={<span style={{ fontSize: 18, lineHeight: 1 }}>⇄</span>}
          title={holdToolSwitchEnabled ? "Wyłącz zmianę długopisu i gumki przez przytrzymanie" : "Włącz zmianę długopisu i gumki przez przytrzymanie"}
          active={holdToolSwitchEnabled}
          onClick={() => setHoldToolSwitchEnabled((enabled) => !enabled)}
        />
        <span style={{ fontSize: 12, opacity: 0.76 }}>
          {holdToolSwitchEnabled ? "Włączone" : "Wyłączone"} · przytrzymanie przez 0,7 s zmienia długopis i gumkę.
        </span>
      </div>

      {showStylusToggle ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ fontWeight: 800, fontSize: 13, opacity: 0.9 }}>Kursor rysika</div>
          <QuickIconButton
            label={<StylusModeIcon />}
            title={stylusCursorEnabled ? "Ukryj kursor rysika" : "Pokaż kursor rysika"}
            active={stylusCursorEnabled}
            onClick={() => {
              if (stylusCursorEnabled) handleCursorMove(null);
              setStylusCursorEnabled((enabled) => !enabled);
            }}
          />
          <span style={{ fontSize: 12, opacity: 0.76 }}>
            {stylusCursorEnabled ? "Włączony" : "Wyłączony"} · pozycja jest widoczna także dla innych uczestników.
          </span>
        </div>
      ) : null}

      {showStylusToggle ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ fontWeight: 800, fontSize: 13, opacity: 0.9 }}>Tryb awaryjny</div>
          <QuickIconButton
            label={<StylusModeIcon />}
            title={stylusOnly ? "Tryb rysika: włączony" : "Tryb rysika: wyłączony"}
            active={stylusOnly}
            onClick={() => setStylusOnly((v) => !v)}
          />
          <span style={{ fontSize: 12, opacity: 0.76 }}>
            Włącz, gdy tablet łapie dotyk dłoni zamiast samego rysika.
          </span>
        </div>
      ) : null}

      {voiceChannelAvailable ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ fontWeight: 800, fontSize: 13, opacity: 0.9 }}>Kanał głosowy</div>
          <QuickIconButton
            label={<VoiceIcon />}
            title={voiceWidgetEnabled ? "Ukryj przycisk kanału głosowego" : "Pokaż przycisk kanału głosowego"}
            active={voiceWidgetEnabled}
            onClick={() => setVoiceWidgetEnabled((v) => !v)}
          />
          <span style={{ fontSize: 12, opacity: 0.76 }}>
            Pokazuje mały przycisk do rozmowy głosowej przy tablicy.
          </span>
        </div>
      ) : null}

      {isBrushTool ? (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {activePalette.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => handleColorChange(c)}
                onContextMenu={(e) => e.preventDefault()}
                title={c}
                aria-label={`Kolor ${c}`}
                className="btn btn-secondary"
                style={{
                  width: 30,
                  height: 30,
                  minWidth: 30,
                  borderRadius: 999,
                  padding: 0,
                  background: c,
                  borderColor: activeColor === c ? "#f59e0b" : "rgba(255,255,255,0.24)",
                  boxShadow: activeColor === c ? "0 0 0 2px rgba(245,158,11,0.25)" : undefined,
                  ...touchSafeButtonStyle,
                }}
              />
            ))}
            <RecentCustomColorButton
              color={lastCustomColor}
              palette={activePalette}
              active={String(activeColor).toLowerCase() === String(lastCustomColor).toLowerCase()}
              onSelect={handleColorChange}
            />
            <CustomColorPickerButton
              value={activeColor}
              active={!activePalette.some((paletteColor) => paletteColor.toLowerCase() === String(activeColor).toLowerCase())}
              onChange={(nextColor) => {
                setLastCustomColor(nextColor);
                handleColorChange(nextColor);
              }}
            />
          </div>
          <label style={{ display: "inline-flex", gap: 8, alignItems: "center", fontSize: 13, fontWeight: 700 }}>
            Grubość
            <input
              type="range"
              min={tool === "highlighter" ? 8 : 2}
              max={tool === "highlighter" ? 36 : 24}
              step={1}
              value={activeSize}
              onChange={(e) => {
                const nextValue = Number(e.target.value);
                if (tool === "highlighter") setHighlighterSize(nextValue);
                else setBrushSize(nextValue);
              }}
            />
            <span style={{ minWidth: 18, textAlign: "right" }}>{activeSize}</span>
          </label>
        </>
      ) : null}

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ fontWeight: 800, fontSize: 13, opacity: 0.9 }}>Tło</div>
        {[
          { id: "default", color: themeMode === "light" ? "rgba(175,236,196,0.98)" : "rgba(16,70,48,0.98)", title: "Domyślne" },
          { id: "white", color: "#ffffff", title: "Białe" },
          { id: "black", color: "#0b0f12", title: "Czarne" },
        ].map((option) => (
          <button
            key={option.id}
            type="button"
            title={option.title}
            aria-label={option.title}
            onClick={() => setBoardSurface(option.id)}
            onContextMenu={(e) => e.preventDefault()}
            className="btn btn-secondary"
            style={{
              width: 32,
              height: 32,
              minWidth: 32,
              borderRadius: 999,
              padding: 0,
              background: option.color,
              borderColor: boardSurface === option.id ? "#f59e0b" : "rgba(255,255,255,0.26)",
              boxShadow: boardSurface === option.id ? "0 0 0 2px rgba(245,158,11,0.25)" : undefined,
              ...touchSafeButtonStyle,
            }}
          />
        ))}
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        <div style={{ fontWeight: 800, fontSize: 13, opacity: 0.9 }}>Uczestnicy</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {(room?.participants || []).map((participant) => {
            const participantId = String(participant?.userId || "");
            const participantColor =
              participantColorMap[participantId] || colorForUserId(participantId);
            const isOnline = !!participant?.connected;
            const isOwner = participantId && participantId === String(room?.hostUserId || "");
            const participantName = participant?.name || "Uczestnik";

            return (
              <span
                key={participantId || participantName}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  borderRadius: 999,
                  padding: "4px 10px",
                  fontSize: 13,
                  fontWeight: 700,
                  color: participantColor,
                  border: `1px solid ${participantColor}`,
                  background:
                    themeMode === "light"
                      ? "rgba(255,255,255,0.72)"
                      : "rgba(30,41,59,0.5)",
                }}
                title={`${participantName} · ${isOnline ? "online" : "offline"}`}
              >
                {participantName}
                {isOwner ? " (właściciel)" : ""}
                {" · "}
                {isOnline ? "online" : "offline"}
              </span>
            );
          })}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <QuickIconButton label={<ChevronUpIcon />} title="Schowaj ustawienia" onClick={() => setIsRibbonOpen(false)} />
      </div>
    </div>
  );

  const toggleControlsSide = () => {
    setControlsSide((prev) => (prev === "right" ? "left" : "right"));
  };

  const toggleSideToolsExpanded = () => {
    const next = !sideToolsExpanded;
    if (!next && !BASIC_SIDE_TOOLS.includes(tool)) {
      setTool("select");
    }
    setSideToolsExpanded(next);
  };

  const panelToggleButton = (
    <QuickIconButton
      label={<SettingsIcon />}
      title={isRibbonOpen ? "Ukryj ustawienia" : "Ustawienia tablicy"}
      active={isRibbonOpen}
      onClick={() => setIsRibbonOpen((v) => !v)}
    />
  );

  const controlsSideButton = (
    <QuickIconButton
      label="⇄"
      title={controlsSide === "right" ? "Przenieś panele na lewą stronę" : "Przenieś panele na prawą stronę"}
      onClick={toggleControlsSide}
    />
  );

  const panelControlsStack = (
    <div
      data-demo-tutorial-target="panels"
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(42px, max-content)",
        rowGap: 8,
        justifyContent: controlsSide === "left" ? "end" : "start",
        alignItems: "start",
      }}
    >
      {panelToggleButton}
      {controlsSideButton}
      <QuickIconButton
        label={<CalculatorIcon />}
        title="Kalkulator"
        active={calcOpen}
        onClick={() => setCalcOpen((v) => !v)}
      />
      <QuickIconButton
        label={<FormulaIcon />}
        title="Karta wzorow"
        active={sheetOpen}
        onClick={() => setSheetOpen((v) => !v)}
      />
    </div>
  );

  const topZoomControls = (
    <div
      style={{
        display: "flex",
        gap: compactButtonGapPx,
        alignItems: "center",
        flexWrap: "wrap",
        justifyContent: isMobileNarrow ? "flex-start" : "flex-end",
        maxWidth: "100%",
      }}
    >
      <span data-demo-tutorial-target="zoom" style={{ display: "inline-flex" }}>
        <ZoomControl
          zoomPercent={zoomPercent}
          onZoomOut={zoomOut}
          onReset={() => setZoomPercent(100)}
          onZoomIn={zoomIn}
          minZoom={100}
          maxZoom={400}
          themeMode={themeMode}
        />
      </span>
      <span data-demo-tutorial-part="quick-tools" style={{ display: "inline-flex" }}>
        <QuickIconButton
          label={<ToolsMenuIcon />}
          title={sideToolsExpanded ? "Zwiń szybkie narzędzia" : "Rozwiń pełne narzędzia"}
          active={sideToolsExpanded}
          onClick={toggleSideToolsExpanded}
        />
      </span>
    </div>
  );
  const readOnlyTopZoomControls = (
    <ZoomControl
      zoomPercent={zoomPercent}
      onZoomOut={zoomOut}
      onReset={() => setZoomPercent(100)}
      onZoomIn={zoomIn}
      minZoom={100}
      maxZoom={400}
      themeMode={themeMode}
    />
  );
  const archiveReadOnlyBanner = (
    <div
      role="status"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        minHeight: compactButtonSizePx,
        padding: "8px 12px",
        border: "1px solid rgba(245, 158, 11, 0.72)",
        borderRadius: 8,
        background: themeMode === "light" ? "rgba(255,255,255,0.94)" : "rgba(5,12,32,0.92)",
        color: themeMode === "light" ? "#713f12" : "#fde68a",
        fontSize: 13,
        fontWeight: 800,
        boxShadow: "0 8px 20px rgba(0,0,0,0.18)",
      }}
      title={archiveReadOnlyAt ? `Zarchiwizowano: ${new Date(archiveReadOnlyAt).toLocaleString()}` : undefined}
    >
      <span aria-hidden="true">●</span>
      Archiwum · tylko do odczytu
    </div>
  );

  const brushPrimaryTool = BRUSH_TOOLS.includes(tool) ? tool : lastBrushTool;
  const shapePrimaryTool = SHAPE_TOOLS.includes(tool) ? tool : lastShapeTool;

  const getToolButtonMeta = (toolName) => {
    if (toolName === "pen") return { label: <PenIcon />, title: "Pióro", indicatorColor: penColor };
    if (toolName === "eraser") return { label: <EraserIcon />, title: "Gumka" };
    if (toolName === "highlighter") return { label: <HighlighterIcon />, title: "Zakreślacz", indicatorColor: highlighterColor };
    if (toolName === "line") return { label: <LineIcon />, title: "Linia", indicatorColor: penColor };
    if (toolName === "polygon") return { label: <PolygonIcon />, title: "Wielokąt", indicatorColor: penColor };
    if (toolName === "circle") return { label: <CircleIcon />, title: "Kółko", indicatorColor: penColor };
    return { label: <SelectIcon />, title: "Wybierz" };
  };

  const renderToolButton = (toolName, keyPrefix = "tool") => {
    const meta = getToolButtonMeta(toolName);
    const hasQuickConfig = isQuickConfigurableTool(toolName);
    return (
      <QuickIconButton
        key={`${keyPrefix}-${toolName}`}
        label={meta.label}
        title={meta.title}
        active={tool === toolName}
        indicatorColor={meta.indicatorColor}
        onClick={() => setTool(toolName)}
        cornerActionTitle="Szybkie ustawienia"
        onCornerAction={
          hasQuickConfig
            ? () => {
                setQuickToolConfigTool((prev) => (prev === toolName ? "" : toolName));
                setTool(toolName);
              }
            : undefined
        }
        cornerActionActive={quickConfigTool === toolName}
        cornerActionLabel="⋯"
      />
    );
  };

  const renderToolGroupToggle = (open, onClick, keyPrefix) => (
    <QuickIconButton
      key={`${keyPrefix}-toggle`}
      label={open ? <ChevronUpIcon /> : <ChevronDownIcon />}
      title={open ? "Zwiń grupę narzędzi" : "Rozwiń grupę narzędzi"}
      active={open}
      minWidth={42}
      style={{ width: 42, height: 22, minHeight: 22, paddingInline: 8 }}
      onClick={onClick}
    />
  );

  const compactViewportHeightPx = fullscreenActive ? Math.max(320, windowHeight - 24) : Math.min(windowHeight * 0.72, 760);
  const compactBottomButtonRows = 4;
  const compactBottomStackHeightPx =
    compactBottomButtonRows * compactButtonSizePx +
    Math.max(0, compactBottomButtonRows - 1) * compactButtonGapPx;
  const compactSideToolStackHeight = Math.max(
    120,
    Math.round(compactViewportHeightPx - 14 - 50 - 8 - compactBottomStackHeightPx - 28)
  );
  const compactSideButtonSlotPx = compactButtonSizePx;
  const compactSideButtonGapPx = compactButtonGapPx;
  const compactSideToolRows = Math.max(
    2,
    Math.floor((compactSideToolStackHeight + compactSideButtonGapPx) / (compactSideButtonSlotPx + compactSideButtonGapPx))
  );
  const compactSideToolStackStyle = {
    display: "grid",
    gridAutoFlow: "column",
    gridTemplateRows: `repeat(${compactSideToolRows}, max-content)`,
    gridAutoColumns: "max-content",
    gap: compactButtonGapPx,
    alignItems: "start",
    justifyContent: "end",
    justifyItems: "end",
    alignContent: "start",
    height: compactSideToolStackHeight,
    maxWidth: "calc(100vw - 96px)",
    overflow: "hidden",
    direction: "rtl",
  };
  const compactSideToolItemStyle = {
    direction: "ltr",
    pointerEvents: "auto",
  };

  const compactTopControls = (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "auto minmax(0, 1fr)",
        gap: 8,
        alignItems: "flex-start",
        maxWidth: "100%",
        width: "100%",
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          display: "grid",
          gap: compactButtonGapPx,
          gridTemplateColumns: `${compactButtonSizePx}px`,
          alignItems: "start",
          justifyContent: "flex-start",
          minWidth: 0,
          width: compactButtonSizePx,
          pointerEvents: "auto",
        }}
      >
      {panelToggleButton}
      {controlsSideButton}
      <QuickIconButton
        label={<CalculatorIcon />}
        title="Kalkulator"
        active={calcOpen}
        onClick={() => setCalcOpen((v) => !v)}
      />
      <QuickIconButton
        label={<FormulaIcon />}
        title="Karta wzorow"
        active={sheetOpen}
        onClick={() => setSheetOpen((v) => !v)}
      />
      </div>
      <div
        style={{
          display: "grid",
          gap: compactButtonGapPx,
          alignItems: "start",
          justifyItems: "end",
          minWidth: 0,
          width: "100%",
          pointerEvents: "none",
        }}
      >
        <div style={{ display: "flex", gap: compactButtonGapPx, alignItems: "center", justifyContent: "flex-end", flexWrap: "nowrap", minWidth: 0, pointerEvents: "auto" }}>
          <span data-demo-tutorial-target="zoom" style={{ display: "inline-flex" }}>
            <ZoomControl
              zoomPercent={zoomPercent}
              onZoomOut={zoomOut}
              onReset={() => setZoomPercent(100)}
              onZoomIn={zoomIn}
              minZoom={100}
              maxZoom={400}
              themeMode={themeMode}
            />
          </span>
          <span data-demo-tutorial-part="quick-tools" style={{ display: "inline-flex" }}>
            <QuickIconButton
              label={<ToolsMenuIcon />}
              title={sideToolsExpanded ? "Zwiń szybkie narzędzia" : "Rozwiń pełne narzędzia"}
              active={sideToolsExpanded}
              onClick={toggleSideToolsExpanded}
            />
          </span>
        </div>
        {sideToolsExpanded ? (
          <div
            style={{ ...compactSideToolStackStyle, pointerEvents: "auto" }}
          >
            <span style={compactSideToolItemStyle}>
              <QuickIconButton label={<SelectIcon />} title="Wybierz" active={tool === "select"} onClick={() => setTool("select")} />
            </span>
            <span style={{ ...compactSideToolItemStyle, display: "inline-grid", gap: compactButtonGapPx, justifyItems: "end" }} data-demo-tutorial-part="brush-tools">
              {renderToolButton(brushPrimaryTool, "compact-brush-main")}
              {renderToolGroupToggle(brushGroupOpen, () => setBrushGroupOpen((v) => !v), "compact-brush")}
            </span>
            {brushGroupOpen ? BRUSH_TOOLS.filter((name) => name !== brushPrimaryTool).map((name) => (
              <span key={`compact-brush-wrap-${name}`} style={compactSideToolItemStyle} data-demo-tutorial-part="brush-tools">
                {renderToolButton(name, "compact-brush")}
              </span>
            )) : null}
            <span style={{ ...compactSideToolItemStyle, display: "inline-grid", gap: compactButtonGapPx, justifyItems: "end" }} data-demo-tutorial-part="shape-tools">
              {renderToolButton(shapePrimaryTool, "compact-shape-main")}
              {renderToolGroupToggle(shapeGroupOpen, () => setShapeGroupOpen((v) => !v), "compact-shape")}
            </span>
            {shapeGroupOpen ? SHAPE_TOOLS.filter((name) => name !== shapePrimaryTool).map((name) => (
              <span key={`compact-shape-wrap-${name}`} style={compactSideToolItemStyle} data-demo-tutorial-part="shape-tools">
                {renderToolButton(name, "compact-shape")}
              </span>
            )) : null}
            <span style={compactSideToolItemStyle}>
              <QuickIconButton label={<TextIcon />} title="Dodaj tekst" onClick={openTextCreator} />
            </span>
            <span style={compactSideToolItemStyle}>
              <QuickIconButton label={<UndoIcon />} title="Cofnij" disabled={!canUndo} onClick={handleUndo} />
            </span>
            <span style={compactSideToolItemStyle}>
              <QuickIconButton label={<RedoIcon />} title="Ponów" disabled={!canRedo} onClick={handleRedo} />
            </span>
            <span style={compactSideToolItemStyle} data-demo-tutorial-part="image-insert">
              <QuickIconButton label={<ImageIcon />} title="Dodaj obraz" onClick={openImagePicker} />
            </span>
            <span style={compactSideToolItemStyle} data-demo-tutorial-part="graph-insert">
              <QuickIconButton label={<GraphIcon />} title="Dodaj wykres" onClick={openGraphCreator} />
            </span>
            <span style={compactSideToolItemStyle} data-demo-tutorial-part="task-insert">
              <QuickIconButton label={<TaskIcon />} title="Dodaj zadanie" onClick={() => setTaskPickerOpen(true)} />
            </span>
          </div>
        ) : (
          <div
            style={{ ...compactSideToolStackStyle, pointerEvents: "auto" }}
          >
            <span style={compactSideToolItemStyle}>
              <QuickIconButton label={<SelectIcon />} title="Wybierz" active={tool === "select"} onClick={() => setTool("select")} />
            </span>
            {BASIC_SIDE_TOOLS.filter((name) => name !== "select").map((name) => (
              <span key={`compact-basic-wrap-${name}`} style={compactSideToolItemStyle}>
                {renderToolButton(name, "compact-basic")}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const viewportHeightPx = compactViewportHeightPx;
  // Bottom-right controls are laid out in rows:
  // 1) up, 2) scroll step, 3) grid, 4) down, 5) (right + fullscreen in one row).
  // Reserve by rows (not raw button count) to avoid overly conservative wrapping.
  const bottomButtonRows = 5;
  const rightOverlaySafetyPx = controlsSide === "right" ? compactButtonGapPx : 0;
  const bottomStackHeightPx =
    bottomButtonRows * compactButtonSizePx +
    Math.max(0, bottomButtonRows - 1) * compactButtonGapPx;
  const bottomReservedPx = bottomStackHeightPx + compactButtonGapPx + rightOverlaySafetyPx;
  const topReservedPx = 68;
  const availableSideHeightPx = Math.max(120, viewportHeightPx - topReservedPx - bottomReservedPx);
  const availableSideHeightSecondaryPx = Math.max(120, viewportHeightPx - topReservedPx - 12);
  const sideButtonSlotPx = compactButtonSizePx;
  const sideButtonGapPx = compactButtonGapPx;
  const sideButtonPitchPx = sideButtonSlotPx + sideButtonGapPx;
  const maxRowsPrimaryColumn = Math.max(
    1,
    Math.floor((availableSideHeightPx + sideButtonGapPx) / sideButtonPitchPx) + 1
  );
  const maxRowsSecondaryColumn = Math.max(
    1,
    Math.floor((availableSideHeightSecondaryPx + sideButtonGapPx) / sideButtonPitchPx)
  );
  const createSideToolDef = (toolName, keyPrefix = "side-tool") => {
    const meta = getToolButtonMeta(toolName);
    const hasQuickConfig = isQuickConfigurableTool(toolName);
    return {
      key: `${keyPrefix}-${toolName}`,
      label: meta.label,
      title: meta.title,
      active: tool === toolName,
      indicatorColor: meta.indicatorColor,
      onClick: () => setTool(toolName),
      cornerActionTitle: hasQuickConfig ? "Szybkie ustawienia" : "",
      onCornerAction: hasQuickConfig
        ? () => {
            setQuickToolConfigTool((prev) => (prev === toolName ? "" : toolName));
            setTool(toolName);
          }
        : undefined,
      cornerActionActive: quickConfigTool === toolName,
      cornerActionLabel: "⋯",
    };
  };

  const sideButtonDefs = {
    select: { key: "select", label: <SelectIcon />, title: "Wybierz", active: tool === "select", onClick: () => setTool("select") },
    brushMain: createSideToolDef(brushPrimaryTool, "side-brush-main"),
    brushToggle: {
      key: "brush-toggle",
      label: brushGroupOpen ? <ChevronUpIcon /> : <ChevronDownIcon />,
      title: brushGroupOpen ? "Zwiń grupę pisania" : "Rozwiń grupę pisania",
      active: brushGroupOpen,
      minWidth: 42,
      style: { width: 42, height: 22, minHeight: 22, paddingInline: 8 },
      onClick: () => setBrushGroupOpen((v) => !v),
    },
    brushExtras: BRUSH_TOOLS.filter((name) => name !== brushPrimaryTool).map((name) => createSideToolDef(name, "side-brush")),
    shapeMain: createSideToolDef(shapePrimaryTool, "side-shape-main"),
    shapeToggle: {
      key: "shape-toggle",
      label: shapeGroupOpen ? <ChevronUpIcon /> : <ChevronDownIcon />,
      title: shapeGroupOpen ? "Zwiń grupę kształtów" : "Rozwiń grupę kształtów",
      active: shapeGroupOpen,
      minWidth: 42,
      style: { width: 42, height: 22, minHeight: 22, paddingInline: 8 },
      onClick: () => setShapeGroupOpen((v) => !v),
    },
    shapeExtras: SHAPE_TOOLS.filter((name) => name !== shapePrimaryTool).map((name) => createSideToolDef(name, "side-shape")),
    text: { key: "text", label: <TextIcon />, title: "Dodaj tekst", onClick: openTextCreator },
    undo: { key: "undo", label: <UndoIcon />, title: "Cofnij", disabled: !canUndo, onClick: handleUndo },
    redo: { key: "redo", label: <RedoIcon />, title: "Ponów", disabled: !canRedo, onClick: handleRedo },
    image: { key: "image", label: <ImageIcon />, title: "Dodaj obraz", onClick: openImagePicker },
    graph: { key: "graph", label: <GraphIcon />, title: "Dodaj wykres", onClick: openGraphCreator },
    task: { key: "task", label: <TaskIcon />, title: "Dodaj zadanie", onClick: () => setTaskPickerOpen(true) },
  };
  const fullSideButtonUnits = [
    [sideButtonDefs.select],
    [sideButtonDefs.brushMain, sideButtonDefs.brushToggle], // keep main tool + toggle together
    ...(brushGroupOpen ? sideButtonDefs.brushExtras.map((btn) => [btn]) : []),
    [sideButtonDefs.shapeMain, sideButtonDefs.shapeToggle], // keep main tool + toggle together
    ...(shapeGroupOpen ? sideButtonDefs.shapeExtras.map((btn) => [btn]) : []),
    [sideButtonDefs.text],
    [sideButtonDefs.undo, sideButtonDefs.redo], // keep undo/redo together where possible
    [sideButtonDefs.image],
    [sideButtonDefs.graph],
    [sideButtonDefs.task],
  ];
  const compactSideButtonUnits = [
    BASIC_SIDE_TOOLS.map((toolName) => (
      toolName === "select"
        ? sideButtonDefs.select
        : createSideToolDef(toolName, "side-compact-basic")
    )),
  ];
  const sideButtonUnits = sideToolsExpanded ? fullSideButtonUnits : compactSideButtonUnits;

  const sideButtonColumns = [];
  let currentColumn = [];
  let currentRows = 0;

  const flushColumn = () => {
    if (!currentColumn.length) return;
    sideButtonColumns.push(currentColumn);
    currentColumn = [];
    currentRows = 0;
  };

  const currentRowsLimit = () => (sideButtonColumns.length === 0 ? maxRowsPrimaryColumn : maxRowsSecondaryColumn);

  const pushButtonWithWrapping = (btn) => {
    let limit = currentRowsLimit();
    if (currentRows > 0 && currentRows + 1 > limit) {
      flushColumn();
      limit = currentRowsLimit();
    }
    currentColumn.push(btn);
    currentRows += 1;
  };

  for (const unit of sideButtonUnits) {
    const unitRows = unit.length;
    const limit = currentRowsLimit();

    if (currentRows > 0 && currentRows + unitRows > limit) {
      flushColumn();
    }

    const postFlushLimit = currentRowsLimit();
    if (unitRows <= postFlushLimit) {
      unit.forEach((btn) => pushButtonWithWrapping(btn));
      continue;
    }

    // Very small viewport fallback: split oversized units.
    unit.forEach((btn) => pushButtonWithWrapping(btn));
  }

  flushColumn();
  const renderedSideButtonColumns = controlsSide === "left"
    ? sideButtonColumns
    : sideButtonColumns.slice().reverse();
  const getDemoTutorialPartForSideButton = (key) => {
    const normalized = String(key || "");
    if (normalized.includes("brush")) return "brush-tools";
    if (normalized.includes("shape")) return "shape-tools";
    if (normalized === "image") return "image-insert";
    if (normalized === "graph") return "graph-insert";
    if (normalized === "task") return "task-insert";
    return undefined;
  };

  const quickSideControls = renderedSideButtonColumns.length > 1 ? (
    <div data-demo-tutorial-target="side-tools" data-demo-tutorial-part="quick-tools" style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
      {renderedSideButtonColumns.map((column, columnIndex) => (
        <div key={`side-col-${columnIndex}`} style={{ display: "grid", gap: 8 }}>
          {column.map((btn) => (
            <span key={btn.key} data-demo-tutorial-part={getDemoTutorialPartForSideButton(btn.key)}>
            <QuickIconButton
              label={btn.label}
              title={btn.title}
              active={!!btn.active}
              indicatorColor={btn.indicatorColor}
              disabled={!!btn.disabled}
              minWidth={Number(btn.minWidth || 42)}
              style={btn.style || undefined}
              onClick={btn.onClick}
              cornerActionTitle={btn.cornerActionTitle || ""}
              onCornerAction={btn.onCornerAction}
              cornerActionActive={!!btn.cornerActionActive}
              cornerActionLabel={btn.cornerActionLabel || "⋯"}
            />
            </span>
          ))}
        </div>
      ))}
    </div>
  ) : (
    <div data-demo-tutorial-target="side-tools" data-demo-tutorial-part="quick-tools" style={{ display: "grid", gap: 8 }}>
      {sideButtonUnits.flat().map((btn) => (
        <span key={btn.key} data-demo-tutorial-part={getDemoTutorialPartForSideButton(btn.key)}>
        <QuickIconButton
          label={btn.label}
          title={btn.title}
          active={!!btn.active}
          indicatorColor={btn.indicatorColor}
          disabled={!!btn.disabled}
          minWidth={Number(btn.minWidth || 42)}
          style={btn.style || undefined}
          onClick={btn.onClick}
          cornerActionTitle={btn.cornerActionTitle || ""}
          onCornerAction={btn.onCornerAction}
          cornerActionActive={!!btn.cornerActionActive}
          cornerActionLabel={btn.cornerActionLabel || "⋯"}
        />
        </span>
      ))}
    </div>
  );

  const topLeftControls = controlsSide === "left" ? topZoomControls : panelControlsStack;
  const topRightControls = controlsSide === "left" ? panelControlsStack : topZoomControls;

  const bottomRightControls = (
    <>
      {demoMode ? <button type="button" className="btn btn-primary" onClick={() => nav("/korepetycje")} title="Wróć do korepetycji" aria-label="Wróć do korepetycji" style={{minHeight:compactButtonSizePx,padding:"0 14px",borderRadius:12,fontSize:13,fontWeight:800,whiteSpace:"nowrap",...touchSafeButtonStyle}}>← Wróć do korepetycji</button> : null}

      <QuickIconButton
        label={fullscreenActive ? <FullscreenExitIcon /> : <FullscreenEnterIcon />}
        title={fullscreenActive ? "Wyjdz z full screen" : "Full screen"}
        onClick={toggleFullscreen}
      />
    </>
  );

  const bottomRightTopControls = (
    <QuickIconButton
      label={<GridIcon />}
      title={gridEnabled ? "Wyłącz kratkę" : "Włącz kratkę"}
      active={gridEnabled}
      onClick={() => setGridEnabled((v) => !v)}
    />
  );

  // Panel zadania i popup ustawień mają wypełniać korytarz między strefami przycisków.
  // Liczymy offset od faktycznych stref UI: z lewej pojedyncza kolumna, z prawej zoom + hamburger.
  const controlEdgeInsetPx = 14;
  const taskDockSafeGapPx = 8;
  const panelStackWidthPx = compactButtonSizePx;
  const zoomStackWidthPx = 176;
  const taskPanelInsetPx = isMobileNarrow ? 12 : controlEdgeInsetPx + panelStackWidthPx + taskDockSafeGapPx;
  const taskZoomInsetPx = isMobileNarrow ? 12 : controlEdgeInsetPx + zoomStackWidthPx + taskDockSafeGapPx;
  const taskDockLeftInsetPx = isMobileNarrow
    ? 12
    : controlsSide === "left"
      ? taskZoomInsetPx
      : taskPanelInsetPx;
  const taskDockRightInsetPx = isMobileNarrow
    ? 12
    : controlsSide === "left"
      ? taskPanelInsetPx
      : taskZoomInsetPx;
  const quickPopupLeftInsetPx = taskDockLeftInsetPx + (isMobileNarrow ? 0 : 8);
  const quickPopupRightInsetPx = taskDockRightInsetPx + (isMobileNarrow ? 0 : 8);
  const quickPopupCorridorPx = quickPopupLeftInsetPx + quickPopupRightInsetPx;
  const sideColumnsCount = Math.max(1, Number(renderedSideButtonColumns.length || 1));
  const sideColumnsVisualWidthPx = sideColumnsCount * (compactButtonSizePx + compactButtonGapPx) - compactButtonGapPx;
  const quickPopupEdgeOffsetPx = 14 + sideColumnsVisualWidthPx + 8;
  const ribbonZoomInsetPx = taskZoomInsetPx;
  const ribbonPanelInsetPx = taskPanelInsetPx + 8;
  const ribbonDockLeftInsetPx = isMobileNarrow
    ? 12
    : controlsSide === "left"
      ? ribbonZoomInsetPx
      : ribbonPanelInsetPx;
  const ribbonDockRightInsetPx = isMobileNarrow
    ? 12
    : controlsSide === "left"
      ? ribbonPanelInsetPx
      : ribbonZoomInsetPx;
  const taskDockButtonStyle = {
    ...touchSafeButtonStyle,
    minWidth: compactButtonSizePx,
    height: compactButtonSizePx,
    minHeight: compactButtonSizePx,
    padding: "0 10px",
    fontSize: 14,
    lineHeight: 1,
    borderRadius: 12,
  };
  const regularBoardViewportHeight = Math.max(520, Math.min(760, Math.round(windowHeight * 0.72)));
  const taskDockNumberButtonStyle = {
    ...taskDockButtonStyle,
    width: compactButtonSizePx,
    padding: 0,
    fontWeight: 800,
  };
  const taskDockAnswerButtonStyle = {
    ...taskDockButtonStyle,
    minWidth: "auto",
    padding: "0 12px",
    fontWeight: 800,
  };
  const ribbonDockWidth = isMobileNarrow
    ? "calc(100% - 24px)"
    : `min(1414px, calc(100% - ${ribbonDockLeftInsetPx + ribbonDockRightInsetPx}px))`;

  const quickToolConfigPopup = quickConfigTool ? (
    <div
      style={{
        position: "absolute",
        top: isMobileNarrow ? 66 : 74,
        ...(isMobileNarrow
          ? {
              left: quickPopupLeftInsetPx,
              right: quickPopupRightInsetPx,
              margin: "0 auto",
            }
          : controlsSide === "left"
          ? {
              left: quickPopupEdgeOffsetPx,
              right: "auto",
              margin: 0,
            }
          : {
              right: quickPopupEdgeOffsetPx,
              left: "auto",
              margin: 0,
            }),
        zIndex: 35,
        width: isMobileNarrow ? `min(320px, calc(100% - ${quickPopupCorridorPx}px))` : 320,
        maxWidth: `calc(100% - ${quickPopupCorridorPx}px)`,
        borderRadius: 14,
        border: themeMode === "light" ? "1px solid rgba(15,23,42,0.16)" : "1px solid rgba(255,255,255,0.16)",
        background: themeMode === "light" ? "rgba(235,248,255,0.96)" : "rgba(10,16,36,0.94)",
        boxShadow: "0 14px 36px rgba(0,0,0,0.32)",
        backdropFilter: "blur(10px)",
        paddingTop: 10,
        paddingBottom: 10,
        paddingLeft: 10,
        paddingRight: 6,
        display: "grid",
        gap: 10,
        pointerEvents: "auto",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ fontWeight: 800, fontSize: 13 }}>
          {getToolButtonMeta(quickConfigTool).title}
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setQuickToolConfigTool("")}
          onContextMenu={(e) => e.preventDefault()}
          style={{ width: 28, height: 28, minWidth: 28, padding: 0, ...touchSafeButtonStyle }}
          title="Zamknij"
        >
          ×
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        {quickConfigPalette.map((c) => (
          <button
            key={`quick-tool-color-${quickConfigTool}-${c}`}
            type="button"
            onClick={() => setQuickToolColor(quickConfigTool, c)}
            onContextMenu={(e) => e.preventDefault()}
            title={c}
            aria-label={`Kolor ${c}`}
            className="btn btn-secondary"
            style={{
              width: 28,
              height: 28,
              minWidth: 28,
              borderRadius: 999,
              padding: 0,
              background: c,
              borderColor: quickConfigColor === c ? "#f59e0b" : "rgba(255,255,255,0.24)",
              boxShadow: quickConfigColor === c ? "0 0 0 2px rgba(245,158,11,0.25)" : undefined,
              ...touchSafeButtonStyle,
            }}
          />
        ))}
        <RecentCustomColorButton
          color={lastCustomColor}
          palette={quickConfigPalette}
          size={28}
          active={String(quickConfigColor).toLowerCase() === String(lastCustomColor).toLowerCase()}
          onSelect={(nextColor) => setQuickToolColor(quickConfigTool, nextColor)}
        />
        <CustomColorPickerButton
          value={quickConfigColor}
          size={28}
          active={!quickConfigPalette.some((paletteColor) => paletteColor.toLowerCase() === String(quickConfigColor).toLowerCase())}
          onChange={(nextColor) => {
            setLastCustomColor(nextColor);
            setQuickToolColor(quickConfigTool, nextColor);
          }}
        />
      </div>

      <label
        style={{
          display: "flex",
          width: "100%",
          boxSizing: "border-box",
          gap: 8,
          alignItems: "center",
          fontSize: 12,
          fontWeight: 700,
          paddingRight: 0,
        }}
      >
        Grubość
        <input
          type="range"
          min={quickConfigRangeMin}
          max={quickConfigRangeMax}
          step={1}
          value={quickConfigSize}
          onChange={(e) => setQuickToolSize(quickConfigTool, Number(e.target.value))}
          style={{ flex: 1, minWidth: 0 }}
        />
        <span style={{ minWidth: 18, textAlign: "right" }}>{quickConfigSize}</span>
      </label>
    </div>
  ) : null;

  const voiceChannelWidget = voiceChannelAvailable && voiceWidgetEnabled ? (
    <VoiceChannel
      channelId={`whiteboard:${roomCode}`}
      socket={socket}
      currentUser={me}
      compact={isMobileNarrow || fullscreenActive}
      dockable={fullscreenActive}
      themeMode={themeMode}
      title="Głos"
    />
  ) : null;

  const fullscreenShareControls = fullscreenActive ? (
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
      <QuickIconButton
        label={<LinkChainIcon />}
        title="Kopiuj link"
        onClick={() => navigator.clipboard?.writeText(shareUrl)}
      />
      <QuickIconButton
        label={<CopyCodeIcon />}
        title={`Kopiuj kod (${roomCode})`}
        onClick={() => navigator.clipboard?.writeText(roomCode)}
      />
      <QuickIconButton
        label={<LeaveDoorIcon />}
        title="Opuść tablicę"
        onClick={handleLeave}
      />
    </div>
  ) : null;

  const fullscreenBottomLeftControls = fullscreenActive ? (
    <div
      style={{
        display: "grid",
        gap: 8,
        justifyItems: controlsSide === "left" ? "end" : "start",
        maxWidth: "min(360px, calc(100vw - 28px))",
      }}
    >
      {voiceChannelWidget ? (
        <div style={{ maxWidth: "100%", pointerEvents: "auto" }}>
          {voiceChannelWidget}
        </div>
      ) : null}
      {fullscreenShareControls}
    </div>
  ) : null;

  const connectionBanner = connectionBannerState ? (
    <div
      style={{
        pointerEvents: "none",
        padding: "8px 12px",
        borderRadius: 12,
        fontSize: 13,
        fontWeight: 800,
        lineHeight: 1.2,
        border:
          connectionBannerState === "offline"
            ? "1px solid rgba(239,68,68,0.65)"
            : connectionBannerState === "reconnecting" || connectionBannerState === "syncing"
            ? "1px solid rgba(245,158,11,0.65)"
            : "1px solid rgba(16,185,129,0.65)",
        background:
          connectionBannerState === "offline"
            ? "rgba(127,29,29,0.82)"
            : connectionBannerState === "reconnecting" || connectionBannerState === "syncing"
            ? "rgba(120,53,15,0.82)"
            : "rgba(6,78,59,0.82)",
        color: "#f8fafc",
        boxShadow: "0 8px 22px rgba(0,0,0,0.3)",
        whiteSpace: "nowrap",
      }}
    >
      {connectionBannerState === "offline"
        ? "Brak połączenia z internetem. Sprawdź połączenie."
        : connectionBannerState === "reconnecting"
        ? "Ponowne łączenie z tablicą…"
        : connectionBannerState === "syncing"
        ? "Zapisywanie zmian na tablicy…"
        : "Połączono"}
    </div>
  ) : null;

  const hardSyncBanner = hardSyncActive ? (
    <div
      style={{
        pointerEvents: "none",
        padding: "8px 12px",
        borderRadius: 12,
        fontSize: 13,
        fontWeight: 800,
        lineHeight: 1.2,
        border: "1px solid rgba(245,158,11,0.65)",
        background: "rgba(120,53,15,0.86)",
        color: "#f8fafc",
        boxShadow: "0 8px 22px rgba(0,0,0,0.3)",
        whiteSpace: "nowrap",
      }}
    >
      Synchronizacja tablicy… chwilowo blokuję edycję
    </div>
  ) : null;

  const storageLimitBanner = storageLimitError ? (
    <div
      role="alert"
      style={{
        pointerEvents: "auto",
        padding: "10px 14px",
        borderRadius: 12,
        maxWidth: "min(620px, calc(100vw - 32px))",
        fontSize: 13,
        fontWeight: 750,
        lineHeight: 1.35,
        border: "1px solid rgba(239,68,68,0.75)",
        background: "rgba(127,29,29,0.94)",
        color: "#f8fafc",
        boxShadow: "0 8px 22px rgba(0,0,0,0.35)",
        whiteSpace: "normal",
        textAlign: "left",
      }}
    >
      <div style={{ fontWeight: 900 }}>Tablica osiągnęła limit zapisu {formatStorageMegabytes(storageLimitError.maxBytes)}.</div>
      <div style={{ marginTop: 3, fontWeight: 650 }}>
        To nie jest problem z internetem. Ostatnia zmiana nie została zapisana. Usuń część obrazów lub zawartości albo przejdź do nowej tablicy.
      </div>
    </div>
  ) : null;

  const storageUsageBanner = !storageLimitError && storageUsageWarning ? (
    <div
      role="status"
      style={{
        pointerEvents: "none",
        padding: "9px 13px",
        borderRadius: 12,
        maxWidth: "min(620px, calc(100vw - 32px))",
        fontSize: 13,
        fontWeight: 750,
        lineHeight: 1.35,
        border: "1px solid rgba(245,158,11,0.72)",
        background: "rgba(120,53,15,0.9)",
        color: "#f8fafc",
        boxShadow: "0 8px 22px rgba(0,0,0,0.3)",
        whiteSpace: "normal",
        textAlign: "left",
      }}
    >
      Tablica wykorzystuje około {Math.round((storageUsageWarning.currentBytes / storageUsageWarning.maxBytes) * 100)}% miejsca na zapis. Najwięcej miejsca zwykle zajmują obrazy — przed dalszą pracą warto rozpocząć nową tablicę.
    </div>
  ) : null;

  return (
    <div
      style={{
        padding: isPseudoFullscreen ? 0 : "0 18px 8px",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        position: isPseudoFullscreen ? "fixed" : "relative",
        inset: isPseudoFullscreen ? 0 : "auto",
        zIndex: isPseudoFullscreen ? 1500 : "auto",
        width: isPseudoFullscreen ? "100vw" : "auto",
        height: isPseudoFullscreen ? "100dvh" : "auto",
        background: isPseudoFullscreen
          ? (themeMode === "light" ? "rgba(223,241,252,0.98)" : "#081020")
          : "transparent",
      }}
    >
      <div
        className="glass-container"
        style={{
          width: "100%",
          maxWidth: isPseudoFullscreen ? "100vw" : undefined,
          height: isPseudoFullscreen ? "100dvh" : "auto",
          margin: isPseudoFullscreen ? 0 : undefined,
          borderRadius: isPseudoFullscreen ? 0 : undefined,
          padding: isPseudoFullscreen ? 0 : undefined,
        }}
      >
        {!isPseudoFullscreen ? (
          <div className="panel-card" style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ fontWeight: 900 }}>
                Tablica: {roomCode} {room?.settings?.title ? `· ${room.settings.title}` : ""}
                {demoMode ? " · tryb demo (bez zapisu na serwerze)" : ""}
              </div>
              {participantNames.length ? (
                <div style={{ fontSize: 13, opacity: 0.85 }}>
                  Uczestnicy: <b>{participantNames.join(", ")}</b>
                </div>
              ) : null}
              {boardGroup?.id ? (
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => nav(`/tablica/grupa/${encodeURIComponent(boardGroup.id)}`)}
                  title={`Otwórz grupę: ${boardGroup.name || "Grupa tablic"}`}
                >
                  Grupa: {boardGroup.name || "Grupa tablic"}
                </button>
              ) : null}
              <QuickIconButton
                label={<LinkChainIcon />}
                title="Kopiuj link"
                onClick={() => navigator.clipboard?.writeText(shareUrl)}
              />
              <QuickIconButton
                label={<CopyCodeIcon />}
                title={`Kopiuj kod (${roomCode})`}
                onClick={() => navigator.clipboard?.writeText(roomCode)}
              />
              {isHost && !demoMode ? (
                <QuickIconButton label={<TrashCanIcon />} title="Usuń tablicę" onClick={() => setDeleteOpen(true)} />
              ) : null}
              <QuickIconButton
                label={<LeaveDoorIcon />}
                title="Opuść tablicę"
                onClick={handleLeave}
                style={{ marginLeft: "auto" }}
              />
            </div>
          </div>
        ) : null}

        <DemoTutorialOverlay
          open={demoMode && demoTutorialOpen}
          stepIndex={demoTutorialStep}
          controlsSide={controlsSide}
          themeMode={themeMode}
          onStepChange={setDemoTutorialStep}
          onClose={() => setDemoTutorialOpen(false)}
        />

        <div
          ref={fullscreenRef}
          style={{
            background: fullscreenActive
              ? (themeMode === "light" ? "rgba(223,241,252,0.98)" : "#081020")
              : "transparent",
            paddingTop: 0,
            paddingLeft: fullscreenActive ? (isPseudoFullscreen ? 0 : 12) : 0,
            paddingRight: fullscreenActive ? (isPseudoFullscreen ? 0 : 12) : 0,
            paddingBottom: 0,
            borderRadius: fullscreenActive ? (isPseudoFullscreen ? 0 : 20) : 0,
            height: fullscreenActive ? "100dvh" : "auto",
            boxSizing: "border-box",
            position: "relative",
            inset: "auto",
            zIndex: "auto",
            overflowY: fullscreenActive ? "hidden" : "visible",
            overflowX: "hidden",
            touchAction: "none",
            overscrollBehavior: "none",
            WebkitTouchCallout: "none",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          {taskBlocks.length ? (
            <>
              <div
                style={{
                  position: "absolute",
                  top: 4,
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 23,
                  display: "flex",
                  justifyContent: "center",
                  pointerEvents: "auto",
                }}
              >
                <QuickIconButton
                  label={taskDockHidden ? <ChevronDownIcon /> : <ChevronUpIcon />}
                  title={taskDockHidden ? "Rozwiń panel zadań" : "Schowaj panel zadań"}
                  onClick={() => {
                    setTaskDockHidden((value) => {
                      const next = !value;
                      writeTaskDockHidden(roomCode, next);
                      return next;
                    });
                  }}
                />
              </div>
              {!taskDockHidden ? (
                <div
              style={{
                display: "grid",
                gap: 8,
                width: "auto",
                position: "absolute",
                top: 8,
                left: taskDockLeftInsetPx,
                right: taskDockRightInsetPx,
                margin: 0,
                zIndex: 22,
                pointerEvents: "auto",
              }}
            >
              {!activeTaskBlock ? (
                <div
                  className="panel-card"
                  style={{
                    padding: 8,
                    borderRadius: 14,
                    overflow: "hidden",
                    border: "1px solid rgba(245,158,11,0.3)",
                    background: themeMode === "light" ? "rgba(255,255,255,0.96)" : "rgba(5,10,32,0.94)",
                  }}
                >
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{ fontWeight: 800, whiteSpace: "nowrap" }}>Zadania na tablicy</div>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <HorizontalScroll>
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          {taskBlocks.map((block, index) => {
                            const blockId = String(block?.id || `task_${index}`);
                            const isActive = blockId === String(activeTaskBlockId || "");
                            return (
                              <button
                                key={blockId}
                                className={isActive ? "btn btn-primary" : "btn btn-secondary"}
                                type="button"
                                onClick={() => handleTaskBlockAction(blockId, "activate")}
                                onContextMenu={(e) => e.preventDefault()}
                                title={block?.subtopicLabel || block?.subtopicId || ""}
                                style={taskDockNumberButtonStyle}
                              >
                                {String(index + 1)}
                              </button>
                            );
                          })}
                        </div>
                      </HorizontalScroll>
                    </div>
                    {!archiveReadOnly ? (
                      <QuickIconButton
                        label={<TaskIcon />}
                        title="Dodaj zadanie"
                        onClick={() => setTaskPickerOpen(true)}
                        style={{ flexShrink: 0 }}
                      />
                    ) : null}
                  </div>
                </div>
              ) : null}

              {activeTaskBlock ? (() => {
                const block = activeTaskBlock;
                const blockId = String(block?.id || "");
                const adapter = taskAdapters[String(block?.subtopicId || "")];
                const adapterStatus = taskAdapterStatus[String(block?.subtopicId || "")] || {};
                const adapterError = adapterStatus.state === "error" ? String(adapterStatus.error || "") : "";
                const task = block?.task || null;
                const canRender = !!adapter && !!task;
                const blockNumber = Math.max(1, taskBlocks.findIndex((item) => String(item?.id || "") === blockId) + 1);
                const canToggleFull = !block?.collapsed && (activeTaskScrollable || !!block?.fullHeight);
                return (
                  <div
                    key={blockId}
                    className="panel-card"
                    style={{
                      padding: 8,
                      borderRadius: 14,
                      overflow: "hidden",
                      border: "1px solid rgba(245,158,11,0.3)",
                      background: themeMode === "light" ? "rgba(255,255,255,0.96)" : "rgba(5,10,32,0.94)",
                    }}
                  >
                    <div style={{ display: "grid", gap: 6 }}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                          <div style={{ fontWeight: 800 }}>
                            Zadanie {blockNumber}
                          </div>
                          <QuickIconButton
                            label={<InfoIcon />}
                            title={block?.metaExpanded ? "Ukryj szczegóły zadania" : "Pokaż szczegóły zadania"}
                            onClick={() => handleTaskBlockAction(blockId, "toggle_meta")}
                          />
                          <QuickIconButton
                            label={block?.collapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
                            title={block?.collapsed ? "Rozwiń" : "Zwiń"}
                            onClick={() => handleTaskBlockAction(blockId, "toggle_collapse")}
                          />
                          {!archiveReadOnly && !block?.locked ? (
                            <QuickIconButton
                              label={<ShuffleIcon />}
                              title="Wylosuj inne"
                              onClick={() => handleRerollTaskBlock(blockId)}
                            />
                          ) : null}
                          {!archiveReadOnly ? (
                            <QuickIconButton
                              label={<DuplicateIcon />}
                              title="Duplikuj typ zadania"
                              onClick={() => handleDuplicateTaskBlock(blockId)}
                            />
                          ) : null}
                          {canToggleFull ? (
                            <QuickIconButton
                              label={block?.fullHeight ? <FullscreenExitIcon /> : <FullscreenEnterIcon />}
                              title={block?.fullHeight ? "Tryb skrótu" : "Pełny widok"}
                              onClick={() => handleTaskBlockAction(blockId, "toggle_full")}
                            />
                          ) : null}
                          {!archiveReadOnly ? (
                            <QuickIconButton
                              label={<TrashIcon />}
                              title="Usuń"
                              onClick={() => handleTaskBlockAction(blockId, "remove")}
                            />
                          ) : null}
                          <button
                            className="btn btn-secondary"
                            type="button"
                            onClick={() => {
                              if (!block?.answerShown) {
                                handleTaskBlockAction(blockId, "show_answer");
                                return;
                              }
                              handleTaskBlockAction(blockId, "toggle_explanation");
                            }}
                            onContextMenu={(e) => e.preventDefault()}
                            style={taskDockAnswerButtonStyle}
                          >
                            {!block?.answerShown
                              ? "Pokaż odpowiedź"
                              : (block?.explanationShown ? "Ukryj wyjaśnienie" : "Pokaż wyjaśnienie")}
                          </button>
                        <div
                          style={{
                            display: "flex",
                            gap: 6,
                            alignItems: "center",
                            minWidth: 220,
                            maxWidth: "100%",
                            flex: "1 1 360px",
                          }}
                        >
                          <div style={{ fontWeight: 800, whiteSpace: "nowrap" }}>Zadania na tablicy</div>
                          <div style={{ minWidth: 120, flex: 1 }}>
                            <HorizontalScroll>
                              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                {taskBlocks.map((item, index) => {
                                  const itemId = String(item?.id || `task_${index}`);
                                  const isActive = itemId === String(activeTaskBlockId || "");
                                  return (
                                    <button
                                      key={itemId}
                                      className={isActive ? "btn btn-primary" : "btn btn-secondary"}
                                      type="button"
                                      onClick={() => handleTaskBlockAction(itemId, "activate")}
                                      onContextMenu={(e) => e.preventDefault()}
                                      title={item?.subtopicLabel || item?.subtopicId || ""}
                                      style={taskDockNumberButtonStyle}
                                    >
                                      {String(index + 1)}
                                    </button>
                                  );
                                })}
                              </div>
                            </HorizontalScroll>
                          </div>
                          {!archiveReadOnly ? (
                            <QuickIconButton
                              label={<TaskIcon />}
                              title="Dodaj zadanie"
                              onClick={() => setTaskPickerOpen(true)}
                              style={{ flexShrink: 0 }}
                            />
                          ) : null}
                        </div>
                      </div>

                      {block?.metaExpanded ? (
                        <div style={{ fontSize: 12, opacity: 0.85 }}>
                          {block?.sectionLabel ? `${block.sectionLabel} · ` : ""}
                          {block?.topicLabel ? `${block.topicLabel} · ` : ""}
                          {block?.subtopicLabel || block?.subtopicId || ""}
                          {block?.taskChoice?.label ? ` · ${block.taskChoice.label}` : ""}
                        </div>
                      ) : null}
                    </div>

                    {!block?.collapsed ? (
                      <div
                        ref={activeTaskBodyRef}
                        style={{
                          marginTop: 10,
                          maxHeight: block?.fullHeight ? "calc(100dvh - 164px)" : 250,
                          overflowY: "auto",
                          overscrollBehavior: "contain",
                          WebkitOverflowScrolling: "touch",
                        }}
                      >
                        {!canRender ? (
                          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", fontSize: 13, opacity: 0.9 }}>
                            <span>
                              {adapterError
                                ? "Nie udało się załadować adaptera zadania."
                                : "Ładowanie adaptera zadania…"}
                            </span>
                            {adapterError ? (
                              <button
                                className="btn btn-secondary"
                                type="button"
                                onClick={() => retryTaskAdapter(block?.subtopicId)}
                                onContextMenu={(event) => event.preventDefault()}
                                style={{ ...touchSafeButtonStyle, padding: "8px 12px" }}
                                title={adapterError}
                              >
                                Spróbuj ponownie
                              </button>
                            ) : null}
                          </div>
                        ) : (
                          <div style={{ display: "grid", gap: 10 }}>
                            <HorizontalScroll>{adapter.renderTask(task)}</HorizontalScroll>

                            {block?.answerShown ? (
                              <>
                                <div>
                                  <strong>Odpowiedź:</strong> {adapter.renderAnswer(task)}
                                </div>
                                {block?.explanationShown ? (
                                  <HorizontalScroll>{adapter.renderExplanation(task)}</HorizontalScroll>
                                ) : null}
                              </>
                            ) : null}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })() : null}
            </div>
              ) : null}
            </>
          ) : null}

          {isRibbonOpen ? (
            <div
              style={{
                width: ribbonDockWidth,
                position: "absolute",
                top: 8,
                left: ribbonDockLeftInsetPx,
                right: ribbonDockRightInsetPx,
                margin: "0 auto",
                zIndex: 26,
                pointerEvents: "auto",
              }}
            >
              {ribbon}
            </div>
          ) : null}

          {quickToolConfigPopup}

          <WhiteboardCanvas
            value={localBoardState}
            onChange={archiveReadOnly ? undefined : handleBoardChange}
            onToolChange={archiveReadOnly ? undefined : setTool}
            onAddPage={archiveReadOnly ? undefined : handleAddPage}
            onPageChange={setCurrentCanvasPage}
            onDraftChange={archiveReadOnly ? undefined : handleDraftChange}
            onCursorMove={archiveReadOnly ? undefined : handleCursorMove}
            tool={archiveReadOnly ? "select" : tool}
            color={activeColor}
            brushSize={tool === "highlighter" ? highlighterSize : brushSize}
            disabled={hardSyncActive}
            themeMode={themeMode}
            boardSurface={boardSurface}
            overlay={null}
            topLeftControls={archiveReadOnly ? archiveReadOnlyBanner : (isMobileNarrow ? null : topLeftControls)}
            topRightControls={archiveReadOnly ? readOnlyTopZoomControls : (isMobileNarrow ? compactTopControls : topRightControls)}
            sideControls={archiveReadOnly || isMobileNarrow ? null : quickSideControls}
            controlsSide={controlsSide}
            bottomLeftTopControls={fullscreenBottomLeftControls}
            bottomRightControls={bottomRightControls}
            bottomRightTopControls={bottomRightTopControls}
            bottomCenterControls={storageLimitBanner || storageUsageBanner || hardSyncBanner || connectionBanner}
            overlayInsets={{
              left: taskDockLeftInsetPx,
              right: taskDockRightInsetPx,
            }}
            overlayTopOffset={8}
            overlayZIndex={24}
            fullscreenActive={fullscreenActive}
            remoteDrafts={archiveReadOnly ? [] : remoteDrafts}
            remoteCursors={archiveReadOnly ? [] : remoteCursors}
            zoomPercent={zoomPercent}
            onZoomPercentChange={setZoomPercent}
            viewportHeightOverride={!fullscreenActive && !isMobileNarrow ? regularBoardViewportHeight : null}
            preferFixedWidth={fullscreenActive || isMobileNarrow}
            gridEnabled={gridEnabled}
            enableElementSelection={!archiveReadOnly}
            readOnly={archiveReadOnly}
            allowAddPage={!archiveReadOnly}
            stylusOnly={stylusOnly}
            holdToolSwitchEnabled={holdToolSwitchEnabled}
            showStylusCursor={stylusCursorEnabled}
            onEditGraph={archiveReadOnly ? undefined : openGraphEditor}
            onEditText={archiveReadOnly ? undefined : openTextEditor}
          />
        </div>

        {!fullscreenActive && voiceChannelWidget ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: 10,
              marginBottom: 6,
              maxWidth: "100%",
              pointerEvents: "auto",
            }}
          >
            {voiceChannelWidget}
          </div>
        ) : null}

        <input
          ref={imageInputRef}
          type="file"
          accept={IMAGE_ACCEPT}
          onChange={handleImageInputChange}
          style={{ display: "none" }}
        />

        {err ? (
          <div className="panel-card" style={{ marginTop: 12, border: "1px solid rgba(255,80,80,0.5)" }}>
            Błąd: {err}
          </div>
        ) : null}

        <EdgeDrawer
          side="left"
          isOpen={calcOpen}
          onOpenChange={setCalcOpen}
          width={420}
          title="Kalkulator maturalny"
          showHandle={false}
          offsetTop={0}
          background="linear-gradient(180deg, rgba(255,153,0,0.36) 0%, rgba(255,190,70,0.36) 100%)"
          contentMaxWidth={380}
          zIndex={sheetOpen ? 1602 : 1600}
          showBackdrop={false}
          headerRight={
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setSheetOpen(true)}
              onContextMenu={(e) => e.preventDefault()}
              style={{ fontSize: 14, padding: "2px 10px", lineHeight: 1, borderRadius: 10, ...touchSafeButtonStyle }}
              title="Otworz karte wzorow"
            >
              Karta wzorow
            </button>
          }
        >
          {calcOpen ? <ExamCalculator embedded /> : null}
        </EdgeDrawer>

        <FormulaSheet
          isOpen={sheetOpen}
          onOpenChange={setSheetOpen}
          pdfPath={WZORY_URL}
          showBackdrop={false}
          headerRightExtra={
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setCalcOpen(true)}
              onContextMenu={(e) => e.preventDefault()}
              style={{ fontSize: 14, padding: "2px 10px", lineHeight: 1, borderRadius: 10, ...touchSafeButtonStyle }}
              title="Otworz kalkulator"
            >
              Kalkulator
            </button>
          }
        />

        <MatematronModal
          open={textPickerOpen}
          variant="info"
          title={textEditTargetId ? "Edytuj tekst" : "Dodaj tekst na tablicę"}
          primaryText={textEditTargetId ? "Zapisz tekst" : "Dodaj tekst"}
          onPrimary={insertTextWidget}
          secondaryText="Anuluj"
          onSecondary={() => {
            setTextPickerOpen(false);
            setTextEditTargetId("");
          }}
          onClose={() => {
            setTextPickerOpen(false);
            setTextEditTargetId("");
          }}
          portalTarget={modalPortalTarget}
        >
          <textarea
            value={textInput}
            onChange={(event) => setTextInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
                event.preventDefault();
                insertTextWidget();
              }
            }}
            autoFocus
            rows={6}
            placeholder="Wpisz tekst..."
            style={{
              width: "100%",
              minHeight: 140,
              resize: "vertical",
              padding: "12px 14px",
              borderRadius: 10,
              border: "1px solid rgba(0,204,255,0.72)",
              background: themeMode === "light" ? "rgba(255,255,255,0.94)" : "rgba(2,6,23,0.82)",
              color: "inherit",
              fontFamily: "Rubik, sans-serif",
              fontSize: 16,
              lineHeight: 1.5,
              boxSizing: "border-box",
            }}
          />
          <div style={{ marginTop: 8, fontSize: 13, opacity: 0.72 }}>
            Ctrl+Enter zapisuje tekst. Rozmiar zmienisz uchwytem po zaznaczeniu elementu.
          </div>
        </MatematronModal>

        <MatematronModal
          open={graphPickerOpen}
          variant="info"
          title={graphEditTargetId ? "Edytuj wykres" : "Dodaj wykres na tablicę"}
          primaryText={graphEditTargetId ? "Zapisz wykres" : "Dodaj wykres"}
          onPrimary={insertGraphWidget}
          secondaryText="Anuluj"
          onSecondary={() => {
            setGraphPickerOpen(false);
            setGraphEditTargetId("");
            setGraphDeleteTarget(null);
          }}
          onClose={() => {
            setGraphPickerOpen(false);
            setGraphEditTargetId("");
            setGraphDeleteTarget(null);
          }}
          portalTarget={modalPortalTarget}
        >
          <div style={{ display: "grid", gap: 12 }}>
            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <div style={{ fontWeight: 800 }}>Wzory funkcji lub równania</div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setGraphInputs((items) => [...items, ""])}
                  title="Dodaj wzór"
                  style={{ width: 40, height: 40, padding: 0 }}
                >
                  +
                </button>
              </div>
              {graphInputs.map((input, index) => (
                <div key={`graph-input-${index}`} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    ref={(node) => { graphInputRefs.current[index] = node; }}
                    value={input}
                    onChange={(e) => {
                      const next = [...graphInputs];
                      next[index] = e.target.value;
                      setGraphInputs(next);
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    placeholder={`np. f(x)=x^2-4`}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid rgba(0,204,255,0.72)",
                      background: themeMode === "light" ? "rgba(255,255,255,0.86)" : "rgba(2,6,23,0.72)",
                      color: "inherit",
                      fontFamily: "monospace",
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => insertGraphInputToken(index, "π")}
                    title="Wstaw pi"
                    style={{ width: 40, height: 40, padding: 0, fontFamily: "'Times New Roman', serif", fontSize: 22, fontWeight: 800 }}
                  >
                    π
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => insertGraphInputToken(index, "√()")}
                    title="Wstaw pierwiastek"
                    style={{ width: 40, height: 40, padding: 0, fontFamily: "'Times New Roman', serif", fontSize: 22, fontWeight: 800 }}
                  >
                    √
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => requestGraphItemDelete("input", index)}
                    title="Usuń wzór"
                    style={{ width: 40, height: 40, padding: 0 }}
                  >
                    x
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <div style={{ fontWeight: 800 }}>Punkty</div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setGraphPoints((items) => [...items, ""])}
                  title="Dodaj punkt"
                  style={{ width: 40, height: 40, padding: 0 }}
                >
                  +
                </button>
              </div>
              {graphPoints.map((point, index) => (
                <div key={`graph-point-${index}`} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    value={point}
                    onChange={(e) => {
                      const next = [...graphPoints];
                      next[index] = e.target.value;
                      setGraphPoints(next);
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    placeholder="np. A=(1,2)"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid rgba(0,204,255,0.52)",
                      background: themeMode === "light" ? "rgba(255,255,255,0.86)" : "rgba(2,6,23,0.72)",
                      color: "inherit",
                      fontFamily: "monospace",
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => requestGraphItemDelete("point", index)}
                    title="Usuń punkt"
                    style={{ width: 40, height: 40, padding: 0 }}
                  >
                    x
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <div style={{ fontWeight: 800 }}>Odcinki</div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={addGraphSegment}
                  disabled={getGraphPointLabels().length < 2}
                  title={getGraphPointLabels().length < 2 ? "Dodaj najpierw co najmniej dwa punkty" : "Dodaj odcinek"}
                  style={{ width: 40, height: 40, padding: 0 }}
                >
                  +
                </button>
              </div>
              {getGraphPointLabels().length < 2 ? (
                <div style={{ color: themeMode === "light" ? "#64748b" : "#94a3b8", fontSize: 13 }}>
                  Dodaj co najmniej dwa punkty, żeby wybrać odcinek.
                </div>
              ) : null}
              {graphSegments.map((segment, index) => {
                const labels = getGraphPointLabels();
                const parsed = parseGraphSegmentEndpointsLoose(segment);
                return (
                  <div key={`graph-segment-${index}`} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <select
                      value={parsed.a || labels[0] || ""}
                      onChange={(e) => updateGraphSegmentEndpoint(index, "a", e.target.value)}
                      aria-label="Początek odcinka"
                      style={{
                        flex: 1,
                        minWidth: 0,
                        padding: "10px 12px",
                        borderRadius: 10,
                        border: "1px solid rgba(0,204,255,0.52)",
                        background: themeMode === "light" ? "rgba(255,255,255,0.86)" : "rgba(2,6,23,0.72)",
                        color: "inherit",
                      }}
                    >
                      {labels.map((label) => (
                        <option key={`segment-${index}-a-${label}`} value={label}>{label}</option>
                      ))}
                    </select>
                    <span style={{ fontWeight: 800, color: themeMode === "light" ? "#64748b" : "#94a3b8" }}>do</span>
                    <select
                      value={parsed.b || labels[0] || ""}
                      onChange={(e) => updateGraphSegmentEndpoint(index, "b", e.target.value)}
                      aria-label="Koniec odcinka"
                      style={{
                        flex: 1,
                        minWidth: 0,
                        padding: "10px 12px",
                        borderRadius: 10,
                        border: "1px solid rgba(0,204,255,0.52)",
                        background: themeMode === "light" ? "rgba(255,255,255,0.86)" : "rgba(2,6,23,0.72)",
                        color: "inherit",
                      }}
                    >
                      {labels.map((label) => (
                        <option key={`segment-${index}-b-${label}`} value={label}>{label}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => requestGraphItemDelete("segment", index)}
                      title="Usuń odcinek"
                      style={{ width: 40, height: 40, padding: 0 }}
                    >
                      x
                    </button>
                  </div>
                );
              })}
            </div>

            <div style={{ display: "grid", gap: 8, fontSize: 14 }}>
              <div style={{ fontWeight: 800 }}>Zakres osi</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8 }}>
                {[
                  ["xmin", "xmin"],
                  ["xmax", "xmax"],
                  ["ymin", "ymin"],
                  ["ymax", "ymax"],
                ].map(([key, label]) => (
                  <label key={key} style={{ display: "grid", gap: 5 }}>
                    <span style={{ color: themeMode === "light" ? "#475569" : "#94a3b8", fontSize: 12, fontWeight: 800 }}>{label}</span>
                    <input
                      type="number"
                      step="1"
                      value={graphRangeFields[key]}
                      onChange={(e) => updateGraphRangeField(key, e.target.value)}
                      style={{
                        width: "100%",
                        minWidth: 0,
                        padding: "9px 8px",
                        borderRadius: 10,
                        border: "1px solid rgba(148,163,184,0.45)",
                        background: themeMode === "light" ? "rgba(255,255,255,0.86)" : "rgba(2,6,23,0.72)",
                        color: "inherit",
                      }}
                    />
                  </label>
                ))}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                padding: 10,
                borderRadius: 12,
                background: themeMode === "light" ? "rgba(15,23,42,0.05)" : "rgba(2,6,23,0.35)",
                border: "1px solid rgba(148,163,184,0.18)",
              }}
            >
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setGraphThemeChoice((value) => (value === "light" ? "dark" : "light"))}
                title={`Przełącz motyw wykresu na ${graphThemeChoice === "light" ? "ciemny" : "jasny"}`}
                aria-label="Przełącz motyw wykresu"
                style={{ width: 42, height: 42, padding: 0, flex: "0 0 auto" }}
              >
                <GraphThemeIcon mode={graphThemeChoice} />
              </button>
              <img
                alt="Podgląd wykresu"
                src={createGraphSvgDataUrl({
                  inputs: graphInputs.map((input) => String(input || "").trim()).filter(Boolean),
                  points: graphPoints.map((input) => String(input || "").trim()).filter(Boolean),
                  segments: graphSegments.map((input) => String(input || "").trim()).filter(Boolean),
                  range: parseGraphRangeFields(graphRangeFields),
                  themeMode: graphThemeChoice,
                })}
                style={{
                  width: "min(320px, 100%)",
                  objectFit: "contain",
                  borderRadius: 12,
                  display: "block",
                }}
              />
            </div>
          </div>
        </MatematronModal>

        <MatematronModal
          open={!!graphDeleteTarget}
          variant="info"
          title={`Usuń ${graphDeleteLabel(graphDeleteTarget?.kind)}?`}
          message={
            graphDeleteTarget?.kind === "point"
              ? "Ten punkt zostanie usunięty. Odcinki korzystające z tego punktu też znikną."
              : `Czy na pewno chcesz usunąć ten ${graphDeleteLabel(graphDeleteTarget?.kind)}?`
          }
          primaryText="Usuń"
          onPrimary={confirmGraphItemDelete}
          secondaryText="Anuluj"
          onSecondary={cancelGraphItemDelete}
          onClose={cancelGraphItemDelete}
          showBadge
          badgeText="Wykres"
          portalTarget={modalPortalTarget}
        />

        <MatematronModal
          open={taskPickerOpen}
          variant="info"
          title="Dodaj zadanie na tablicę"
          hideFooter
          onClose={() => setTaskPickerOpen(false)}
          portalTarget={modalPortalTarget}
        >
          <div style={{ display: "grid", gap: 12 }}>
            {demoMode ? (
              <div
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(245,158,11,0.38)",
                  background: themeMode === "light" ? "rgba(245,158,11,0.1)" : "rgba(245,158,11,0.12)",
                  fontSize: 13,
                  lineHeight: 1.45,
                }}
              >
                <strong>Przyklad z puli demo.</strong>{" "}
                W tym samouczku pokazujemy zadania z wybranych tematow: rownania liniowe,
                nierownosci kwadratowe i polozenie okregu. Pelna tablica bedzie mogla korzystac z szerszej bazy.
              </div>
            ) : null}

            <label style={{ display: "grid", gap: 6, fontSize: 14 }}>
              Dział
              <select
                value={String(taskSectionIndex)}
                onChange={(e) => {
                  setTaskSectionIndex(Number(e.target.value || 0));
                  setTaskTopicIndex(0);
                  setTaskSubtopicId("");
                }}
                style={{ minHeight: 36 }}
              >
                {SECTION_OPTIONS.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.label}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: "grid", gap: 6, fontSize: 14 }}>
              Temat
              <select
                value={String(taskTopicIndex)}
                onChange={(e) => {
                  setTaskTopicIndex(Number(e.target.value || 0));
                  setTaskSubtopicId("");
                }}
                style={{ minHeight: 36 }}
              >
                {selectedTopicOptions.map((topic, idx) => (
                  <option key={`${idx}_${topic?.name || "topic"}`} value={idx}>
                    {topic?.name || `Temat ${idx + 1}`}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: "grid", gap: 6, fontSize: 14 }}>
              Podtemat
              <select value={activeTaskSubtopicId} onChange={(e) => setTaskSubtopicId(String(e.target.value || ""))} style={{ minHeight: 36 }}>
                {selectedSubtopicOptions.map((subtopic) => (
                  <option key={String(subtopic.id)} value={String(subtopic.id)}>
                    {subtopic.name}
                  </option>
                ))}
              </select>
            </label>

            {activeTaskGenerationModes.length > 1 ? (
              <label style={{ display: "grid", gap: 6, fontSize: 14 }}>
                Wariant
                <select
                  value={resolveTaskGenerationMode(activeTaskAdapter, taskGenerationMode)?.value || ""}
                  onChange={(e) => {
                    setTaskGenerationMode(String(e.target.value || ""));
                    setTaskChoiceKey("");
                  }}
                  style={{ minHeight: 36 }}
                  disabled={taskChoicesLoading}
                >
                  {activeTaskGenerationModes.map((mode) => (
                    <option key={mode.value} value={mode.value}>
                      {mode.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            <label style={{ display: "grid", gap: 6, fontSize: 14 }}>
              Zadanie
              <select value={taskChoiceKey} onChange={(e) => setTaskChoiceKey(String(e.target.value || ""))} style={{ minHeight: 36 }}>
                {taskChoices.map((choice) => (
                  <option key={choice.key} value={choice.key}>
                    {choice.label}
                  </option>
                ))}
              </select>
            </label>

            {taskChoicesLoading ? <div style={{ fontSize: 12, opacity: 0.8 }}>Wczytywanie listy zadań…</div> : null}
            {!taskChoicesLoading && taskAdapterStatus[String(activeTaskSubtopicId || "")]?.state === "error" ? (
              <div style={{ fontSize: 12, color: "#f87171", lineHeight: 1.45 }}>
                {taskAdapterStatus[String(activeTaskSubtopicId || "")]?.error || "Nie udało się wczytać zadań dla tego podtematu."}
              </div>
            ) : null}
            {!taskChoicesLoading && taskAdapterStatus[String(activeTaskSubtopicId || "")]?.state === "loaded" && taskChoices.length === 0 ? (
              <div style={{ fontSize: 12, opacity: 0.78, lineHeight: 1.45 }}>
                Ten podtemat nie zwrócił żadnych zadań do dodania na tablicę.
              </div>
            ) : null}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap", marginTop: 4 }}>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => setTaskPickerOpen(false)}
                onContextMenu={(e) => e.preventDefault()}
                style={touchSafeButtonStyle}
                disabled={taskPickerBusy}
              >
                Anuluj
              </button>
              <button
                className="btn btn-primary"
                type="button"
                onClick={handleAddTaskBlock}
                onContextMenu={(e) => e.preventDefault()}
                style={touchSafeButtonStyle}
                disabled={taskPickerBusy || taskChoicesLoading || !activeTaskSubtopicId || !taskChoiceKey}
              >
                {taskPickerBusy ? "Dodawanie…" : "Dodaj zadanie"}
              </button>
            </div>
          </div>
        </MatematronModal>

        <MatematronModal
          open={taskDeleteOpen}
          variant="info"
          title="Usuń zadanie z tablicy"
          message="Czy na pewno chcesz usunąć to zadanie?"
          primaryText="Usuń zadanie"
          onPrimary={confirmTaskDelete}
          secondaryText="Anuluj"
          onSecondary={() => {
            setTaskDeleteOpen(false);
            setTaskDeleteTargetId("");
          }}
          onClose={() => {
            setTaskDeleteOpen(false);
            setTaskDeleteTargetId("");
          }}
          showBadge
          badgeText="Zadanie"
          portalTarget={modalPortalTarget}
        />

        <MatematronModal
          open={taskRerollOpen}
          variant="info"
          title="Wylosować inne zadanie?"
          message="Aktualna treść tego zadania zostanie podmieniona na nową wersję."
          primaryText="Wylosuj inne"
          onPrimary={confirmTaskReroll}
          secondaryText="Anuluj"
          onSecondary={() => {
            setTaskRerollOpen(false);
            setTaskRerollTargetId("");
          }}
          onClose={() => {
            setTaskRerollOpen(false);
            setTaskRerollTargetId("");
          }}
          showBadge
          badgeText="Zadanie"
          portalTarget={modalPortalTarget}
        />

        <MatematronModal
          open={taskDuplicateOpen}
          variant="info"
          title="Duplikować typ zadania?"
          message="To doda kolejne zadanie tego samego typu i od razu wylosuje nowe dane."
          primaryText="Duplikuj typ zadania"
          onPrimary={confirmTaskDuplicate}
          secondaryText="Anuluj"
          onSecondary={() => {
            setTaskDuplicateOpen(false);
            setTaskDuplicateTargetId("");
          }}
          onClose={() => {
            setTaskDuplicateOpen(false);
            setTaskDuplicateTargetId("");
          }}
          showBadge
          badgeText="Zadanie"
          portalTarget={modalPortalTarget}
        />

        <MatematronModal
          open={taskLimitOpen}
          variant="info"
          title="Limit zadań osiągnięty"
          message={
            taskLimitContext === "duplicate"
              ? `Limit zadań na jednej tablicy to ${MAX_TASK_BLOCKS}. To zadanie nie zostało zduplikowane.`
              : `Limit zadań na jednej tablicy to ${MAX_TASK_BLOCKS}. To zadanie nie zostało dodane.`
          }
          primaryText="OK"
          onPrimary={() => setTaskLimitOpen(false)}
          onClose={() => setTaskLimitOpen(false)}
          showBadge
          badgeText="Zadanie"
          portalTarget={modalPortalTarget}
        />

        <MatematronModal
          open={deleteOpen}
          variant="info"
          title="Potwierdź usunięcie"
          message={`Czy na pewno chcesz usunąć tablicę "${room?.settings?.title || roomCode}"?`}
          primaryText="Usuń tablicę"
          onPrimary={handleDeleteBoard}
          secondaryText="Anuluj"
          onSecondary={() => setDeleteOpen(false)}
          onClose={() => setDeleteOpen(false)}
          showBadge
          badgeText="Tablica"
          portalTarget={modalPortalTarget}
        />

        <MatematronModal
          open={clearOpen}
          variant="info"
          title="Wyczyść tablicę"
          message="Czy na pewno chcesz wyczyścić całą tablicę? Wszystkie elementy znikną z aktualnego widoku dla uczestników."
          primaryText="Wyczyść tablicę"
          onPrimary={confirmClear}
          secondaryText="Anuluj"
          onSecondary={() => setClearOpen(false)}
          onClose={() => setClearOpen(false)}
          showBadge
          badgeText="Tablica"
          portalTarget={modalPortalTarget}
        />
      </div>
    </div>
  );
}
